import Phaser from 'phaser';
import { isMenuOpen, isPreGameScreenOpen, openDoorPanel, showTitle, showIntro, showDeathScreen, refreshTurnHud, updateStatsHud, updateFacedTarget, updateLinkIndicator, updateRoomName } from '../ui';
import { isDoorLocked } from '../doors';
import {
  getSpriteId,
  onSpriteIdChange,
  SpriteId,
  getShowEnergyDebug,
  getShowChargeDebug,
  getShowHealthDebug,
  getShowEntropyDebug,
  getShowConductivityDebug,
  getShowGridCoords,
  onDebugOverlayChange,
  getShowDevSignposts,
  getMovementInputMode,
  getTutorialEnabled,
  getTileSize,
  onTileSizeChange,
} from '../settings';
import { onNewGame } from '../gameReset';
import { onRoomRestart } from '../roomRestart';
import { getHasInteractedOnce } from '../tutorialHints';
import { onRadialMeasure, RadialMeasureEvent } from '../radialMeasure';
import { onTransferEffect, TransferEffectEvent, TransferEffectResult } from '../transferEffect';
import { onMomentumEffect, MomentumEffectEvent, MomentumEffectResult } from '../momentumEffect';
import { onProjectEffect, ProjectEffectEvent, ProjectEffectResult } from '../projectEffect';
import { onSeparateEffect, SeparateEffectEvent, SeparateEffectResult } from '../separateEffect';
import { onBindEffect, BindEffectEvent, BindEffectResult } from '../bindEffect';
import { onUnbindEffect, UnbindEffectEvent, UnbindEffectResult } from '../unbindEffect';
import { onEnergyBindEffect, EnergyBindEffectEvent, EnergyBindEffectResult } from '../energyBindEffect';
import { onUnbindEnergyEffect, UnbindEnergyEffectEvent, UnbindEnergyEffectResult } from '../unbindEnergyEffect';
import { onChargeBindEffect, ChargeBindEffectEvent, ChargeBindEffectResult } from '../chargeBindEffect';
import { onUnbindChargeEffect, UnbindChargeEffectEvent, UnbindChargeEffectResult } from '../unbindChargeEffect';
import { onEntropyBindEffect, EntropyBindEffectEvent, EntropyBindEffectResult } from '../entropyBindEffect';
import { onUnbindEntropyEffect, UnbindEntropyEffectEvent, UnbindEntropyEffectResult } from '../unbindEntropyEffect';
import { onDpadDirection, onDpadRelease } from '../dpadInput';
import { getMana, getHealth, getCharge, getMaxCharge, getThermalState, getMaxThermalState } from '../playerStats';
import { onRoomJump } from '../roomJump';
import { onInteractEffects } from '../interactEffects';
import { registerSnapshotProvider, registerSnapshotApplier, RoomSnapshot } from '../gameSave';
import { getLinkedSource, onLinkedSourceChange } from '../objectLink';
import { ConceptId } from '../../../rune-lab/src/runes/concepts';
// The world's data shapes and tunable numbers now live in the renderer-independent
// sim module (see design/06_Architecture.md) -- same cross-package import the rune
// vocabulary above already uses, in the same direction (game -> rune-lab).
import { ObjectDef, DoorDef, RoomDef, Facing } from '../../../rune-lab/src/sim/types';
import { serializeRoomMutations, applyRoomMutations } from '../../../rune-lab/src/sim/rooms';
import { buildRoomsForPlay, playtestPayload } from '../playtest';
import { getWorld } from '../activeWorld';
// Namespaced so the scene's own thin wrapper methods can keep their original
// names (and therefore all their call sites) without colliding.
import * as Q from '../../../rune-lab/src/sim/queries';
import { CABLE_SHAPES } from '../../../rune-lab/src/sim/cable';
import { AUTOTILE_SHAPES } from '../../../rune-lab/src/sim/autotile';
import { linkLabels } from '../linkIndicators';
import * as Mech from '../../../rune-lab/src/sim/mechanics';
import * as Casts from '../../../rune-lab/src/sim/casts';
import { settleWorld } from '../../../rune-lab/src/sim/settle';
import { restartRoom } from '../../../rune-lab/src/sim/state';
import { advanceTurn, tick } from '../../../rune-lab/src/sim/tick';
import { litTiles, roomIsDark } from '../../../rune-lab/src/sim/light';
import { applyCommand } from '../../../rune-lab/src/sim/replay';
import { beginTake, recordCommand, recordElapsed } from '../moveRecorder';
import { applyClockSetting, isTurnMode } from '../clockMode';
import { onClockModeChange } from '../settings';
import { onCastBegan, onCastEnded, onUndoRequested, onWaitRequested } from '../turnActions';
import { clearUndoHistory, discardUndoPoint, recordUndoPoint, refreshUndoAvailability, undo } from '../undoHistory';
import { SimEvents, TextureTransition } from '../../../rune-lab/src/sim/events';
import {
  WALL,
  VOID,
  DIRECTIONS,
  DIRECTION_OFFSETS,
  START_ROOM,
  START_X,
  START_Y,
  START_FACING,
} from '../../../rune-lab/src/sim/constants';
import { initAnalytics, trackEvent } from '../analytics';
import { reportError, setErrorContextProvider } from '../errorTelemetry';

// Grid-locked movement (Pokemon-style: one tile per press, not free-roam pixels) --
// each step is a short tween between tile centers, never a mid-tile stop. Purely
// visual, unlike the simulation constants that used to sit here -- see
// rune-lab/src/sim/constants.ts.
//
// One tile is 32 world pixels and always will be: it is the unit every room, every
// texture and every offset in this file is written in. How *big a tile looks* is a
// separate question, answered by the camera's zoom (see applyTileSize) rather than
// by this number, so a player changing tile size never changes the world's geometry.
const TILE = 32;
const MOVE_DURATION = 140;
// Turn-mode key repeat (see acceptsStep). The delay has to be long enough that a
// normal tap can never trigger it by accident, since an accidental repeat here
// does not just move you a tile too far -- it spends a turn of the world.
//
// The interval is deliberately **more than twice `MOVE_DURATION`**, and that
// relationship is the point rather than the number. A real-time walk is paced by
// the move animation; a turn-mode repeat is paced by how fast the player should be
// allowed to spend turns without looking, which is a slower thing. At 180ms (the
// first attempt) it was barely slower than the animation, so holding a key ran the
// world at nearly the same rate as before the mode existed -- which made the
// difference between the two modes almost unobservable, and made it unobservable
// to a *test* too. ~3 turns a second is brisk enough to cross a room and slow
// enough to stop before overshooting, which matters when overshooting costs turns.
const TURN_REPEAT_DELAY_MS = 400;
const TURN_REPEAT_INTERVAL_MS = 300;
const TILESET_KEY = 'placeholder-tileset';

// What can be pinned to screen space (see GameScene's screenSpaceUi): the HUD
// texts drawn on the canvas, and the touch wedge.
type ScreenSpaceUi = Phaser.GameObjects.Text | Phaser.GameObjects.Graphics;

const SPRITE_IDS: SpriteId[] = ['default', 'witch', 'wizard'];

// Low->high color pair per concept, used only to paint the Measure view (see
// showMeasureVisual) -- every reading concept gets its own physically-motivated
// gradient rather than one shared palette: energy is a single-hue brightness
// ramp (a dim ember to a bright one, not a hue change), entropy runs
// white/ordered to green/disordered, and charge runs blue to red the way a
// signed +/- reading conventionally would (see 04_Physics_Engine.md's Charge
// section) -- none of these are literal color theory, just a consistent
// per-concept "cold" (low reading) to "hot" (high reading) mapping.
const CONCEPT_COLOR_RANGE: Record<ConceptId, { cold: number; hot: number }> = {
  energy: { cold: 0x3a0a0a, hot: 0xff2a1a },
  charge: { cold: 0x2255ee, hot: 0xee2222 },
  mass: { cold: 0x224422, hot: 0x88dd66 },
  momentum: { cold: 0x444444, hot: 0xe0c030 },
  space: { cold: 0x1a1a55, hot: 0x66ddee },
  time: { cold: 0x5c4020, hot: 0xd8b040 },
  entropy: { cold: 0xf0f0f0, hot: 0x33cc55 },
};

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

// All art here is generated at runtime with Graphics -> generateTexture, not loaded
// from files -- placeholder art until a real tileset/spritesheet exists. The tileset
// is laid out as one image with tiles side by side (floor, wall, door, room2-floor),
// the same shape a real Tiled-exported PNG would have, so swapping in real art later
// is a one-line change to which image `addTilesetImage` points at, not a rewrite of
// the map/movement code.
export class GameScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private tilemap!: Phaser.Tilemaps.Tilemap;
  private layer!: Phaser.Tilemaps.TilemapLayer;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<Facing, Phaser.Input.Keyboard.Key>;
  // The spawn point the *current* room was entered through -- captured at the
  // top of enterRoom, reused for a death restart (see onRoomRestart below) so
  // dying doesn't strand the player wherever a hazard happened to catch them.
  // Not on WorldState: this is "how did I get here" navigation history, not
  // part of what the world currently is, so it stays scene-local the same way
  // isMoving/moveStartedAt below do.
  private lastEntrySpawn: { x: number; y: number; facing: Facing } = { x: START_X, y: START_Y, facing: START_FACING };

  // Not a field: the rooms belong to the shared WorldState, and the scene is one
  // of several things reading them (see game/src/activeWorld.ts). A getter keeps
  // all 30-odd `this.rooms` call sites below untouched while making it impossible
  // for the scene to drift into holding a second, private copy of the world.
  private get rooms(): Record<string, RoomDef> {
    return getWorld().rooms;
  }
  // Where the player is lives in the shared WorldState, not on the scene -- casts
  // are position- and facing-relative, so a headless caller has to be able to
  // place the caster before casting. Accessors rather than fields so all ~68
  // `this.tileX` / `this.facing` / `this.currentRoomId` uses below are unchanged.
  private get currentRoomId(): string {
    return getWorld().player.roomId;
  }
  private set currentRoomId(value: string) {
    getWorld().player.roomId = value;
  }
  private get tileX(): number {
    return getWorld().player.x;
  }
  private set tileX(value: number) {
    getWorld().player.x = value;
  }
  private get tileY(): number {
    return getWorld().player.y;
  }
  private set tileY(value: number) {
    getWorld().player.y = value;
  }
  private get facing(): Facing {
    return getWorld().player.facing;
  }
  private set facing(value: Facing) {
    getWorld().player.facing = value;
  }
  private isMoving = false;
  // Timestamp isMoving last became true. tryMove()'s tween is the only thing that
  // clears isMoving (via onComplete); if it's ever interrupted before completing --
  // scene teardown, a dropped frame, anything -- movement would otherwise lock up
  // permanently even though nothing else is wrong. update() self-heals past that.
  private moveStartedAt = 0;
  private objectSprites: Phaser.GameObjects.Sprite[] = [];
  // Developer Tools debug overlay: live Energy/Charge/Health readouts floating
  // above objects/the player. Object labels are only ever created for objects
  // with `properties` (nothing else has a live value to show) and are stored
  // via the sprite's own data, same key-by-objectId pattern objectSprites
  // already uses for melt/fade lookups. playerDebugLabel persists across
  // rooms; roomAmbientLabel is recreated per room (the value it shows is
  // per-room). See refreshDebugLabels/settings.ts's onDebugOverlayChange.
  private playerDebugLabel?: Phaser.GameObjects.Text;
  private roomAmbientLabel?: Phaser.GameObjects.Text;
  // Developer Tools option: one (x,y) label per tile of the current room,
  // built lazily (only when the toggle is actually on -- a full room can be
  // 30x20 tiles, not worth creating unconditionally like the small per-object
  // debug labels above). Rebuilt on room entry and on toggle (see
  // refreshGridCoordinateLabels/onDebugOverlayChange).
  private gridCoordLabels: Phaser.GameObjects.Text[] = [];
  // The conductivity overlay's single Graphics layer, plus the reading it draws
  // from. The reading is recomputed only when the world changes (it walks a
  // charge network per object, which is not a per-frame cost worth paying) and
  // the geometry is redrawn every frame from the sprites' live positions, so an
  // outline follows a pushed block through its move tween.
  private conductivityLayer?: Phaser.GameObjects.Graphics;
  private darknessLayer?: Phaser.GameObjects.Graphics;
  private conductivityReadings = new Map<string, { conducts: boolean; stores: boolean; potential: number }>();
  // A second onboarding hint under the always-visible movement one -- unlike
  // that one, this disappears forever the instant the player successfully
  // Interacts with anything for the first time (see tutorialHints.ts),
  // rather than staying up the whole game. Polled each frame in update()
  // rather than wired to a dedicated event, the same "just check every
  // frame" approach refreshPlayerAura already uses.
  private interactHintLabel!: Phaser.GameObjects.Text;
  // Guards startAmbientTimers() -- polled each frame the same way, so the
  // ambient Energy regen/decay timers don't start counting real time until the
  // title/intro pre-game screens have actually closed (see isPreGameScreenOpen).
  // Registering them immediately in create() would anchor their fixed-interval
  // schedule to scene-boot wall-clock time instead of to when a life actually
  // starts, silently eating into decay-margin-sensitive puzzles' timing by
  // however long the pre-game screens happen to take.
  private ambientTimersStarted = false;

  // Touch/mouse movement: the screen is split into 4 triangular wedges by its two
  // diagonals (up/down/left/right, meeting at center) -- whichever wedge is currently
  // pressed is the held direction, re-evaluated continuously while the pointer is
  // down so dragging into a different wedge changes direction without lifting.
  private touchDirection: Facing | null = null;
  // Turn-mode input edge state (see acceptsStep). Untouched on the wall clock,
  // where a held direction simply walks.
  private heldDirection: Facing | null = null;
  private heldSince = 0;
  private lastRepeatAt = 0;
  private touchGraphics?: Phaser.GameObjects.Graphics;

  // Overlay objects that are meant to sit at fixed *screen* coordinates at a
  // fixed size, whatever the camera is doing, along with the screen position
  // each one wants.
  //
  // `setScrollFactor(0)` alone is not enough for that once the camera can zoom:
  // it stops an object scrolling with the camera but not scaling with it, since
  // the camera matrix scales everything about the viewport's center. At 2x zoom
  // a label pinned to (8,8) renders twice as large and half a screen off the
  // top-left corner. layoutScreenSpaceUi cancels the zoom back out for
  // everything registered here, which is why registering is how these get
  // positioned at all rather than a separate step afterwards.
  private screenSpaceUi: Array<{ obj: ScreenSpaceUi; x: number; y: number }> = [];

  // A temporary, stationary overlay left by a Measure + Radial cast -- a snapshot
  // of the area at the moment of casting, not something that follows the player.
  private radialMeasureGraphics?: Phaser.GameObjects.Graphics;

  // A pulsing reticle over whichever object is currently marked as a Transfer
  // source (see objectLink.ts) -- the HUD's "Source: ..." text says which one
  // in words, this shows where. Undefined whenever nothing's marked, or the
  // marked object isn't in the room currently loaded.
  private markIndicatorSprite?: Phaser.GameObjects.Sprite;

  // One floating rune-symbol label per currently Bind·Momentum-linked object
  // (see BindDef), keyed by object id -- unlike the single marked-source
  // reticle above, more than one object can be bound at once. Shows the
  // literal runes cast (momentum+bind, plus reverse for a mirror link) so the
  // effect stays legible while it's active, not just in the one-off cast
  // narration. Built/torn down dynamically by refreshLinkIndicators rather than
  // precreated per object the way debug labels are, since only a small, variable
  // subset of objects is ever actually linked at once.
  //
  // One map for all four link clusters, keyed `objectId:cluster` -- there were
  // four maps and four refresh/sync pairs until 2026-08-16, and the duplication
  // is what let two bugs live in them (a link labelled at only one of its ends,
  // and a fourth kind left out of the staleness flags). What each label says and
  // where it sits is decided by `linkIndicators.ts`; this just holds them.
  private linkIndicators = new Map<string, Phaser.GameObjects.Text>();

  // A live halo around the player reflecting their own current Energy/Charge
  // reserves -- Energy's a warm ember glow (usually at least somewhat lit,
  // since Energy starts full and idly regenerates), Charge a colder, jittery
  // spark (usually invisible, since Charge rests at 0 until deliberately
  // drawn onto the player -- see 04_Physics_Engine.md's Conservation of
  // Charge). Both persist across rooms like the player sprite itself,
  // refreshed live every frame from playerStats.ts rather than only at the
  // moment a cast changes them, the same "recompute continuously" approach
  // refreshReformableVisual and the marked-source reticle already use.
  private energyAuraSprite?: Phaser.GameObjects.Sprite;
  private chargeAuraSprite?: Phaser.GameObjects.Sprite;
  // The ambient chargeAuraSprite glow above is subtle by design (Charge is
  // rarely more than a sliver of the Charge ceiling in practice) and its "hot" tint
  // trends toward the same warm red Energy's already-full aura sits at,
  // easy to miss entirely. This tracks the last-seen value so refreshPlayerAura
  // can fire a one-off spark (reusing Project·Charge's own fx-bolt) the
  // instant Charge actually goes up, a real "something happened" cue instead
  // of relying on a continuous glow a player has to notice changed shade.
  private lastKnownCharge = 0;

  constructor() {
    super('game');
  }

  create(): void {
    this.generateTextures();
    getWorld().rooms = buildRoomsForPlay();

    // Depth 0.9, just behind the player (depth 1) -- a halo peeking out from
    // behind the character rather than a decal painted over them. The Charge
    // aura is drawn smaller so, when both are lit at once, it reads as an
    // inner spark inside Energy's broader outer glow rather than the two
    // simply overwriting each other.
    this.energyAuraSprite = this.add.sprite(0, 0, 'fx-player-aura').setDepth(0.9).setAlpha(0);
    this.chargeAuraSprite = this.add.sprite(0, 0, 'fx-player-aura').setDepth(0.9).setScale(0.65).setAlpha(0);

    this.player = this.add.sprite(0, 0, this.playerTexture(this.facing)).setDepth(1);
    onSpriteIdChange(() => this.player.setTexture(this.playerTexture(this.facing)));

    const keyboard = this.input.keyboard!;
    this.cursors = keyboard.createCursorKeys();
    this.wasd = {
      up: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
    // Registering W/S/A/D (and the arrow keys, via createCursorKeys) makes
    // Phaser's KeyboardManager call preventDefault() on those keydown events
    // globally -- including while a DOM text input/textarea has focus, which
    // silently swallowed the "w"/"a"/"s"/"d" characters a player tried to type
    // into e.g. the rune-naming box. isMenuOpen() already stops the *movement*
    // reading these keys while a panel is up, but that doesn't stop the browser
    // from being told to ignore the keystroke in the first place -- only
    // toggling the manager's own preventDefault flag does.
    const isTypingTarget = (el: EventTarget | null) =>
      el instanceof HTMLElement && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT');
    document.addEventListener('focusin', (e) => {
      if (isTypingTarget(e.target)) keyboard.manager!.preventDefault = false;
    });
    document.addEventListener('focusout', (e) => {
      if (isTypingTarget(e.target)) keyboard.manager!.preventDefault = true;
    });

    initAnalytics();
    // What every crash report gets stamped with. "Which room, standing where,
    // facing which way, with what marked" is the difference between a stack
    // trace and a reproduction -- and since the simulation is deterministic,
    // it is very nearly a reproduction on its own. Registered as a provider
    // rather than read by errorTelemetry directly so that module stays
    // installable before this scene exists (see its own comment).
    setErrorContextProvider(() => ({
      roomId: getWorld().player.roomId,
      playerX: getWorld().player.x,
      playerY: getWorld().player.y,
      facing: getWorld().player.facing,
      markedSourceId: getWorld().markedSource?.id ?? null,
    }));
    // A playtest launch starts in the room being tested, at the caster the editor
    // had placed -- Project fires along the facing and half the puzzles are about
    // approach, so where you were standing when you pressed Play is the state
    // worth reproducing.
    const playtest = playtestPayload();
    if (playtest) this.enterRoom(playtest.room.id, playtest.spawn.x, playtest.spawn.y, playtest.spawn.facing as Facing);
    else this.enterRoom(START_ROOM, this.tileX, this.tileY, this.facing);
    this.cameras.main.roundPixels = true;
    // No title screen for a playtest: you already chose what to play, in the
    // editor. "New Game / Continue" over a room that exists in neither a save nor
    // the shipped world is a question with no right answer.
    if (!playtest) showTitle();
    onNewGame(() => {
      // Rebuilds every room from scratch -- not just re-entering room1 -- so any
      // mutated `properties` (a partly-melted ice gate, a drained brazier) and any
      // objects removed by a threshold effect are back to their starting state too.
      getWorld().rooms = buildRoomsForPlay();
      const restart = playtestPayload();
      if (restart) this.enterRoom(restart.room.id, restart.spawn.x, restart.spawn.y, restart.spawn.facing as Facing);
      else this.enterRoom(START_ROOM, START_X, START_Y, START_FACING);
      // "New Game" means starting over -- waking up again -- so show the intro
      // again. Not in a playtest: restarting there means restarting the room
      // under test, and the intro is about waking up in room0.
      if (!restart) showIntro();
    });
    onRoomRestart(() => {
      // Death's own reset, deliberately narrower than New Game's above:
      // only the room just failed in (restartRoom) and the body that failed
      // in it (Health/Energy/Charge) start over -- every other room, and
      // everything carried or learned, survives. Re-enters at the same
      // spawn point the room itself was last entered through, not wherever
      // the fatal hit happened to land, so the player never reappears
      // standing somewhere a freshly-rebuilt room's own state disagrees with.
      const roomId = this.currentRoomId;
      restartRoom(getWorld(), roomId);
      updateStatsHud();
      this.enterRoom(roomId, this.lastEntrySpawn.x, this.lastEntrySpawn.y, this.lastEntrySpawn.facing);
    });
    onTransferEffect((event) => this.applyTransferEffect(event));
    onMomentumEffect((event) => this.applyMomentumEffect(event));
    onProjectEffect((event) => this.applyProjectEffect(event));
    onSeparateEffect((event) => this.applySeparateEffect(event));
    onBindEffect((event) => this.applyBindEffect(event));
    onUnbindEffect((event) => this.applyUnbindEffect(event));
    onEnergyBindEffect((event) => this.applyEnergyBindEffect(event));
    onUnbindEnergyEffect((event) => this.applyUnbindEnergyEffect(event));
    onChargeBindEffect((event) => this.applyChargeBindEffect(event));
    onUnbindChargeEffect((event) => this.applyUnbindChargeEffect(event));
    onEntropyBindEffect((event) => this.applyEntropyBindEffect(event));
    onUnbindEntropyEffect((event) => this.applyUnbindEntropyEffect(event));
    onRadialMeasure((event) => this.showMeasureVisual(event));
    // D-pad buttons live in ui.ts's DOM overlay, outside the canvas -- they set
    // the same touchDirection field the quadrant-tap scheme uses, just without
    // computing it from a tap position. Menu-open guard mirrors handlePointerDown.
    onDpadDirection((dir) => {
      if (isMenuOpen()) return;
      this.touchDirection = dir;
    });
    onDpadRelease(() => {
      this.touchDirection = null;
    });
    // A checkbox toggle should update the overlay immediately, not wait for the
    // next decay/regen tick or cast.
    onDebugOverlayChange(() => {
      this.refreshDebugLabels();
      this.refreshGridCoordinateLabels();
      this.refreshDevSignpostVisibility();
      // If the player is currently facing a signpost, hiding it should make
      // it stop being the Interact/Mark/Cast target immediately, not just on
      // the next move -- pushFacedTarget recomputes from scratch, so a now-
      // hidden signpost is excluded right away.
      this.pushFacedTarget();
    });
    onLinkedSourceChange(() => this.refreshMarkIndicator());
    onWaitRequested(() => {
      if (!isTurnMode()) return;
      const events: SimEvents = [];
      recordUndoPoint(getWorld());
      recordCommand({ kind: 'wait' });
      applyCommand(getWorld(), { kind: 'wait' }, events);
      this.applySimEvents(events);
      this.refreshAfterTurn();
    });
    onUndoRequested(() => this.undoTurn());
    // A cast's two edges (see turnActions.ts): snapshot before any effect runs,
    // spend the turn after every one of them has.
    onCastBegan(() => {
      if (isTurnMode()) recordUndoPoint(getWorld());
    });
    onCastEnded(() => {
      if (!isTurnMode()) return;
      const events: SimEvents = [];
      advanceTurn(getWorld(), events);
      this.applySimEvents(events);
      this.refreshAfterTurn();
    });
    // Switching clocks throws the history away rather than carrying it across.
    // Snapshots taken under one clock are not wrong under the other so much as
    // meaningless: undoing into the wall clock's world restores a position the
    // room has since decayed past, which is the whole reason the two options are
    // one option (see settings.ts).
    onClockModeChange(() => {
      applyClockSetting();
      clearUndoHistory();
      refreshTurnHud();
    });
    onRoomJump((roomId) => {
      // Dev-only teleport, bypassing doors/locks entirely. (1,1) is always a safe
      // interior floor tile in every room's layout -- nothing is ever placed there.
      if (this.rooms[roomId]) this.enterRoom(roomId, 1, 1, 'down');
    });
    // An interaction's effects, on their way in from ui.ts (see
    // interactEffects.ts). The mutations -- taking an item, learning a rune,
    // filling an altar, throwing a lever -- and the settle that follows them
    // all happen in `sim/interact.ts` now; what arrives here is what that
    // produced, and it goes through the same `applySimEvents` a cast or a step
    // does.
    //
    // This listener used to *be* the altar mechanism: it wrote `altar.filled`,
    // swapped the interact, retextured the sprite and settled, which made it the
    // last place in the game where world state was written from the renderer.
    onInteractEffects((events) => {
      this.applySimEvents(events);
      // What the player is facing may now read differently -- a filled altar
      // says something else when inspected, and room10's Rune Circle may have
      // just become a Portal.
      this.pushFacedTarget();
    });
    registerSnapshotProvider((): RoomSnapshot => ({
      roomId: this.currentRoomId,
      tileX: this.tileX,
      tileY: this.tileY,
      facing: this.facing,
      rooms: serializeRoomMutations(this.rooms),
    }));
    registerSnapshotApplier((snapshot: RoomSnapshot) => {
      getWorld().rooms = buildRoomsForPlay();
      applyRoomMutations(this.rooms, snapshot.rooms);
      this.enterRoom(snapshot.roomId, snapshot.tileX, snapshot.tileY, snapshot.facing as Facing);
    });

    this.addScreenSpaceUi(
      this.add
        .text(0, 0, 'WASD / Arrows / touch to move', { fontSize: '14px', color: '#e8e8e8', fontFamily: 'monospace' })
        .setScrollFactor(0)
        .setDepth(100),
      8,
      8,
    );

    // y=100 -- everywhere from y=26 through the link-indicator's ~94 is taken
    // by the DOM overlay's hud-bars/room-name-label/link-indicator (index.html),
    // which sit on top of this canvas text and silently clipped it there.
    this.interactHintLabel = this.addScreenSpaceUi(
      this.add
        .text(0, 0, 'Face something, then tap Interact to look at it', { fontSize: '14px', color: '#e8e8e8', fontFamily: 'monospace' })
        .setScrollFactor(0)
        .setDepth(100)
        .setVisible(getTutorialEnabled() && !getHasInteractedOnce()),
      8,
      100,
    );

    // Above the floor and every walkable fixture, below the objects themselves
    // (0.95) so an outline never hides the sprite it is describing.
    this.conductivityLayer = this.add.graphics().setDepth(0.6);

    // Darkness, above every object (0.95) and above the player (1) -- it is the
    // dark, so it covers what is in it. Below the cast effects at 55, which is
    // deliberate: a projection thrown into an unlit corner lights it for as long
    // as it is drawn, which is the correct thing for a beam of Energy to do and
    // costs nothing to arrange.
    this.darknessLayer = this.add.graphics().setDepth(2.2);
    this.playerDebugLabel = this.add
      .text(0, 0, '', { fontSize: '11px', color: '#e8e8e8', fontFamily: 'monospace', backgroundColor: '#000000a0' })
      .setOrigin(0.5, 1)
      .setDepth(60)
      .setVisible(false);

    this.input.on('pointerdown', this.handlePointerDown, this);
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);

    // Scale.RESIZE doesn't automatically resize the camera viewport to match the new
    // canvas size (rotating a phone, or a browser window resize) -- without this the
    // camera would keep its boot-time viewport dimensions while the canvas around it
    // changes size.
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      this.cameras.main.setSize(gameSize.width, gameSize.height);
      const room = this.rooms?.[this.currentRoomId];
      if (room) this.applyCenteredBounds(room);
      // The screen's center moved, and screen-space placement is expressed
      // relative to it (see placeScreenSpaceUi).
      this.layoutScreenSpaceUi();
    });

    // After the overlay texts above, because it lays them out, and after the
    // first enterRoom, whose applyCenteredBounds ran at the old zoom. Still
    // inside create(), so the very first frame is already drawn at the chosen
    // size rather than snapping to it a frame later.
    this.applyTileSize();
    onTileSizeChange(() => this.applyTileSize());
  }

  // The whole of the tile-size setting: one camera zoom. Nothing in the world,
  // the simulation or this file's arithmetic knows it happened -- a tile is
  // still TILE world pixels, the player still moves one tile per step, and the
  // only thing that changes is how many of those tiles fit on the screen.
  private applyTileSize(): void {
    this.cameras.main.setZoom(getTileSize() / TILE);
    // A room that comfortably fitted the screen at 32px tiles may not at 64,
    // and vice versa -- which of the two cases a room is in is exactly what
    // applyCenteredBounds decides, so it has to be redone.
    const room = this.rooms?.[this.currentRoomId];
    if (room) this.applyCenteredBounds(room);
    this.layoutScreenSpaceUi();
    // Same reasoning as activeWorld.ts's __world hook: the only on-screen
    // evidence that this worked is that everything got bigger, and a test that
    // asserts it by sampling canvas pixels is a test about antialiasing.
    // Nothing in the game reads this back.
    (window as unknown as { __cameraZoom: number }).__cameraZoom = this.cameras.main.zoom;
  }

  // Pins an overlay object to a screen position, at its natural size, in spite
  // of the camera zoom -- see the screenSpaceUi field for why that isn't just
  // setScrollFactor(0). Returns the object so it can be registered inline at
  // the point it's created, which is the only place that knows where it goes.
  private addScreenSpaceUi<T extends ScreenSpaceUi>(obj: T, x: number, y: number): T {
    // Drop entries whose object has since been destroyed (roomAmbientLabel is
    // rebuilt per room), and any earlier registration of this same object.
    this.screenSpaceUi = this.screenSpaceUi.filter((entry) => !!entry.obj.scene && entry.obj !== obj);
    this.screenSpaceUi.push({ obj, x, y });
    this.placeScreenSpaceUi(obj, x, y);
    return obj;
  }

  private layoutScreenSpaceUi(): void {
    for (const { obj, x, y } of this.screenSpaceUi) this.placeScreenSpaceUi(obj, x, y);
  }

  // The camera renders every object -- scrollFactor 0 included -- at
  // `center + zoom * (position - center)`, where center is the middle of the
  // viewport. Inverting that gives the position an object must hold to land on
  // the screen coordinate it asked for; scaling by 1/zoom cancels the size half
  // of the same transform. The two multiply out to an identity, so a Text drawn
  // this way is sampled once at its natural size, not scaled down and back up.
  private placeScreenSpaceUi(obj: ScreenSpaceUi, x: number, y: number): void {
    const zoom = this.cameras.main.zoom;
    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;
    obj.setScale(1 / zoom);
    obj.setPosition(centerX + (x - centerX) / zoom, centerY + (y - centerY) / zoom);
  }

  // Every room so far is smaller than a typical viewport in at least one
  // dimension, and Phaser's camera bounds always clamp scroll to the bounds'
  // own top-left corner when the room doesn't fill the screen -- there's no
  // built-in "center small content" mode. Padding the bounds out symmetrically
  // (by however much screen space the room doesn't fill) and letting the
  // follow-clamp land in the middle of that padding is what actually centers
  // it; a room that's bigger than the viewport in a given axis gets zero
  // padding there and scrolls exactly as before.
  private applyCenteredBounds(room: RoomDef): void {
    const roomWidthPx = room.cols * TILE;
    const roomHeightPx = room.rows * TILE;
    // How much of the *world* the screen shows, which is the viewport divided
    // by the zoom rather than the viewport itself: at 64px tiles the camera
    // sees half as many world pixels across, so it needs half the padding --
    // and a room that fitted at 32 may now overflow and want none at all.
    const viewWidthPx = this.scale.width / this.cameras.main.zoom;
    const viewHeightPx = this.scale.height / this.cameras.main.zoom;
    const padX = Math.max(0, (viewWidthPx - roomWidthPx) / 2);
    const padY = Math.max(0, (viewHeightPx - roomHeightPx) / 2);
    this.cameras.main.setBounds(-padX, -padY, roomWidthPx + padX * 2, roomHeightPx + padY * 2);
  }

  // The frame boundary. Phaser does not catch what a scene's update() throws,
  // so before this an exception here took the whole render loop down with it:
  // a frozen canvas, no error anywhere the player could see, and no signal at
  // all on our side. Now the bad frame is skipped and the game keeps drawing.
  //
  // This is also the boundary that most needs errorTelemetry's signature
  // dedup, and the reason it exists: a throw in here is not one crash, it is
  // one crash per frame at ~60/second until the tab closes. Reported once,
  // counted thereafter, with the total sent on unload.
  update(time: number, delta: number): void {
    try {
      this.updateFrame(time, delta);
    } catch (err) {
      reportError(err, 'update');
    }
  }

  private updateFrame(_time: number, delta: number): void {
    this.syncDebugLabelPositions();
    this.redrawConductivityOverlay();
    this.syncLinkIndicatorPositions();
    this.refreshPlayerAura();
    if (!this.ambientTimersStarted && !isPreGameScreenOpen()) {
      this.ambientTimersStarted = true;
      this.startAmbientTimers();
    }
    // Advance the world by this frame's own elapsed time, but only once the
    // pre-game screens have closed -- registering the old Phaser timers in
    // create() would have anchored their schedule to scene-boot wall-clock time
    // instead of to when a life actually starts, and this preserves that.
    //
    // And only on the wall clock. Under the turn clock the frame loop is not
    // what makes time pass -- the player is (see clockMode.ts) -- so this is the
    // single line that switches the whole game between the two. Everything below
    // it still runs every frame: reading input, syncing sprite positions and
    // tweens, refreshing the HUD. Turn-based does not mean the game stops
    // drawing, only that the world stops moving on its own.
    if (this.ambientTimersStarted && !isTurnMode()) this.advanceWorld(delta);
    // Bidirectional, not just "hide once" -- New Game resets the underlying
    // flag (see tutorialHints.resetTutorialHints), and this is what actually
    // brings the hint back for a fresh life, not just suppresses it forever.
    const shouldShowInteractHint = getTutorialEnabled() && !getHasInteractedOnce();
    if (this.interactHintLabel.visible !== shouldShowInteractHint) this.interactHintLabel.setVisible(shouldShowInteractHint);
    if (this.isMoving) {
      if (this.time.now - this.moveStartedAt > MOVE_DURATION * 3) this.isMoving = false;
      else return;
    }
    if (isMenuOpen()) return;
    const dir = this.readDirection();
    if (!this.acceptsStep(dir)) return;

    this.facing = dir;
    this.player.setTexture(this.playerTexture(dir));
    this.tryMove(dir);
    // Interact/Mark/Cast's target is whatever's directly ahead -- recompute on
    // every attempted move, whether or not it actually moved (turning to face a
    // new direction while blocked still changes the target, same as it changes
    // Project's facing-based aim in ui.ts's performCast).
    this.pushFacedTarget();
  }

  // The single, unambiguous target Interact/Mark/Cast (with no marked source)
  // resolve to -- whatever's on the tile directly ahead of the player, if
  // anything. Replaces the old model of tapping a specific object's own
  // sprite: with one shared action button instead of one button per object,
  // there has to be exactly one target, and facing (not "any adjacent tile")
  // is what a single-button Zelda-style interact model actually needs to
  // disambiguate standing between two objects at once.
  // True for a dev-signpost-* object while Developer Tools' "Show dev
  // signposts" toggle is off -- checked wherever a signpost's in-world
  // presence matters (facing/Interact, sprite visibility), never by mutating
  // room.objects itself, so this can't be confused with a real save
  // mutation (see settings.ts's setShowDevSignposts).
  private isDevSignpostHidden(obj: ObjectDef): boolean {
    return obj.id.startsWith('dev-signpost') && !getShowDevSignposts();
  }

  private getFacedObject(): ObjectDef | undefined {
    const offset = DIRECTION_OFFSETS[this.facing];
    const fx = this.tileX + offset.x;
    const fy = this.tileY + offset.y;
    const atTile = this.rooms[this.currentRoomId].objects.filter((o) => o.x === fx && o.y === fy && !this.isDevSignpostHidden(o));
    // room8's pressure pads are walkable (a floor fixture, not an obstacle),
    // so a rock resting on one shares its tile with the pad object -- prefer
    // the actual physical thing sitting there over the fixture underneath it,
    // falling back to the fixture only when nothing else occupies the tile.
    return atTile.find((o) => !o.walkable) ?? atTile[0];
  }

  // Pushes the current faced object (or null) to ui.ts, which has no Phaser
  // scene reference of its own -- same one-directional call shape already used
  // for updateStatsHud(). Called after every move attempt, after entering a
  // room, and after anything that removes an object (see removeObject) --
  // exactly the events that could change what's directly ahead of the player.
  private pushFacedTarget(): void {
    const obj = this.getFacedObject();
    // What makes something markable is `sim/queries.ts`'s `isLinkable` and no
    // longer a condition written out here -- the analysis search asks the same
    // question when it decides whether marking is a move the caster could make,
    // and two statements of the rule would let it propose one they could not.
    updateFacedTarget(obj ? { id: obj.id, name: obj.name, interact: obj.interact, linkable: Q.isLinkable(obj) } : null);
  }

  /** Whether this frame's held direction should become a step.
   *
   * On the wall clock: yes, whenever a direction is held, exactly as it always
   * has -- the `isMoving` tween lock above is what paces walking, and holding a
   * key walks continuously.
   *
   * On the turn clock that pacing is not good enough, and it is worth being
   * exact about how much. A turn is a discrete, consequential thing: mobs step,
   * reservoirs drain, a charge network relaxes. `readDirection` polls `isDown`,
   * so a held key steps again as soon as the tween lock lifts -- not once per
   * frame, but once per `MOVE_DURATION`, about seven turns a second. Leaning on
   * an arrow key for two seconds would put fourteen turns through a world the
   * player is not watching. So a *press* is what steps, not a hold.
   *
   * (A held key against a *wall* really did poll every frame, since a blocked
   * step never sets the tween lock. That one costs nothing now for a different
   * reason -- a step that moved nothing is not charged a turn -- but it is why
   * the two guards are worth having separately.)
   *
   * With one concession, because pure edge-triggering makes crossing a large
   * room genuinely unpleasant: after `TURN_REPEAT_DELAY_MS` of holding the same
   * direction, it repeats every `TURN_REPEAT_INTERVAL_MS`. That is a wall-clock
   * number inside a turn-based mode, which looks like a contradiction and is
   * not: it paces the *input*, not the world. Nothing decays between two
   * repeats that would not have decayed between two taps -- each repeat is a
   * whole turn, identical to one the player typed. It is the same accommodation
   * a key-repeat is in a text editor.
   *
   * Covers all three input schemes at once because all three arrive through
   * `readDirection`: WASD/arrows via `isDown`, the on-screen D-pad and the
   * quadrant-tap scheme via `touchDirection`. */
  private acceptsStep(dir: Facing | null): dir is Facing {
    if (!dir) {
      this.heldDirection = null;
      return false;
    }
    if (!isTurnMode()) return true;
    const now = this.time.now;
    // A new direction is always a fresh press, even mid-hold of another one --
    // pivoting from Left to Up is a deliberate second input, not a repeat.
    if (dir !== this.heldDirection) {
      this.heldDirection = dir;
      this.heldSince = now;
      this.lastRepeatAt = 0;
      return true;
    }
    if (now - this.heldSince < TURN_REPEAT_DELAY_MS) return false;
    if (now - this.lastRepeatAt < TURN_REPEAT_INTERVAL_MS) return false;
    this.lastRepeatAt = now;
    return true;
  }

  private readDirection(): Facing | null {
    if (this.cursors.up.isDown || this.wasd.up.isDown) return 'up';
    if (this.cursors.down.isDown || this.wasd.down.isDown) return 'down';
    if (this.cursors.left.isDown || this.wasd.left.isDown) return 'left';
    if (this.cursors.right.isDown || this.wasd.right.isDown) return 'right';
    return this.touchDirection;
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (isMenuOpen()) return;
    // In D-pad mode, screen taps are for tapping objects/self, not walking --
    // the D-pad's own buttons (see onDpadDirection above) own movement instead.
    if (getMovementInputMode() === 'dpad') return;
    this.updateTouchDirection(pointer);
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!pointer.isDown) return;
    if (isMenuOpen()) {
      this.clearTouch();
      return;
    }
    if (getMovementInputMode() === 'dpad') return;
    this.updateTouchDirection(pointer);
  }

  private handlePointerUp(): void {
    this.clearTouch();
  }

  private updateTouchDirection(pointer: Phaser.Input.Pointer): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;
    const dx = pointer.x - cx;
    const dy = pointer.y - cy;
    const dir: Facing = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
    this.touchDirection = dir;
    this.renderTouchWedge(dir);
  }

  private clearTouch(): void {
    this.touchDirection = null;
    this.touchGraphics?.clear();
  }

  // Visual feedback for the currently-held wedge -- without this, touch controls are
  // an invisible D-pad, which is confusing on first contact. Faint on purpose: it
  // should read as a confirmation, not compete with the tilemap for attention.
  private renderTouchWedge(dir: Facing): void {
    if (!this.touchGraphics) {
      // Registered at (0,0) and then drawn below in plain screen coordinates:
      // the wedge covers the whole viewport, so "where it goes" is the screen's
      // own top-left corner and the registration is entirely about undoing the
      // camera's zoom.
      this.touchGraphics = this.addScreenSpaceUi(this.add.graphics().setScrollFactor(0).setDepth(90), 0, 0);
    }
    const w = this.scale.width;
    const h = this.scale.height;
    const cx = w / 2;
    const cy = h / 2;

    this.touchGraphics.clear();
    this.touchGraphics.fillStyle(0xc9a24b, 0.12);
    if (dir === 'up') this.touchGraphics.fillTriangle(cx, cy, 0, 0, w, 0);
    else if (dir === 'down') this.touchGraphics.fillTriangle(cx, cy, w, h, 0, h);
    else if (dir === 'left') this.touchGraphics.fillTriangle(cx, cy, 0, h, 0, 0);
    else this.touchGraphics.fillTriangle(cx, cy, w, 0, w, h);
  }

  private tryMove(dir: Facing): void {
    const offset = DIRECTION_OFFSETS[dir];
    const targetX = this.tileX + offset.x;
    const targetY = this.tileY + offset.y;

    const door = this.doorAt(targetX, targetY);
    if (door) {
      if (door.lockId && isDoorLocked(door.lockId)) {
        openDoorPanel(door.lockId, door.keyItemId);
        return;
      }
      // enterRoom is an instant teleport, not a tweened move -- without this,
      // a direction key still physically held at the exact moment it crosses
      // a doorway would get processed again on the very next update() tick,
      // now against the *new* room, producing one uncontrolled extra step
      // straight past the threshold (isMoving is what normally blocks that;
      // an ordinary move sets it via movePlayerTo, but a door never went
      // through there). Cleared after one ordinary move's worth of time,
      // same as movePlayerTo's own tween onComplete would, rather than
      // leaning on update()'s longer (3x) stuck-tween fallback -- crossing a
      // doorway should feel like one step, not a longer, separate pause.
      this.isMoving = true;
      this.moveStartedAt = this.time.now;
      this.time.delayedCall(MOVE_DURATION, () => {
        this.isMoving = false;
      });
      this.enterRoom(door.toRoom, door.spawnX, door.spawnY, door.spawnFacing);
      return;
    }

    // The whole walk-into decision -- push, follow a bind, redirect through
    // one, or stop -- plus the step itself now lives in the sim (see
    // Casts.walkPlayer), so it is reachable from a headless test and cannot
    // drift from the bind rules sim/ already owns. Doors stay here: crossing
    // one is a room transition with UI attached, not a step.
    //
    // Routed through `applyCommand` rather than calling `Casts.walkPlayer`
    // directly (2026-08-12). Two things follow from the indirection, and the
    // second is the reason for it. A walk becomes a `SimCommand`, so it is
    // finally something a recording can contain -- before this it was the one
    // thing a player could do that `replay.ts` had no vocabulary for. And the
    // turn clock's cost rule lives on the sim's side of the boundary, where the
    // editor and a replay obey exactly the same one, instead of being restated
    // here in terms this file would have had to keep in step by hand.
    //
    // The undo snapshot is taken before the command, not after, and only in turn
    // mode -- see undoHistory.ts for why a snapshot per *turn* rather than per
    // frame is the only version of this that is affordable.
    const events: SimEvents = [];
    const turnMode = isTurnMode();
    if (turnMode) recordUndoPoint(getWorld());
    const before = getWorld().timeMs;
    // Recorded whether or not the step lands: `applyCommand` is what decides
    // that, and a walk into a wall still turns the caster (see `walkPlayer`), so
    // a take that dropped it would replay facing somewhere else.
    recordCommand({ kind: 'step', dir });
    applyCommand(getWorld(), { kind: 'step', dir }, events);
    this.applySimEvents(events);
    if (turnMode) {
      // The snapshot had to be taken before the step, which means before knowing
      // whether there was going to be one -- a walk into a wall takes this same
      // path and only the sim can tell the two apart. The clock is what says
      // which happened: `applyCommand` charges a turn exactly when something
      // moved. A wall gets its snapshot dropped rather than leaving the Undo
      // button lit for a move that never occurred.
      if (getWorld().timeMs === before) discardUndoPoint();
      // A turn may have moved a mob, decayed a reservoir or drifted the caster's
      // own thermalState, none of which the wall-clock path's own refresh covers
      // here -- that one runs from advanceWorld, which no longer fires.
      else this.refreshAfterTurn();
    }
  }

  /** What `advanceWorld` does for the wall clock, for a turn instead: the HUD
   * and the debug readouts are refreshed because a turn's worth of ambient rules
   * just ran, not because anything was drawn.
   *
   * Unconditional, unlike `advanceWorld`'s "did any rule actually fire" gate. A
   * turn is rare (one per deliberate player action) where a frame is not, so the
   * gate would be saving nothing and costing a class of bug where the HUD lags
   * one action behind the world. */
  private refreshAfterTurn(): void {
    updateStatsHud();
    this.refreshDebugLabels();
    this.pushFacedTarget();
    refreshUndoAvailability();
  }


  private doorAt(x: number, y: number): DoorDef | undefined {
    return Q.doorAt(this.rooms[this.currentRoomId], x, y);
  }


  private tileCenter(x: number, y: number): { x: number; y: number } {
    return { x: x * TILE + TILE / 2, y: y * TILE + TILE / 2 };
  }

  private playerTexture(dir: Facing): string {
    return `player-${getSpriteId()}-${dir}`;
  }

  private enterRoom(roomId: string, spawnX: number, spawnY: number, facing: Facing): void {
    this.lastEntrySpawn = { x: spawnX, y: spawnY, facing };
    this.currentRoomId = roomId;
    updateRoomName(roomId, this.rooms[roomId].name);
    trackEvent('room_enter', { roomId });
    // Arriving is a break in continuity: a snapshot describes one room (see
    // WorldSnapshot), so nothing taken on the other side of this doorway can be
    // restored into the world the player is now standing in.
    clearUndoHistory();
    this.rebuildRoomVisuals(roomId, spawnX, spawnY, facing);
    // After `rebuildRoomVisuals`, not before: that is what puts the caster on the
    // spawn tile, and a take's `start` has to be where the commands after it
    // actually began. Same break-in-continuity argument as `clearUndoHistory`
    // above -- a Recording covers one room.
    beginTake(getWorld(), roomId);
  }

  /** Draws the current room from world state, from nothing: tilemap, every
   * object sprite and its debug label, the ambient readout, the mark and all
   * four families of bind indicator, the caster, the camera.
   *
   * Split out of `enterRoom` (2026-08-12) so that undo can reuse it. Undo needs
   * exactly this and none of what stayed behind -- an undone move is not an
   * arrival, so it must not push a `room_enter` analytics event, must not move
   * `lastEntrySpawn` (which is where a *restart* puts the player back, and
   * undoing a step is not a restart), and must not re-run any arrival effect.
   *
   * Total teardown and rebuild rather than a diff against what changed, which
   * is what `undoHistory.ts` requires of its caller and what makes rewinding the
   * runtime-object id counters safe: after this there is no sprite left holding
   * an id from before the restore. It is also simply what `enterRoom` has always
   * done, so undo is reusing a path the game exercises on every door crossing
   * rather than a second one written to be equivalent to it. */
  private rebuildRoomVisuals(roomId: string, spawnX: number, spawnY: number, facing: Facing): void {
    const room = this.rooms[roomId];

    this.layer?.destroy();
    this.tilemap?.destroy();
    this.objectSprites.forEach((s) => {
      (s.getData('debugLabel') as Phaser.GameObjects.Text | undefined)?.destroy();
      s.destroy();
    });
    this.objectSprites = [];
    this.roomAmbientLabel?.destroy();
    this.gridCoordLabels.forEach((label) => label.destroy());
    this.gridCoordLabels = [];
    // A Measure + Radial snapshot from the room being left no longer applies.
    this.radialMeasureGraphics?.destroy();
    this.radialMeasureGraphics = undefined;
    // The marked object may not exist in the new room -- rebuilt below once
    // objects are back in place, not carried over from the old room's sprite.
    this.markIndicatorSprite?.destroy();
    this.markIndicatorSprite = undefined;
    // Same story for bind indicators -- the old room's object ids mean
    // nothing here, so every one of them is torn down and rebuilt below
    // rather than carried over as an orphaned label pointing at nothing.
    this.linkIndicators.forEach((label) => label.destroy());
    this.linkIndicators.clear();

    const tilemap = this.make.tilemap({ data: room.data, tileWidth: TILE, tileHeight: TILE });
    const tileset = tilemap.addTilesetImage(TILESET_KEY, TILESET_KEY, TILE, TILE, 0, 0)!;
    const layer = tilemap.createLayer(0, tileset, 0, 0)!;
    // Arcade collision is vestigial for the player -- every step goes through
    // the sim's own isWalkable (see tryMove -> Casts.walkPlayer) -- but if
    // anything ever does lean on it, a void has to stop it for the same reason
    // a wall does.
    layer.setCollision([WALL, VOID]);
    this.tilemap = tilemap;
    this.layer = layer;

    // A single room-wide readout, not per-tile -- ambientEnergy is one baseline
    // for the whole room, not something each object has its own copy of.
    this.roomAmbientLabel = this.addScreenSpaceUi(
      this.add
        // 5, not 0 -- an unset room now defaults to thermalStart (neutral for
        // the caster), same as RoomDef.ambientEnergy's own doc comment.
        .text(0, 0, `Room ambient Energy: ${room.ambientEnergy ?? 5}`, {
          fontSize: '11px',
          color: '#e8e8e8',
          fontFamily: 'monospace',
          backgroundColor: '#000000a0',
        })
        .setScrollFactor(0)
        .setDepth(100)
        .setVisible(getShowEnergyDebug()),
      8,
      100,
    );

    for (const obj of room.objects) {
      const sprite = this.createObjectSprite(obj);
      if (this.isDevSignpostHidden(obj)) sprite.setVisible(false);
      this.objectSprites.push(sprite);
      // A reformable object's sprite is created from its resting `texture` --
      // sync it to whatever stage its actual current properties call for,
      // since melt/reform progress persists across room visits but the base
      // texture field never changes.
      if (obj.barrier?.reformable) this.refreshReformableVisual(obj);
    }
    // Re-sync room9's water-flow visuals to whatever progress the aqueduct/
    // channel already has (a no-op via the water.source guard in every
    // other room), along with the rest of the declared system order.
    this.settle();

    this.tileX = spawnX;
    this.tileY = spawnY;
    this.facing = facing;
    const { x, y } = this.tileCenter(spawnX, spawnY);
    this.player.setPosition(x, y);
    this.player.setTexture(this.playerTexture(facing));
    this.player.setDepth(1);

    this.applyCenteredBounds(room);
    this.cameras.main.stopFollow();
    this.cameras.main.startFollow(this.player, true, 0.15, 0.15);

    this.refreshDebugLabels();
    this.refreshGridCoordinateLabels();
    this.refreshMarkIndicator();
    this.refreshLinkIndicators();
    this.pushFacedTarget();
    // Last, because it reads the caster's own tile -- which is only correct
    // once the spawn above has been applied.
    this.refreshDarkness();
  }

  /** Steps the world back one turn and redraws it, or does nothing if there is
   * nothing to step back to.
   *
   * The redraw goes through `rebuildRoomVisuals` at the restored caster's own
   * position and facing, rather than through `enterRoom`: undoing is not
   * arriving. Everything the player can see is rebuilt from the restored world,
   * which is the whole safety argument for undo -- there is no incremental path
   * here that could leave one sprite describing a world that no longer exists.
   *
   * Deliberately not gated on `isTurnMode()`. Nothing can ever be *in* the
   * history outside turn mode (`recordUndoPoint` is only called there, and
   * switching modes is one of the things that clears it), so the gate would be
   * restating a guarantee the stack already gives -- and if it ever stopped
   * being true, failing loudly by undoing is better than silently refusing. */
  private undoTurn(): void {
    const world = getWorld();
    if (!undo(world)) return;
    this.rebuildRoomVisuals(world.player.roomId, world.player.x, world.player.y, world.player.facing);
    updateStatsHud();
    updateLinkIndicator();
  }

  // Developer Tools option: builds one (x,y) text label per tile of the
  // current room, only when the toggle is actually on (destroyed and left
  // empty otherwise -- a full room's worth of tiles isn't worth creating
  // unconditionally the way the small per-object debug labels are). Called on
  // room entry and again from the onDebugOverlayChange listener so flipping
  // the checkbox mid-room shows/hides it immediately rather than waiting for
  // the next room change.
  private refreshGridCoordinateLabels(): void {
    this.gridCoordLabels.forEach((label) => label.destroy());
    this.gridCoordLabels = [];
    if (!getShowGridCoords()) return;

    const room = this.rooms[this.currentRoomId];
    for (let ty = 0; ty < room.rows; ty++) {
      for (let tx = 0; tx < room.cols; tx++) {
        const { x, y } = this.tileCenter(tx, ty);
        const label = this.add
          .text(x, y, `${tx},${ty}`, { fontSize: '9px', color: '#ffffffb0', fontFamily: 'monospace' })
          .setOrigin(0.5, 0.5)
          .setDepth(0.5);
        this.gridCoordLabels.push(label);
      }
    }
  }

  // Developer Tools debug overlay: refreshes label *content and visibility*
  // from current state -- called after anything that changes a relevant value
  // (a cast, a decay/regen tick, entering a room) or when a checkbox is
  // toggled. Position tracking is separate (see update()'s per-frame sync)
  // since a label needs to keep following its sprite through a move tween,
  // not just when its underlying value happens to change.
  private refreshDebugLabels(): void {
    const showEnergy = getShowEnergyDebug();
    const showCharge = getShowChargeDebug();
    const showHealth = getShowHealthDebug();
    const showEntropy = getShowEntropyDebug();
    const room = this.rooms[this.currentRoomId];

    for (const sprite of this.objectSprites) {
      const label = sprite.getData('debugLabel') as Phaser.GameObjects.Text | undefined;
      if (!label) continue;
      const obj = room.objects.find((o) => o.id === sprite.getData('objectId'));
      // Short 3-letter forms (eng/chg/hlt/ent), not the full concept names --
      // a room dense with adjacent properties-bearing tiles (room11's charged
      // floor grid, dozens of objects a couple of pixels apart) had labels
      // wide enough to visibly overlap into a neighbouring tile's own sprite.
      const parts: string[] = [];
      if (obj?.properties) {
        if (showEnergy && obj.properties.energy !== undefined) parts.push(`eng ${obj.properties.energy.toFixed(1)}`);
        if (showCharge && obj.properties.charge !== undefined) parts.push(`chg ${obj.properties.charge.toFixed(1)}`);
        if (showEntropy && obj.properties.entropy !== undefined) parts.push(`ent ${obj.properties.entropy.toFixed(1)}`);
      }
      label.setText(parts.join(' / '));
      label.setVisible(parts.length > 0);
    }

    if (this.playerDebugLabel) {
      const parts: string[] = [];
      if (showEnergy) parts.push(`mana ${getMana().toFixed(1)}`);
      if (showEnergy) parts.push(`thm ${getThermalState().toFixed(1)}`);
      if (showCharge) parts.push(`chg ${getCharge().toFixed(1)}`);
      if (showHealth) parts.push(`hlt ${getHealth().toFixed(1)}`);
      this.playerDebugLabel.setText(parts.join(' / '));
      this.playerDebugLabel.setVisible(parts.length > 0);
    }

    this.roomAmbientLabel?.setVisible(showEnergy);
    this.refreshConductivityReadings();
  }

  // Developer Tools overlay: what conducts, what does not, and what potential
  // each network is sitting at.
  //
  // The two questions "why is this circuit dead" actually has, neither of which
  // anything on screen answered before. A run of cable draws the same picture
  // carrying 8 as carrying nothing; a Charged Font resting *beside* a cable
  // rather than *on* it (adjacency joins two conductive *fixtures*; a store
  // joins by sharing a tile) is indistinguishable from one that is wired in.
  // Both were real, reported confusions about a real authored room.
  //
  // Recomputed here rather than per frame because `conductionReading` walks the
  // object's whole charge network -- cheap once per mutation, wasteful sixty
  // times a second. `redrawConductivityOverlay` below draws it from the cache.
  private refreshConductivityReadings(): void {
    this.conductivityReadings.clear();
    if (!getShowConductivityDebug()) {
      this.conductivityLayer?.clear();
      return;
    }
    const room = this.rooms[this.currentRoomId];
    if (!room) return;
    const caster = { charge: getCharge() };
    for (const obj of room.objects) {
      const reading = Q.conductionReading(room, obj, getWorld().physics, caster);
      if (reading) this.conductivityReadings.set(obj.id, reading);
    }
  }

  // The geometry half, redrawn every frame the overlay is on so an outline
  // tracks its sprite through a move tween rather than snapping at the end of
  // one.
  //
  // Three outline colours, and the third one earns its place:
  //
  //   green   conducts right now -- charge crosses this
  //   amber   a *store* that does not conduct: a Charged Font, a Standing
  //           Pillar. Not a fault, which is exactly why it must not be red --
  //           a source drawn in the same colour as a corroded cable reads as
  //           the broken thing in a circuit it is in fact powering.
  //   red     a break: takes part in the Charge system and carries nothing
  //           onward. A corroded cable, a thrown breaker, a wood block in a
  //           conduit path, a dry or frozen channel.
  //
  // The fill is the heat map: the object's network potential as a fraction of
  // the caster's own ceiling, so a dead line is empty and a live one is visibly
  // charged. That is the half that answers "is this all one circuit" -- two
  // things on the same network are filled identically, and a store sitting
  // *beside* a run rather than *on* it shows as a bright square against empty
  // ones. A store also takes a heavier border, since "this one divides the line"
  // is what most often explains a reading nobody expected.
  private redrawConductivityOverlay(): void {
    const layer = this.conductivityLayer;
    if (!layer) return;
    layer.clear();
    if (this.conductivityReadings.size === 0) return;
    const ceiling = Math.max(1, getMaxCharge());
    for (const sprite of this.objectSprites) {
      const reading = this.conductivityReadings.get(sprite.getData('objectId') as string);
      if (!reading) continue;
      const color = reading.conducts ? 0x4bd97a : reading.stores ? 0xe0a53c : 0xd9473f;
      const x = sprite.x - TILE / 2;
      const y = sprite.y - TILE / 2;
      const charged = Math.max(0, Math.min(1, reading.potential / ceiling));
      if (charged > 0) {
        layer.fillStyle(color, 0.12 + charged * 0.38);
        layer.fillRect(x + 2, y + 2, TILE - 4, TILE - 4);
      }
      layer.lineStyle(reading.stores ? 3 : 1.5, color, reading.stores ? 1 : 0.85);
      layer.strokeRect(x + 1.5, y + 1.5, TILE - 3, TILE - 3);
    }
  }

  // Darkness: the tiles the caster cannot currently see, painted over.
  //
  // The simulation answers *which* tiles those are (`sim/light.ts`) and this
  // decides what "cannot see" looks like, which is the same split the water
  // flow, the conduit readings and the conveyor already use -- and it matters
  // more here than usual, because the World Editor draws the identical answer
  // and two opinions about what is lit would be two different rooms.
  //
  // **Nothing about this changes what the player may do.** A dark tile can be
  // walked onto, cast at and pushed through exactly as a lit one can. Darkness
  // is an information problem; see `sim/light.ts` for why that is a decision
  // rather than a first step.
  //
  // Recomputed on settle and on every step rather than per frame: it walks a ray
  // per tile per source, which is cheap once per mutation and wasteful sixty
  // times a second. A room that is not dark clears the layer and returns, which
  // is every room the world currently ships.
  private refreshDarkness(): void {
    const layer = this.darknessLayer;
    if (!layer) return;
    layer.clear();
    const room = this.rooms[this.currentRoomId];
    if (!room || !roomIsDark(room)) return;

    const lit = litTiles(room, { x: this.tileX, y: this.tileY, charge: getCharge() }, getWorld().physics);
    // Not pure black: a flat void reads as a rendering fault, where a very dark
    // blue-grey reads as a room with the lights off. The unlit tiles are still
    // *there*, which is the whole claim darkness is making about them.
    layer.fillStyle(0x05070c, 0.94);
    for (let y = 0; y < room.rows; y++) {
      for (let x = 0; x < room.cols; x++) {
        if (lit.has(`${x},${y}`)) continue;
        const { x: cx, y: cy } = this.tileCenter(x, y);
        layer.fillRect(cx - TILE / 2, cy - TILE / 2, TILE, TILE);
      }
    }
  }

  // Applies the "Show dev signposts" toggle to every signpost sprite already
  // built for the current room -- called on room entry (see enterRoom) and
  // whenever the toggle itself changes, so switching it mid-room takes effect
  // immediately rather than only on the next room visit.
  private refreshDevSignpostVisibility(): void {
    const room = this.rooms[this.currentRoomId];
    for (const obj of room.objects) {
      if (!obj.id.startsWith('dev-signpost')) continue;
      const sprite = this.objectSprites.find((s) => s.getData('objectId') === obj.id);
      sprite?.setVisible(!this.isDevSignpostHidden(obj));
    }
  }

  // Keeps visible debug labels glued to their sprite's *actual* current
  // position every frame, independent of refreshDebugLabels' content/visibility
  // updates -- needed because a pushed block or a recoiling/walking player moves
  // via a tween, and a label positioned only once (at the start of that tween)
  // would visibly detach from its sprite while the animation plays.
  // Reads getThermalState()/getCharge() live (not just at the moment a cast
  // changes them) and updates the two aura sprites' position, color, and
  // alpha to match -- color continuously interpolated across the same
  // cold->hot per-concept gradient Measure already uses (CONCEPT_COLOR_RANGE),
  // so a lukewarm player reads as a color partway between the two, not a
  // sudden on/off flip. Driven by thermalState, not Mana -- this is a body-
  // heat visualization, and the split (see design/12_Mechanics_Energy.md)
  // means those are no longer the same reserve. Charge's pulse is
  // deliberately faster and more irregular (product of two mismatched sine
  // frequencies) than the thermal aura's slow, steady breathing, to read as
  // an erratic spark rather than a calm glow -- see Conservation of Charge in
  // 04_Physics_Engine.md for why Charge, unlike thermalState, normally sits
  // at zero and is rarely visible at all.
  private refreshPlayerAura(): void {
    if (!this.energyAuraSprite || !this.chargeAuraSprite) return;
    const t = this.time.now;

    const energyFraction = Phaser.Math.Clamp(getThermalState() / getMaxThermalState(), 0, 1);
    const energyPulse = 0.85 + 0.15 * Math.sin(t / 500);
    this.energyAuraSprite.setPosition(this.player.x, this.player.y);
    this.energyAuraSprite.setTint(lerpColor(CONCEPT_COLOR_RANGE.energy.cold, CONCEPT_COLOR_RANGE.energy.hot, energyFraction));
    this.energyAuraSprite.setAlpha(energyFraction * 0.5 * energyPulse);

    const currentCharge = getCharge();
    const chargeFraction = Phaser.Math.Clamp(currentCharge / getMaxCharge(), 0, 1);
    const chargePulse = 0.7 + 0.3 * Math.sin(t / 90) * Math.sin(t / 47);
    this.chargeAuraSprite.setPosition(this.player.x, this.player.y);
    this.chargeAuraSprite.setTint(lerpColor(CONCEPT_COLOR_RANGE.charge.cold, CONCEPT_COLOR_RANGE.charge.hot, chargeFraction));
    // sqrt, not linear -- a sliver of Charge (the common case, see Conservation
    // of Charge above) still reads as a real glow instead of rounding down to
    // nothing the way a linear scale would at, say, 1/10 of the ceiling.
    this.chargeAuraSprite.setAlpha(Math.sqrt(chargeFraction) * 0.65 * Math.max(0, chargePulse));

    // The ambient glow alone is easy to miss (see the field comment on
    // lastKnownCharge) -- a real Charge gain also gets a one-off spark, reusing
    // Project·Charge's own fx-bolt so "you got charged" reads the same way
    // "you discharged" already does.
    if (currentCharge > this.lastKnownCharge) this.spawnChargeSpark();
    this.lastKnownCharge = currentCharge;
  }

  // A quick flash of Project·Charge's lightning-bolt FX centered on the player,
  // fired once whenever Charge actually increases (see refreshPlayerAura) --
  // purely decorative, same as animateProjectFx's own tiles.
  private spawnChargeSpark(): void {
    const fx = this.add.sprite(this.player.x, this.player.y, 'fx-bolt').setDepth(1.1).setAlpha(0).setScale(1.3);
    this.tweens.add({
      targets: fx,
      alpha: { from: 0, to: 1 },
      scale: { from: 1.3, to: 1.7 },
      duration: 150,
      yoyo: true,
      hold: 120,
      onComplete: () => fx.destroy(),
    });
  }

  private syncDebugLabelPositions(): void {
    if (!getShowEnergyDebug() && !getShowChargeDebug() && !getShowHealthDebug() && !getShowEntropyDebug()) return;
    for (const sprite of this.objectSprites) {
      const label = sprite.getData('debugLabel') as Phaser.GameObjects.Text | undefined;
      if (label?.visible) label.setPosition(sprite.x, sprite.y - TILE / 2 - 4);
    }
    this.playerDebugLabel?.setPosition(this.player.x, this.player.y - TILE / 2 - 4);
  }

  // Repositions (or hides) the marked-source reticle to match whatever
  // objectLink.ts currently says is marked -- called whenever that changes,
  // whenever a room loads (the marked object may not live in this one), and
  // whenever an object is added/removed/pushed, since any of those can change
  // where (or whether) the marked object actually is right now.
  private refreshMarkIndicator(): void {
    const sourceId = getLinkedSource();
    const room = this.rooms[this.currentRoomId];
    const obj = sourceId ? room.objects.find((o) => o.id === sourceId) : undefined;

    if (!obj) {
      this.markIndicatorSprite?.destroy();
      this.markIndicatorSprite = undefined;
      return;
    }

    const { x, y } = this.tileCenter(obj.x, obj.y);
    if (!this.markIndicatorSprite) {
      this.markIndicatorSprite = this.add.sprite(x, y, 'fx-target').setDepth(3);
      this.tweens.add({
        targets: this.markIndicatorSprite,
        scale: 1.12,
        duration: 650,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else {
      this.markIndicatorSprite.setPosition(x, y);
    }
  }

  // Creates/updates/tears down the floating rune-symbol labels for every link in
  // the room -- called after any Bind/Separate cast (the only thing that changes
  // a link cluster), on room entry (a differently-bound world may be loading),
  // and whenever an object is added or removed (either could be an end of one).
  // Idempotent: safe to call any time, the same shape refreshMarkIndicator and
  // refreshDebugLabels already have.
  //
  // One pass over all four kinds rather than four near-identical passes
  // (2026-08-16). What that fixed is in `linkIndicators.ts`: a link is labelled
  // at **both** ends now, and the labels are packed from what each object
  // actually carries instead of each kind sitting at a fixed rung whether or not
  // anything is below it. The layout is decided there, purely; what is left here
  // is drawing it.
  private refreshLinkIndicators(): void {
    const room = this.rooms[this.currentRoomId];
    const wanted = new Map(linkLabels(room.objects).map((label) => [label.key, label]));

    for (const [key, spec] of wanted) {
      const sprite = this.spriteFor(spec.objectId);
      if (!sprite) {
        wanted.delete(key);
        continue;
      }
      const existing = this.linkIndicators.get(key);
      if (existing) {
        existing.setText(spec.text);
        existing.setColor(spec.color);
        existing.setData('offsetY', spec.offsetY);
        existing.setPosition(sprite.x, sprite.y - TILE / 2 - spec.offsetY);
        continue;
      }
      const label = this.add
        .text(sprite.x, sprite.y - TILE / 2 - spec.offsetY, spec.text, {
          fontSize: '13px',
          color: spec.color,
          fontFamily: 'monospace',
          backgroundColor: '#000000a0',
        })
        .setOrigin(0.5, 1)
        .setDepth(61);
      label.setData('objectId', spec.objectId);
      label.setData('offsetY', spec.offsetY);
      this.tweens.add({ targets: label, scale: 1.2, duration: 550, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      this.linkIndicators.set(key, label);
    }

    for (const [key, label] of this.linkIndicators) {
      if (wanted.has(key)) continue;
      label.destroy();
      this.linkIndicators.delete(key);
    }
  }

  // Keeps every link indicator glued to its object through a move tween, the
  // same "read the sprite's actual on-screen position every frame" approach
  // syncDebugLabelPositions already uses. The offset is read back off the label
  // rather than recomputed, so this cannot disagree with what placed it.
  private syncLinkIndicatorPositions(): void {
    for (const label of this.linkIndicators.values()) {
      const sprite = this.spriteFor(label.getData('objectId') as string);
      if (sprite) label.setPosition(sprite.x, sprite.y - TILE / 2 - (label.getData('offsetY') as number));
    }
  }

  // Every Measure cast darkens the swept area (the whole radius for Radial, or
  // just the single faced tile without it) and highlights only the tiles that
  // actually have a reading for the measured concept, in that concept's
  // low->hot gradient (CONCEPT_COLOR_RANGE) -- everything else in the area
  // stays dark, so what lights up *is* the answer, not a color wash over
  // territory that isn't relevant. Stationary and temporary (a snapshot at
  // cast time), not something that tracks the player.
  private showMeasureVisual(event: RadialMeasureEvent): void {
    this.radialMeasureGraphics?.destroy();
    // Depth 2 -- above both the tilemap and every object/player sprite (depth
    // 1, see createObjectSprite/enterRoom), not just above the floor. Most
    // object textures fill almost their entire tile near-opaquely (the
    // Fireplace's stone alcove, for instance), so anything drawn below sprite
    // depth would be invisibly hidden behind them instead of darkening/
    // highlighting what's actually there.
    const g = this.add.graphics().setDepth(2);
    this.radialMeasureGraphics = g;

    const colors = CONCEPT_COLOR_RANGE[event.conceptId];
    const room = this.rooms[this.currentRoomId];

    if (event.radial) {
      const centerX = this.player.x;
      const centerY = this.player.y;
      const radiusPx = event.radiusTiles * TILE;

      g.fillStyle(0x000000, 0.45);
      g.fillCircle(centerX, centerY, radiusPx);
      g.lineStyle(1, 0xffffff, 0.25);
      g.strokeCircle(centerX, centerY, radiusPx);

      for (const obj of room.objects) {
        const intensity = obj.signature?.[event.conceptId];
        if (intensity === undefined) continue;

        const dx = obj.x - this.tileX;
        const dy = obj.y - this.tileY;
        if (Math.sqrt(dx * dx + dy * dy) > event.radiusTiles) continue;

        const { x: ox, y: oy } = this.tileCenter(obj.x, obj.y);
        const color = lerpColor(colors.cold, colors.hot, intensity);
        g.fillStyle(color, 0.6 + intensity * 0.3);
        g.fillRect(ox - TILE / 2, oy - TILE / 2, TILE, TILE);
      }
    } else {
      // No Radial modifier -- the same darken-then-highlight treatment, just
      // narrowed to the single tile directly ahead instead of an area sweep.
      const offset = DIRECTION_OFFSETS[this.facing];
      const fx = this.tileX + offset.x;
      const fy = this.tileY + offset.y;
      const { x: fPx, y: fPy } = this.tileCenter(fx, fy);

      g.fillStyle(0x000000, 0.45);
      g.fillRect(fPx - TILE / 2, fPy - TILE / 2, TILE, TILE);
      g.lineStyle(1, 0xffffff, 0.25);
      g.strokeRect(fPx - TILE / 2, fPy - TILE / 2, TILE, TILE);

      const obj = room.objects.find((o) => o.x === fx && o.y === fy);
      const intensity = obj?.signature?.[event.conceptId];
      if (intensity !== undefined) {
        const color = lerpColor(colors.cold, colors.hot, intensity);
        g.fillStyle(color, 0.6 + intensity * 0.3);
        g.fillRect(fPx - TILE / 2, fPy - TILE / 2, TILE, TILE);
      }
    }

    this.tweens.add({
      targets: g,
      alpha: 0,
      delay: 1800,
      duration: 700,
      onComplete: () => {
        g.destroy();
        if (this.radialMeasureGraphics === g) this.radialMeasureGraphics = undefined;
      },
    });
  }

  // The four cast handlers below are thin delegators into rune-lab/src/sim/casts.ts
  // (2026-07-30, sim extraction step 4b). Each keeps its original name and
  // signature so ui.ts's bridges are unchanged; each does the same three things --
  // run the sim function against the shared world, apply whatever it reported, and
  // hand ui.ts back the result it narrates from.
  //
  // The trailing refreshDebugLabels() reproduces the unconditional one each handler
  // used to end with. It cannot be an event: a Transfer that melts nothing and
  // moves nothing still changes the numbers those labels display, and inferring
  // "some object's properties changed" from the event list would mean every future
  // mechanism had to remember to announce it. applySimEvents' own refreshes are the
  // ones that genuinely follow from what happened (an object appeared, the player
  // moved); this one follows from a cast having been applied at all.

  private applyTransferEffect(event: TransferEffectEvent): TransferEffectResult {
    const events: SimEvents = [];
    const result = Casts.applyTransferEffect(getWorld(), event, events);
    this.applySimEvents(events);
    this.refreshDebugLabels();
    return result;
  }

  private applyMomentumEffect(event: MomentumEffectEvent): MomentumEffectResult {
    const events: SimEvents = [];
    const result = Casts.applyMomentumEffect(getWorld(), event, events);
    this.applySimEvents(events);
    this.refreshDebugLabels();
    return result;
  }

  private applyProjectEffect(event: ProjectEffectEvent): ProjectEffectResult {
    const events: SimEvents = [];
    const result = Casts.applyProjectEffect(getWorld(), event, events);
    this.applySimEvents(events);
    this.refreshDebugLabels();
    return result;
  }

  private applySeparateEffect(event: SeparateEffectEvent): SeparateEffectResult {
    const events: SimEvents = [];
    const result = Casts.applySeparateEffect(getWorld(), event, events);
    this.applySimEvents(events);
    return result;
  }

  // Bind/Unbind take no `out` -- binding itself changes nothing a renderer
  // would animate (see Casts.applyBindEffect's own comment); only the
  // movement it later causes does, and that already flows through
  // movePlayerTo's own SimEvents.
  private applyBindEffect(event: BindEffectEvent): BindEffectResult {
    const result = Casts.applyBindEffect(getWorld(), event);
    this.refreshLinkIndicators();
    return result;
  }

  private applyUnbindEffect(event: UnbindEffectEvent): UnbindEffectResult {
    const result = Casts.applyUnbindEffect(getWorld(), event);
    this.refreshLinkIndicators();
    return result;
  }

  // Unlike Bind/Unbind·Momentum above, this *does* take `out` -- Bind·Energy
  // settles immediately on cast (see Casts.applyEnergyBindEffect's own
  // comment), so there may already be real stats-changed/texture-set events
  // to run through the renderer from the very first equilibration nudge.
  private applyEnergyBindEffect(event: EnergyBindEffectEvent): EnergyBindEffectResult {
    const events: SimEvents = [];
    const result = Casts.applyEnergyBindEffect(getWorld(), event, events);
    this.applySimEvents(events);
    this.refreshDebugLabels();
    this.refreshLinkIndicators();
    return result;
  }

  private applyUnbindEnergyEffect(event: UnbindEnergyEffectEvent): UnbindEnergyEffectResult {
    const result = Casts.applyUnbindEnergyEffect(getWorld(), event);
    this.refreshLinkIndicators();
    return result;
  }

  // Bind·Charge takes `out` for the same reason Bind·Energy does, plus one
  // Energy's link doesn't have: Charge is read by the conduit/gate system, so
  // the settle Casts.applyChargeBindEffect runs can produce real
  // texture-set/object-removed events even though no Charge has crossed the
  // new wire yet.
  private applyChargeBindEffect(event: ChargeBindEffectEvent): ChargeBindEffectResult {
    const events: SimEvents = [];
    const result = Casts.applyChargeBindEffect(getWorld(), event, events);
    this.applySimEvents(events);
    this.refreshDebugLabels();
    this.refreshLinkIndicators();
    return result;
  }

  private applyUnbindChargeEffect(event: UnbindChargeEffectEvent): UnbindChargeEffectResult {
    const result = Casts.applyUnbindChargeEffect(getWorld(), event);
    this.refreshLinkIndicators();
    return result;
  }

  private applyEntropyBindEffect(event: EntropyBindEffectEvent): EntropyBindEffectResult {
    const events: SimEvents = [];
    const result = Casts.applyEntropyBindEffect(getWorld(), event, events);
    this.applySimEvents(events);
    this.refreshDebugLabels();
    this.refreshLinkIndicators();
    return result;
  }

  private applyUnbindEntropyEffect(event: UnbindEntropyEffectEvent): UnbindEntropyEffectResult {
    const result = Casts.applyUnbindEntropyEffect(getWorld(), event);
    this.refreshLinkIndicators();
    return result;
  }

  // A brief flicker across every tile a Project cast actually reaches -- Radial's
  // diamond (same Manhattan-distance rule applyProjectEffect's own targeting
  // uses, so the visual never claims a wider reach than the real effect) or
  // Linear's straight line ahead. Purely decorative: it doesn't touch room/
  // object state, so it's safe to fire before knowing whether anything was
  // actually there to catch it. Shared by Project·Energy (`fx-flame`, or
  // `fx-frost` under Reverse), Project·Charge (`fx-bolt`) and Project·
  // Momentum (`fx-shock`) -- only the texture differs, and the sim picks
  // which one (see the 'project-fx' event); everything below -- which
  // tiles, how long, how it eases -- is the renderer's own business and
  // stays here.
  // `origin` is where the projection comes out of, and defaults to the caster's
  // own tile -- which is where every player cast has always fired from, and why
  // this used to read `this.tileX/tileY` directly. A mob's spell (see
  // rune-lab/src/sim/mob.ts) is the first projection thrown by something that
  // is not the player, and it is otherwise the identical animation: same
  // textures, same tiles-along-an-offset walk, same fade.
  private animateProjectFx(
    textureId: string,
    radial: boolean,
    range: number,
    offset: { x: number; y: number },
    origin?: { x: number; y: number },
    oriented?: boolean,
  ): void {
    const fromX = origin?.x ?? this.tileX;
    const fromY = origin?.y ?? this.tileY;
    const tiles: { x: number; y: number }[] = [];
    if (radial) {
      for (let dx = -range; dx <= range; dx++) {
        for (let dy = -range; dy <= range; dy++) {
          if (dx === 0 && dy === 0) continue;
          if (Math.abs(dx) + Math.abs(dy) <= range) tiles.push({ x: fromX + dx, y: fromY + dy });
        }
      }
    } else {
      for (let i = 1; i <= range; i++) {
        tiles.push({ x: fromX + offset.x * i, y: fromY + offset.y * i });
      }
    }

    // An oriented projectile is drawn nose-first along its own flight; every
    // other look here is direction-agnostic and left alone (rotating a frost
    // bloom would be busywork nobody could see). The texture is drawn pointing
    // right, so this is the angle of `offset` measured from there.
    const angle = oriented ? Math.atan2(offset.y, offset.x) : 0;

    for (const tile of tiles) {
      const { x, y } = this.tileCenter(tile.x, tile.y);
      const fx = this.add.sprite(x, y, textureId).setDepth(55).setAlpha(0).setScale(0.9).setRotation(angle);
      this.tweens.add({
        targets: fx,
        alpha: { from: 0, to: 0.9 },
        duration: 150,
        yoyo: true,
        hold: 120,
        onComplete: () => fx.destroy(),
      });
    }
  }

  // Newton's law of cooling: every *passive* object's Energy in the current room
  // drifts toward that room's ambientEnergy (default 5, thermalStart) by ROOM_DECAY_RATE of the
  // remaining gap each tick. Scoped to objects with a meltsWhen rule specifically
  // -- that's what marks an object as "absorbing heat toward some threshold"
  // (the Ice-Choked Passage, warming toward melting) rather than "an active
  // reservoir with its own supply." Reuses checkMeltThreshold so ambient warmth
  // alone could in principle cross a threshold, same as a cast would.
  //
  // Active reservoirs (Brazier/Fireplace/Candle/Magnetic Block) get the mirror
  // treatment instead: every `maxProperties` entry drifts back *up* toward its
  // own max at the same rate, the object-level equivalent of the player's own
  // passive Energy regen -- a real source replenishes given time, rather than
  // staying spent forever once drawn from. The two roles never overlap on one
  // object (meltsWhen marks a sink, maxProperties marks a source), so a single
  // pass over the room's objects handles both without double-applying either.
  // Iterates a copy since checkMeltThreshold mutates room.objects when
  // something melts.
  // Deferred out of create() and instead polled for in update() (see
  // ambientTimersStarted) until the title/intro pre-game screens close --
  // registering these immediately at scene creation would anchor their
  // fixed-interval schedule to scene-boot wall-clock time rather than to when
  // a life actually starts, silently eating into decay-margin-sensitive
  // puzzles' timing by however long the pre-game screens happen to take.
  // The two ambient rules used to be Phaser timers registered here. They are now
  // one call to the sim's tick() from update() below, so elapsed time is an
  // argument rather than something only a running browser can produce -- see
  // rune-lab/src/sim/tick.ts. This method survives only as the "the world may
  // start running now" latch; the gate itself is unchanged (see update()).
  // Drives one frame of simulated time and applies whatever came back. The
  // debug readouts refresh on either kind of tick, not every frame, which is
  // what a naive port would have done. The HUD used to refresh only on a
  // regen tick (the only thing that changed between ticks) -- now widened to
  // decay ticks too, since the caster's own thermalState can drift purely
  // from room-ambient decay with no cast involved at all, and a HUD gated on
  // regenTicks alone would leave the new Thermal bar visibly stale.
  private advanceWorld(deltaMs: number): void {
    const events: SimEvents = [];
    recordElapsed(deltaMs);
    const summary = tick(getWorld(), deltaMs, events);
    // Mob steps and mob casts count here alongside the two ambient rules, and
    // have to: they run on their own cadences, which none of the other intervals
    // divides, so a frame that only moved a Grunt would otherwise drop its
    // `object-moved` event on the floor and leave the sprite standing where the
    // world says it no longer is -- and a frame in which a Drone Mage stood
    // still and threw a spell would drop the spell.
    // Conveyor steps count here for exactly the reason mob steps do: a belt runs
    // on its own cadence, so a frame in which nothing else fired but a boulder
    // was carried a tile would otherwise drop its `object-moved` event and leave
    // the sprite standing where the world says it no longer is.
    if (summary.regenTicks === 0 && summary.decayTicks === 0 && summary.conveyorSteps === 0 && summary.mobSteps === 0 && summary.mobCasts === 0) return;
    this.applySimEvents(events);
    if (summary.regenTicks > 0 || summary.decayTicks > 0) updateStatsHud();
    this.refreshDebugLabels();
  }

  private startAmbientTimers(): void {
    // Nothing to register any more. Kept as an explicit no-op rather than
    // deleting the call site so the pre-game gating below reads the same way.
  }

  // Runs the sim's declared system order (see sim/settle.ts) and draws whatever
  // it reported: which tiles carry flowing water, which reservoirs the flow now
  // powers, which conduit pillars read charged, and any gate whose conditions
  // are all satisfied. The scene calls this where it mutates the world outside a
  // cast -- on room entry, to re-sync the sprites to whatever progress the world
  // already has -- and never picks individual systems for itself.
  private settle(): void {
    const events: SimEvents = [];
    settleWorld(getWorld(), events);
    this.applySimEvents(events);
  }

  // Swaps a reformable object's sprite texture to whichever meltStages entry
  // matches how close its meltsWhen-relevant property currently is to the
  // threshold that makes it passable -- 0 at rest, the last stage once crossed.
  private refreshReformableVisual(obj: ObjectDef): void {
    const texture = Mech.reformableTexture(obj);
    if (texture) this.spriteFor(obj.id)?.setTexture(texture);
  }

  // Builds the sprite (+ debug label, if the object has properties) for one
  // object -- shared by enterRoom's initial pass over room.objects and
  // addObject below, so a runtime-created object (see applySeparateEffect)
  // gets exactly the same treatment as one placed at room-build time.
  private createObjectSprite(obj: ObjectDef): Phaser.GameObjects.Sprite {
    const { x: ox, y: oy } = this.tileCenter(obj.x, obj.y);
    // Solid objects sit at 0.95, just behind the player (depth 1) -- most
    // never share a tile with anything else anyway. Walkable ones (pressure
    // pads, dev-signposts) are floor fixtures, not obstacles, and do
    // regularly share a tile with something resting on them (a pushed Block
    // landing on a pad, the player standing on a signpost) -- given a
    // genuinely lower depth (0.4) instead of relying on display-list
    // insertion order, which used to leave whichever pad/signpost happened
    // to be *created* later drawing on top of whatever was already standing
    // there (2026-07-29 fix -- previously a pad's own texture rendered over
    // a Block resting on it).
    // A transformsInto object that's already toggled (see BarrierDef.transformed)
    // needs to start on its transformsInto texture, not its resting one --
    // this fires on every room entry (sprites are rebuilt from scratch), not
    // just the very first time the object is created.
    // A gate left standing open is the same case as a transformed barrier: the
    // state is persisted on the object, the sprite is not, so a sprite rebuilt
    // on room entry has to start from the state rather than from obj.texture.
    // The gate system restates the texture on every check as well, but that
    // lands a frame later -- long enough to see the gate flick shut and open
    // again on every re-entry.
    const openGate = obj.gate && Q.gateIsOpen(obj.gate) ? obj.gate.openTexture : undefined;
    const initialTexture = openGate ?? (obj.barrier?.transformed && obj.barrier.transformsInto ? obj.barrier.transformsInto : obj.texture);
    const sprite = this.add.sprite(ox, oy, initialTexture).setDepth(obj.walkable ? 0.4 : 0.95);
    if (obj.spriteScale) sprite.setScale(obj.spriteScale);
    sprite.setData('objectId', obj.id);
    // Only objects with `properties` ever have a live value worth showing --
    // no label at all for a bookshelf/crate/etc. rather than an always-empty one.
    if (obj.properties) {
      const label = this.add
        .text(ox, oy - TILE / 2 - 4, '', { fontSize: '11px', color: '#e8e8e8', fontFamily: 'monospace', backgroundColor: '#000000a0' })
        .setOrigin(0.5, 1)
        .setDepth(60)
        .setVisible(false);
      sprite.setData('debugLabel', label);
    }
    return sprite;
  }

  // Adds a brand-new object to the current room at runtime -- the creation
  // counterpart to removeObject below, needed by Separate·Mass splitting one
  // rock into two (or merging two into one). Refreshes whatever depends on
  // room state, the same way removeObject already does.
  private spriteFor(objectId: string): Phaser.GameObjects.Sprite | undefined {
    return this.objectSprites.find((s) => s.getData('objectId') === objectId);
  }

  // The renderer half of the sim boundary (see rune-lab/src/sim/events.ts).
  // Every Phaser side effect that used to sit inline inside a mechanism --
  // creating and destroying sprites, swapping textures, the transformsInto
  // cross-fade -- happens here instead, driven by what the sim reports it did.
  //
  // The four "refresh" calls at the bottom are deliberately collapsed to at
  // most one each per batch rather than fired per event. They are idempotent
  // recomputes from current world state (which object is faced, where the
  // marked-source reticle belongs, what the debug labels read, which objects
  // have a bind indicator), so running them once after the whole batch is
  // equivalent to the old inline calls and avoids repeating them for every
  // object a decay tick happens to touch.
  // The renderer boundary. Every mutator hands its SimEvents here, so a throw
  // while applying one used to unwind back through the cast handler and out
  // into whichever caller was unlucky -- with the world already mutated,
  // because the sim runs to completion before the renderer sees anything.
  //
  // Guarding the whole batch rather than each event is a deliberate, and
  // lesser, choice: per-event isolation would let the remaining events and the
  // end-of-batch staleness refreshes (faced target, mark reticle, debug
  // labels, bind indicators) still run, where this leaves them stale until the
  // next batch. It is not done that way yet because the switch is ~100 lines
  // and re-indenting it wholesale to add a try is a larger, riskier edit than
  // the bug it would improve on -- the world state stays correct either way,
  // and it is only the picture of it that lags a frame. Worth revisiting the
  // next time this method is opened for another reason.
  private applySimEvents(events: SimEvents): void {
    try {
      this.applySimEventsUnguarded(events);
    } catch (err) {
      reportError(err, 'sim-events', { eventKinds: events.map((e) => e.kind).slice(0, 12) });
    }
  }

  private applySimEventsUnguarded(events: SimEvents): void {
    let facedStale = false;
    let markStale = false;
    let labelsStale = false;
    // One flag rather than the three there used to be, which is also how the
    // fourth stopped being forgotten: `object-removed` marked Momentum, Energy
    // and Charge stale and never Entropy, so a removed object's Entropy label
    // outlived it. Four near-identical flags for four near-identical passes is
    // exactly the shape that goes wrong three times out of four.
    let linksStale = false;
    let died = false;

    for (const event of events) {
      switch (event.kind) {
        case 'object-added':
          this.objectSprites.push(this.createObjectSprite(event.object));
          facedStale = true;
          labelsStale = true;
          // A new object can be the far end of a link something else already
          // names -- a split piece inheriting one, say -- which is a label that
          // could not be drawn until now.
          linksStale = true;
          break;
        case 'object-removed': {
          const sprite = this.spriteFor(event.objectId);
          if (sprite) {
            this.objectSprites = this.objectSprites.filter((s) => s !== sprite);
            const label = sprite.getData('debugLabel') as Phaser.GameObjects.Text | undefined;
            label?.destroy();
            this.tweens.add({ targets: sprite, alpha: 0, duration: 500, onComplete: () => sprite.destroy() });
          }
          // The removed object may have been whatever the player was facing (a
          // melted barrier, a barrier.unlocks follow-through), and it may also
          // have been the marked source -- objectLink.ts doesn't know it's gone
          // until the next cast tries to use it, so drop the reticle now rather
          // than leaving it pointing at empty air. It may also have been
          // linked, so its indicator(s) need the same cleanup -- and so do its
          // *partner's*, now that a link is labelled at both ends.
          facedStale = true;
          markStale = true;
          linksStale = true;
          break;
        }
        case 'object-moved': {
          const sprite = this.spriteFor(event.objectId);
          if (sprite) {
            // Glides rather than snapping, over the same MOVE_DURATION a walk
            // takes -- a Sokoban shove and a Momentum push should read as the
            // same physical motion, since they are.
            const { x, y } = this.tileCenter(event.x, event.y);
            this.tweens.add({ targets: sprite, x, y, duration: MOVE_DURATION });
          }
          facedStale = true;
          // The moved object may be the marked source, in which case the
          // reticle should follow it rather than staying at its old tile.
          markStale = true;
          labelsStale = true;
          break;
        }
        case 'player-moved': {
          this.isMoving = true;
          this.moveStartedAt = this.time.now;
          const { x, y } = this.tileCenter(event.x, event.y);
          this.tweens.add({
            targets: this.player,
            x,
            y,
            duration: MOVE_DURATION,
            onComplete: () => {
              this.isMoving = false;
            },
          });
          // Interact/Mark/Cast's target is whatever's directly ahead, so
          // standing somewhere new changes it. (update() also recomputes after
          // every attempted move, which covers turning in place while blocked
          // -- no move, and so no event, but a new facing all the same.)
          facedStale = true;
          break;
        }
        case 'stats-changed':
          updateStatsHud();
          break;
        case 'player-hurt':
          // Health itself already arrived via 'stats-changed' -- this is
          // purely the flourish, the same "sim reports what happened, the
          // renderer owns how it looks" split 'project-fx' already has.
          this.cameras.main.flash(150, 200, 30, 30);
          break;
        case 'project-fx':
          this.animateProjectFx(event.texture, event.radial, event.range, event.offset, event.origin, event.oriented);
          break;
        case 'texture-set':
          this.applyTextureSet(event.objectId, event.texture, event.transition);
          break;
        case 'interact-changed':
          facedStale = true;
          break;
        case 'puzzle-solved':
          trackEvent('puzzle_solved', { objectId: event.objectId, roomId: this.currentRoomId });
          break;
        case 'player-died':
          // Deferred to after the loop, same as facedStale/markStale/etc --
          // the flash and HUD update from stats-changed/player-hurt earlier
          // in this same batch should already be visible before the death
          // panel covers the screen, not raced against them.
          died = true;
          break;
      }
    }

    if (facedStale) this.pushFacedTarget();
    if (markStale) this.refreshMarkIndicator();
    if (labelsStale) this.refreshDebugLabels();
    if (linksStale) this.refreshLinkIndicators();
    // Unconditionally rather than behind a staleness flag, and it is the one
    // refresh here that has to be: what is lit depends on where the caster is
    // standing, what every source on every circuit is holding, and where every
    // solid object in the room currently sits -- so the set of events that can
    // change it is "most of them", and a flag listing those would be a list to
    // forget to extend. Cheap enough: it returns immediately in a room that is
    // not dark, which is every room the world ships.
    this.refreshDarkness();
    if (died) showDeathScreen();
  }

  private applyTextureSet(objectId: string, texture: string, transition: TextureTransition): void {
    const sprite = this.spriteFor(objectId);
    if (!sprite) return;
    if (transition === 'cross-fade') {
      // Fade out, swap underneath, fade back in -- an object visibly becoming
      // something else while staying exactly where it is.
      this.tweens.add({
        targets: sprite,
        alpha: 0,
        duration: 400,
        onComplete: () => {
          sprite.setTexture(texture);
          this.tweens.add({ targets: sprite, alpha: 1, duration: 400 });
        },
      });
      return;
    }
    if (sprite.texture.key === texture) return;
    sprite.setTexture(texture);
    // Water arriving in a channel tile that was dry a moment ago.
    if (transition === 'fade-in') {
      sprite.setAlpha(0.3);
      this.tweens.add({ targets: sprite, alpha: 1, duration: 400 });
    }
  }

  private generateTextures(): void {
    const g = this.add.graphics();

    // Room 1 floor
    g.fillStyle(0x2b3a2b, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x24312a, 1);
    g.strokeRect(0, 0, TILE, TILE);

    // Wall (shared between rooms)
    g.fillStyle(0x4a3b2e, 1);
    g.fillRect(TILE, 0, TILE, TILE);
    g.lineStyle(2, 0x2e2318, 1);
    g.strokeRect(TILE + 1, 1, TILE - 2, TILE - 2);

    // Door (shared between rooms) -- warm amber-brown, distinct from wall, with a
    // small handle dot so it reads as a door rather than another wall tile.
    g.fillStyle(0x8a5a2e, 1);
    g.fillRect(TILE * 2, 0, TILE, TILE);
    g.lineStyle(2, 0x3a2413, 1);
    g.strokeRect(TILE * 2 + 4, 2, TILE - 8, TILE - 4);
    g.fillStyle(0x3a2413, 1);
    g.fillCircle(TILE * 2 + TILE - 8, TILE / 2, 2);

    // Room 2 floor -- a distinct cool tint so a new room reads as a new room, not a
    // teleport within the same one.
    g.fillStyle(0x2b333a, 1);
    g.fillRect(TILE * 3, 0, TILE, TILE);
    g.lineStyle(1, 0x242c31, 1);
    g.strokeRect(TILE * 3, 0, TILE, TILE);

    // Room 3 floor -- warm stone gray, a workshop/study feel.
    g.fillStyle(0x3a3530, 1);
    g.fillRect(TILE * 4, 0, TILE, TILE);
    g.lineStyle(1, 0x2e2a26, 1);
    g.strokeRect(TILE * 4, 0, TILE, TILE);

    // Room 4 floor -- pale icy blue, a cold-archive feel.
    g.fillStyle(0x2e3d47, 1);
    g.fillRect(TILE * 5, 0, TILE, TILE);
    g.lineStyle(1, 0x24303a, 1);
    g.strokeRect(TILE * 5, 0, TILE, TILE);

    // Room 0 floor -- dim stone-grey, a small waking chamber rather than any of the
    // tutorial rooms' established color identities.
    g.fillStyle(0x2b2a2e, 1);
    g.fillRect(TILE * 6, 0, TILE, TILE);
    g.lineStyle(1, 0x232226, 1);
    g.strokeRect(TILE * 6, 0, TILE, TILE);

    // Room 5 floor -- worn ochre stone, a mechanics workshop/hall feel (levers,
    // sliding blocks) distinct from every existing room's tint.
    g.fillStyle(0x4a3f2e, 1);
    g.fillRect(TILE * 7, 0, TILE, TILE);
    g.lineStyle(1, 0x3a3124, 1);
    g.strokeRect(TILE * 7, 0, TILE, TILE);

    // Room 6 floor -- ashen violet-gray, a faded archive/ruin feel distinct
    // from every existing room's tint.
    g.fillStyle(0x3a3040, 1);
    g.fillRect(TILE * 8, 0, TILE, TILE);
    g.lineStyle(1, 0x2e2636, 1);
    g.strokeRect(TILE * 8, 0, TILE, TILE);

    // Room 7 floor -- dusty slate gray, a quarry/masonry-yard feel distinct
    // from every existing room's tint.
    g.fillStyle(0x45433f, 1);
    g.fillRect(TILE * 9, 0, TILE, TILE);
    g.lineStyle(1, 0x37352f, 1);
    g.strokeRect(TILE * 9, 0, TILE, TILE);

    // Room 8 floor -- cut, leveled flagstone (an engineered access corridor
    // rather than open quarry rock), distinct from room7's rougher tint.
    g.fillStyle(0x3f463f, 1);
    g.fillRect(TILE * 10, 0, TILE, TILE);
    g.lineStyle(1, 0x333a33, 1);
    g.strokeRect(TILE * 10, 0, TILE, TILE);

    // Room 9 floor -- weathered stone with a faint blue-gray cast (charge and
    // old masonry), distinct from room8's warmer flagstone.
    g.fillStyle(0x3a3f47, 1);
    g.fillRect(TILE * 11, 0, TILE, TILE);
    g.lineStyle(1, 0x2e333a, 1);
    g.strokeRect(TILE * 11, 0, TILE, TILE);

    // Room 10 floor -- deep violet-black, distinct from every prior room's
    // tint on purpose: the last space in the demo, not another waypoint.
    g.fillStyle(0x241f30, 1);
    g.fillRect(TILE * 12, 0, TILE, TILE);
    g.lineStyle(1, 0x1a1725, 1);
    g.strokeRect(TILE * 12, 0, TILE, TILE);

    // Cliff, north-facing (tile id 13, see CLIFF_NORTH) -- a rock face read
    // from below, its lightest edge along the top where the raised area it
    // borders begins. Impassable like a wall until a climbable Vines object
    // occupies this exact tile (see isWalkable); the two orientations exist
    // so a rectangular raised area's north and east edges both read as the
    // same kind of rock face from whichever side the player approaches.
    g.fillStyle(0x3a3228, 1);
    g.fillRect(TILE * 13, 0, TILE, TILE);
    g.fillStyle(0x4a4030, 1);
    g.fillTriangle(TILE * 13, 0, TILE * 13 + TILE, 0, TILE * 13 + TILE / 2, TILE * 0.4);
    g.lineStyle(1, 0x241f18, 0.8);
    g.lineBetween(TILE * 13 + 4, TILE * 0.55, TILE * 13 + TILE - 6, TILE * 0.5);
    g.lineBetween(TILE * 13 + 6, TILE * 0.75, TILE * 13 + TILE - 8, TILE * 0.8);
    g.lineStyle(2, 0x241f18, 1);
    g.strokeRect(TILE * 13 + 1, 1, TILE - 2, TILE - 2);

    // Cliff, east-facing (tile id 14, see CLIFF_EAST) -- the same rock face,
    // its lightest edge rotated to the right side instead of the top, so an
    // east-bordered plateau reads distinctly from a north-bordered one.
    g.fillStyle(0x3a3228, 1);
    g.fillRect(TILE * 14, 0, TILE, TILE);
    g.fillStyle(0x4a4030, 1);
    g.fillTriangle(TILE * 14 + TILE, 0, TILE * 14 + TILE, TILE, TILE * 14 + TILE * 0.6, TILE / 2);
    g.lineStyle(1, 0x241f18, 0.8);
    g.lineBetween(TILE * 14 + TILE * 0.45, 4, TILE * 14 + TILE * 0.5, TILE - 6);
    g.lineBetween(TILE * 14 + TILE * 0.25, 6, TILE * 14 + TILE * 0.2, TILE - 8);
    g.lineStyle(2, 0x241f18, 1);
    g.strokeRect(TILE * 14 + 1, 1, TILE - 2, TILE - 2);

    // Void (tile id 15, see VOID) -- deliberately nothing at all. Not drawn,
    // not even a background fill: the cell is left transparent so the scene's
    // own backdrop shows through, which is exactly what "the room does not
    // extend here" should look like. A wall is a thing the room has; a void is
    // the room stopping, and painting anything here would make it a thing
    // again. It is still one cell wide in the strip, because a tilemap indexes
    // by position and index 15 has to exist to be addressable.

    g.generateTexture(TILESET_KEY, TILE * 16, TILE);

    // Placeholder interactive object -- a crate, distinct from any tile color so it
    // reads as "a thing", not part of the floor.
    g.clear();
    g.fillStyle(0x9a7a4a, 1);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x5c4726, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.lineBetween(2, TILE / 2, TILE - 2, TILE / 2);
    g.lineBetween(TILE / 2, 2, TILE / 2, TILE - 2);
    g.generateTexture('object-crate', TILE, TILE);

    // Iron Lever (room3) -- a floor-mounted mechanism, distinct from a
    // bookshelf/crate: a stone base plate with an angled iron arm and a gold
    // grip, reading as "pull this" rather than another prop to read or open.
    // Unlocks the room's own east door once pulled (see
    // ObjectInteract.unlocksDoorId).
    g.clear();
    g.fillStyle(0x4a4a52, 1);
    g.fillRect(6, TILE - 10, TILE - 12, 6);
    g.lineStyle(2, 0x2a2a30, 1);
    g.strokeRect(6, TILE - 10, TILE - 12, 6);
    g.lineStyle(4, 0x6b6b76, 1);
    g.lineBetween(TILE / 2, TILE - 8, TILE - 9, 8);
    g.fillStyle(0xc9a24b, 1);
    g.fillCircle(TILE - 9, 8, 4);
    g.generateTexture('object-lever', TILE, TILE);

    // Iron-bound chest (room6) -- a domed lid, two horizontal iron bands, and
    // a solid latch plate with deliberately *no* keyhole drawn into it (see
    // its interact text: there's no key for this, Entropy is the only way
    // in). Distinct enough from the crate's cross pattern to read as its own
    // kind of prop rather than another give-item box.
    g.clear();
    g.fillStyle(0x6b4a2a, 1);
    g.fillRect(3, 12, TILE - 6, TILE - 14);
    g.fillEllipse(TILE / 2, 12, TILE / 2 - 3, 6);
    g.lineStyle(2, 0x3a2a18, 1);
    g.strokeRect(3, 12, TILE - 6, TILE - 14);
    g.strokeEllipse(TILE / 2, 12, TILE / 2 - 3, 6);
    g.fillStyle(0x8a8578, 1);
    g.fillRect(3, 15, TILE - 6, 3);
    g.fillRect(3, TILE - 9, TILE - 6, 3);
    g.fillStyle(0x5c584e, 1);
    g.fillRect(TILE / 2 - 4, TILE / 2, 8, 7);
    g.generateTexture('object-chest', TILE, TILE);

    // Bookshelf -- a wooden frame with a row of colored book spines, distinct from
    // the crate's cross pattern so the two read as different kinds of thing at a
    // glance.
    g.clear();
    g.fillStyle(0x5c4020, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(2, 0x3a2413, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    const spineColors = [0xa8433a, 0x3a6b8a, 0x4a8a5a, 0xb08a3a, 0x7a4a8a];
    const spineWidth = (TILE - 6) / spineColors.length;
    spineColors.forEach((color, i) => {
      g.fillStyle(color, 1);
      g.fillRect(3 + i * spineWidth, 5, spineWidth - 1, TILE - 10);
    });
    g.generateTexture('object-bookshelf', TILE, TILE);

    // Fireplace -- a stone alcove with a two-tone flame, an Energy prop.
    g.clear();
    g.fillStyle(0x4a4038, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(2, 0x2e2822, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0xd9473f, 1);
    g.fillTriangle(TILE / 2, 6, 8, TILE - 6, TILE - 8, TILE - 6);
    g.fillStyle(0xf0a83f, 1);
    g.fillTriangle(TILE / 2, 13, 13, TILE - 8, TILE - 13, TILE - 8);
    g.generateTexture('object-fireplace', TILE, TILE);

    // Cold hearth -- the same stone surround with the flames replaced by grey
    // ash and a couple of spent logs, so a Fireplace or Brazier that has been
    // drained out reads as out rather than merely as dimmer. The surround is
    // identical on purpose: it is the same hearth, in the other state.
    g.clear();
    g.fillStyle(0x4a4038, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(2, 0x2e2822, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0x6b6560, 1);
    g.fillTriangle(TILE / 2, 12, 9, TILE - 6, TILE - 9, TILE - 6);
    g.lineStyle(2, 0x3a3430, 1);
    g.lineBetween(9, TILE - 8, TILE - 9, TILE - 11);
    g.lineBetween(10, TILE - 11, TILE - 10, TILE - 8);
    g.generateTexture('object-fireplace-unlit', TILE, TILE);

    // Candle -- a small cream stick with a single flame, a cheaper Energy prop.
    g.clear();
    g.fillStyle(0xe8dcc0, 1);
    g.fillRect(TILE / 2 - 4, 14, 8, TILE - 16);
    g.lineStyle(1, 0xb0a480, 1);
    g.strokeRect(TILE / 2 - 4, 14, 8, TILE - 16);
    g.fillStyle(0xf0a83f, 1);
    g.fillTriangle(TILE / 2, 4, TILE / 2 - 4, 13, TILE / 2 + 4, 13);
    g.generateTexture('object-candle', TILE, TILE);

    // Unlit candle -- the same stick, no flame, just a bare dark wick -- room2's
    // Guttering Candle starts as this (properties.energy: 0) and collapsesInto
    // the ordinary lit 'object-candle' above once Project·Energy pushes it past
    // its threshold, rather than vanishing outright like every other barrier.
    g.clear();
    g.fillStyle(0xe8dcc0, 1);
    g.fillRect(TILE / 2 - 4, 14, 8, TILE - 16);
    g.lineStyle(1, 0xb0a480, 1);
    g.strokeRect(TILE / 2 - 4, 14, 8, TILE - 16);
    g.lineStyle(1, 0x3a3226, 1);
    g.lineBetween(TILE / 2, 14, TILE / 2, 9);
    g.generateTexture('object-candle-unlit', TILE, TILE);

    // Melted candle -- a low, slumped puddle of wax with the wick collapsed
    // sideways, nothing standing. Room2's corner Candles collapsesInto this
    // once pushed past their own second, higher threshold -- overcharged, not
    // just lit.
    g.clear();
    g.fillStyle(0xe8dcc0, 1);
    g.fillEllipse(TILE / 2, TILE - 10, 9, 5);
    g.fillTriangle(TILE / 2 - 8, TILE - 8, TILE / 2 + 8, TILE - 8, TILE / 2, TILE - 2);
    g.lineStyle(1, 0xb0a480, 1);
    g.strokeEllipse(TILE / 2, TILE - 10, 9, 5);
    g.lineStyle(2, 0x3a3226, 1);
    g.lineBetween(TILE / 2 - 3, TILE - 11, TILE / 2 + 2, TILE - 8);
    g.generateTexture('object-candle-melted', TILE, TILE);

    // Crystal orb -- a violet diamond with a lighter facet, an Entropy/Charge prop
    // (ambiguous on purpose -- which concept it "is" is for the player to test).
    g.clear();
    g.fillStyle(0x7a5ec9, 1);
    g.fillTriangle(TILE / 2, 4, 6, TILE / 2, TILE - 6, TILE / 2);
    g.fillTriangle(TILE / 2, TILE - 4, 6, TILE / 2, TILE - 6, TILE / 2);
    g.lineStyle(2, 0x4a3a8a, 1);
    g.strokeTriangle(TILE / 2, 4, 6, TILE / 2, TILE - 6, TILE / 2);
    g.strokeTriangle(TILE / 2, TILE - 4, 6, TILE / 2, TILE - 6, TILE / 2);
    g.fillStyle(0xb8a8f0, 0.7);
    g.fillTriangle(TILE / 2, 9, TILE / 2 - 5, TILE / 2, TILE / 2 + 2, TILE / 2 - 5);
    g.generateTexture('object-crystal', TILE, TILE);

    // Charge Crystal Pillar (room3) -- the reward tucked behind the Sealed
    // Reliquary. A pillar of pale blue stone (same silhouette as
    // object-pillar, just recolored) with a bright crystal set into a niche
    // cut into its face -- "contains," not "capped by" -- plus a couple of
    // short static-spark ticks near it so it reads as genuinely electric at
    // rest, not just during a cast.
    g.clear();
    g.fillStyle(0x4a5a72, 1);
    g.fillRect(8, 2, TILE - 16, TILE - 4);
    g.lineStyle(1, 0x33404f, 1);
    g.strokeRect(8, 2, TILE - 16, TILE - 4);
    g.fillStyle(0x5f7390, 1);
    g.fillRect(6, 2, TILE - 12, 3);
    g.fillRect(6, TILE - 6, TILE - 12, 3);
    g.fillStyle(0x7ecbff, 0.9);
    g.fillTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.lineStyle(1, 0xd8f0ff, 0.9);
    g.strokeTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.lineStyle(1, 0xbfe0ff, 0.8);
    g.lineBetween(TILE / 2 - 7, TILE / 2 - 2, TILE / 2 - 11, TILE / 2 - 6);
    g.lineBetween(TILE / 2 + 7, TILE / 2, TILE / 2 + 11, TILE / 2 - 4);
    g.generateTexture('object-pillar-crystal-charge', TILE, TILE);

    // Energy Crystal Pillar (room4) -- the reward tucked past the Ice-Choked
    // Passage, same shape as room3's Charge Crystal Pillar (a crystal set
    // into a niche in the pillar's face, not capping it), just recolored
    // warm red stone with an amber crystal, and two small flame-lick
    // accents near it instead of static-spark ticks, so it reads as banked
    // heat rather than electricity.
    g.clear();
    g.fillStyle(0x7a3a34, 1);
    g.fillRect(8, 2, TILE - 16, TILE - 4);
    g.lineStyle(1, 0x4f2622, 1);
    g.strokeRect(8, 2, TILE - 16, TILE - 4);
    g.fillStyle(0x8f4c40, 1);
    g.fillRect(6, 2, TILE - 12, 3);
    g.fillRect(6, TILE - 6, TILE - 12, 3);
    g.fillStyle(0xffb060, 0.9);
    g.fillTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.lineStyle(1, 0xffe0b0, 0.9);
    g.strokeTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.fillStyle(0xff8c3a, 0.8);
    g.fillTriangle(TILE / 2 - 9, TILE / 2 - 3, TILE / 2 - 12, TILE / 2 - 9, TILE / 2 - 6, TILE / 2 - 8);
    g.fillTriangle(TILE / 2 + 9, TILE / 2 - 1, TILE / 2 + 6, TILE / 2 - 7, TILE / 2 + 12, TILE / 2 - 6);
    g.generateTexture('object-pillar-crystal-energy', TILE, TILE);

    // Mass Crystal Pillar (room8) -- the reward behind the second gate,
    // same shape as room3/room4's crystal pillars, recolored ochre/yellow
    // stone with a heavy-looking amber crystal, plus two short hairline
    // crack marks radiating from it (straining under its own weight)
    // instead of static-spark ticks or flame licks.
    g.clear();
    g.fillStyle(0x7a6a34, 1);
    g.fillRect(8, 2, TILE - 16, TILE - 4);
    g.lineStyle(1, 0x4f4222, 1);
    g.strokeRect(8, 2, TILE - 16, TILE - 4);
    g.fillStyle(0x8f7c40, 1);
    g.fillRect(6, 2, TILE - 12, 3);
    g.fillRect(6, TILE - 6, TILE - 12, 3);
    g.fillStyle(0xd2a83c, 0.9);
    g.fillTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.lineStyle(1, 0xf0d080, 0.9);
    g.strokeTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.lineStyle(1, 0x4f4222, 0.8);
    g.lineBetween(TILE / 2 - 2, TILE / 2 + 4, TILE / 2 - 6, TILE / 2 + 9);
    g.lineBetween(TILE / 2 + 3, TILE / 2 + 3, TILE / 2 + 7, TILE / 2 + 8);
    g.generateTexture('object-pillar-crystal-mass', TILE, TILE);

    // Entropy Crystal Pillar (room9) -- the reward at the far end of the
    // bridge, same shape as the other 3 crystal pillars, moss-dark green
    // stone with a crystal visibly fractured through (three short crack
    // lines instead of one or two ticks), reading as something still
    // quietly falling apart rather than electric, warm, or heavy.
    g.clear();
    g.fillStyle(0x3a4a34, 1);
    g.fillRect(8, 2, TILE - 16, TILE - 4);
    g.lineStyle(1, 0x222f1e, 1);
    g.strokeRect(8, 2, TILE - 16, TILE - 4);
    g.fillStyle(0x4a5f42, 1);
    g.fillRect(6, 2, TILE - 12, 3);
    g.fillRect(6, TILE - 6, TILE - 12, 3);
    g.fillStyle(0x6ea85c, 0.9);
    g.fillTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.lineStyle(1, 0xa8d89c, 0.9);
    g.strokeTriangle(TILE / 2, TILE / 2 - 7, TILE / 2 - 5, TILE / 2 + 5, TILE / 2 + 5, TILE / 2 + 5);
    g.lineStyle(1, 0x222f1e, 0.85);
    g.lineBetween(TILE / 2, TILE / 2 - 4, TILE / 2 - 3, TILE / 2 + 2);
    g.lineBetween(TILE / 2 - 3, TILE / 2 + 2, TILE / 2 + 1, TILE / 2 + 4);
    g.lineBetween(TILE / 2 + 1, TILE / 2 - 2, TILE / 2 + 3, TILE / 2 + 3);
    g.generateTexture('object-pillar-crystal-entropy', TILE, TILE);

    // Ice block -- pale translucent-looking cyan with crack lines, an Energy
    // (cold) / Entropy (crystalline order) prop.
    g.clear();
    g.fillStyle(0xafd8e6, 0.9);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x7ab0c2, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(1, 0xe8f8ff, 0.8);
    g.lineBetween(6, 8, TILE - 10, TILE - 14);
    g.lineBetween(TILE - 8, 6, 10, TILE - 10);
    g.generateTexture('object-iceblock', TILE, TILE);

    // Melt-stage textures for reformable ice (see BarrierDef.meltStages/
    // reformable) -- swapped live as Energy climbs toward (or decays back away
    // from) the threshold that makes it passable, so progress is visible
    // instead of an all-or-nothing block.
    g.clear();
    g.fillStyle(0xafd8e6, 0.75);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x7ab0c2, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(1, 0xe8f8ff, 0.8);
    g.lineBetween(6, 8, TILE - 10, TILE - 14);
    g.lineBetween(TILE - 8, 6, 10, TILE - 10);
    g.lineBetween(8, TILE - 8, TILE - 12, 10);
    g.lineBetween(TILE / 2, 4, TILE / 2 - 4, TILE - 6);
    g.generateTexture('object-iceblock-cracked', TILE, TILE);

    g.clear();
    g.fillStyle(0x3a6a78, 0.35);
    g.fillRect(2, TILE - 12, TILE - 4, 10);
    g.fillStyle(0xafd8e6, 0.45);
    g.fillRect(2, 2, TILE - 4, TILE - 16);
    g.lineStyle(1, 0x7ab0c2, 0.8);
    g.strokeRect(2, 2, TILE - 4, TILE - 16);
    g.generateTexture('object-iceblock-melting', TILE, TILE);

    // Barely anything left standing -- reads unmistakably as "not solid ice
    // anymore", matching isWalkable's live pass-through check at this exact
    // threshold. Still occupies the tile visually; it reforms if left alone.
    g.clear();
    g.fillStyle(0x3a6a78, 0.4);
    g.fillEllipse(TILE / 2, TILE - 8, TILE - 8, 8);
    g.lineStyle(1, 0x7ab0c2, 0.6);
    g.strokeEllipse(TILE / 2, TILE - 8, TILE - 8, 8);
    g.generateTexture('object-iceblock-melted', TILE, TILE);

    // Magnetic block -- classic red/blue pole halves, a Charge prop.
    g.clear();
    g.fillStyle(0x3a3a3a, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0xd9473f, 1);
    g.fillRect(3, 3, (TILE - 6) / 2, TILE - 6);
    g.fillStyle(0x4b8ec9, 1);
    g.fillRect(3 + (TILE - 6) / 2, 3, (TILE - 6) / 2, TILE - 6);
    g.lineStyle(2, 0x1a1a1a, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.generateTexture('object-magnet', TILE, TILE);

    // Charged Ward -- a crackling jagged bolt of light, distinct from the magnet's
    // static red/blue halves. Blocks the way until enough Charge discharges it.
    g.clear();
    g.lineStyle(3, 0xd94bc9, 1);
    g.lineBetween(TILE / 2 - 8, 3, TILE / 2 + 4, TILE / 2 - 2);
    g.lineBetween(TILE / 2 + 4, TILE / 2 - 2, TILE / 2 - 6, TILE / 2 + 4);
    g.lineBetween(TILE / 2 - 6, TILE / 2 + 4, TILE / 2 + 8, TILE - 3);
    g.lineStyle(1, 0xf5c6ef, 0.9);
    g.lineBetween(TILE / 2 - 8, 3, TILE / 2 + 4, TILE / 2 - 2);
    g.lineBetween(TILE / 2 - 6, TILE / 2 + 4, TILE / 2 + 8, TILE - 3);
    g.generateTexture('object-ward', TILE, TILE);

    // The same ward, spent: the bolt reduced to a faint trace of itself. This
    // is what a Ward wired as a re-closable gate (`gate.openTexture`) shows
    // while it is open -- a ward that only ever opens once is removed instead
    // and never needs one, which is why this did not exist until gates grew
    // close/toggle triggers.
    g.clear();
    g.lineStyle(1, 0xd94bc9, 0.35);
    g.lineBetween(TILE / 2 - 8, 3, TILE / 2 + 4, TILE / 2 - 2);
    g.lineBetween(TILE / 2 + 4, TILE / 2 - 2, TILE / 2 - 6, TILE / 2 + 4);
    g.lineBetween(TILE / 2 - 6, TILE / 2 + 4, TILE / 2 + 8, TILE - 3);
    g.generateTexture('object-ward-open', TILE, TILE);

    // Dual-property ward (Sealed Reliquary) -- the Charged Ward's jagged bolt
    // crossed with a second one in Energy's orange, so an AND-gate barrier
    // reads as visually different from an ordinary single-property one at a
    // glance, not just in its interact text.
    g.clear();
    g.lineStyle(3, 0xd94bc9, 1);
    g.lineBetween(4, 4, TILE / 2, TILE / 2);
    g.lineBetween(TILE / 2, TILE / 2, 4, TILE - 4);
    g.lineStyle(3, 0xd94b1f, 1);
    g.lineBetween(TILE - 4, 4, TILE / 2, TILE / 2);
    g.lineBetween(TILE / 2, TILE / 2, TILE - 4, TILE - 4);
    g.lineStyle(1, 0xf5c6ef, 0.9);
    g.lineBetween(4, 4, TILE / 2, TILE / 2);
    g.lineStyle(1, 0xffcf9f, 0.9);
    g.lineBetween(TILE / 2, TILE / 2, TILE - 4, TILE - 4);
    g.generateTexture('object-ward-dual', TILE, TILE);

    // Same dual ward, one half at a time cleared -- the visible "one of two
    // requirements met" cue (see BarrierDef.partialMeltTextures): only the
    // still-outstanding concept's own bolt remains once the other's threshold
    // is individually crossed, reading as literally half an X instead of a
    // whole one.
    g.clear();
    g.lineStyle(3, 0xd94b1f, 1);
    g.lineBetween(TILE - 4, 4, TILE / 2, TILE / 2);
    g.lineBetween(TILE / 2, TILE / 2, TILE - 4, TILE - 4);
    g.lineStyle(1, 0xffcf9f, 0.9);
    g.lineBetween(TILE / 2, TILE / 2, TILE - 4, TILE - 4);
    g.generateTexture('object-ward-dual-charge-cleared', TILE, TILE);

    g.clear();
    g.lineStyle(3, 0xd94bc9, 1);
    g.lineBetween(4, 4, TILE / 2, TILE / 2);
    g.lineBetween(TILE / 2, TILE / 2, 4, TILE - 4);
    g.lineStyle(1, 0xf5c6ef, 0.9);
    g.lineBetween(4, 4, TILE / 2, TILE / 2);
    g.generateTexture('object-ward-dual-energy-cleared', TILE, TILE);

    // Pendulum -- a bob hanging by a string from a small wall anchor, a Momentum
    // prop.
    g.clear();
    g.fillStyle(0x5c4a3a, 1);
    g.fillRect(TILE / 2 - 6, 2, 12, 4);
    g.lineStyle(2, 0xc8ccd2, 1);
    g.lineBetween(TILE / 2, 6, TILE / 2, TILE - 10);
    g.fillStyle(0x9a9a9a, 1);
    g.fillCircle(TILE / 2, TILE - 8, 6);
    g.lineStyle(1, 0x5a5a5a, 1);
    g.strokeCircle(TILE / 2, TILE - 8, 6);
    g.generateTexture('object-pendulum', TILE, TILE);

    // Hourglass -- a classic silhouette with amber sand, a Time prop.
    g.clear();
    g.fillStyle(0x5c4020, 1);
    g.fillRect(6, 3, TILE - 12, 3);
    g.fillRect(6, TILE - 6, TILE - 12, 3);
    g.fillStyle(0xd8c8a0, 0.9);
    g.fillTriangle(8, 6, TILE - 8, 6, TILE / 2, TILE / 2);
    g.fillStyle(0xc9a24b, 1);
    g.fillTriangle(TILE / 2, TILE / 2, 8, TILE - 6, TILE - 8, TILE - 6);
    g.lineStyle(1, 0x8a6a2e, 1);
    g.strokeTriangle(8, 6, TILE - 8, 6, TILE / 2, TILE / 2);
    g.strokeTriangle(TILE / 2, TILE / 2, 8, TILE - 6, TILE - 8, TILE - 6);
    g.generateTexture('object-hourglass', TILE, TILE);

    // Bed -- a dark wood frame, tan mattress, and a pale pillow, in room0.
    g.clear();
    g.fillStyle(0x4a3020, 1);
    g.fillRect(2, 4, TILE - 4, TILE - 8);
    g.fillStyle(0x8a6a4a, 1);
    g.fillRect(4, 8, TILE - 8, TILE - 14);
    g.fillStyle(0xe8e0d0, 1);
    g.fillRect(5, 9, 10, 8);
    g.lineStyle(1, 0x2e2015, 1);
    g.strokeRect(2, 4, TILE - 4, TILE - 8);
    g.generateTexture('object-bed', TILE, TILE);

    // Faded ruin circle -- a pale, low-opacity etched ring with radiating tick
    // marks, meant to read as old and inert rather than a live/active rune.
    g.clear();
    g.lineStyle(2, 0x8a8a90, 0.5);
    g.strokeCircle(TILE / 2, TILE / 2, TILE / 2 - 5);
    g.lineStyle(1, 0x8a8a90, 0.4);
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      const r1 = TILE / 2 - 5;
      const r2 = TILE / 2 - 9;
      const cx = TILE / 2;
      const cy = TILE / 2;
      g.lineBetween(cx + Math.cos(angle) * r1, cy + Math.sin(angle) * r1, cx + Math.cos(angle) * r2, cy + Math.sin(angle) * r2);
    }
    g.generateTexture('object-ruin-circle', TILE, TILE);

    // Pushable stone block -- a plain beveled slab, deliberately unremarkable
    // (distinct from the crate's cross-lashing and the bookshelf's spines) so it
    // reads as "a thing you shove," not a container to open.
    g.clear();
    g.fillStyle(0x6b6b6b, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0x818181, 1);
    g.fillRect(4, 4, TILE - 8, TILE - 8);
    g.lineStyle(2, 0x3a3a3a, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.generateTexture('object-block', TILE, TILE);

    // Chasm (sim/autotile.ts) -- an open pit, deliberately reading as an absence
    // rather than a solid obstacle the way object-block does: a near-black gap
    // with no fill inside at all (there's nothing there to fill), ringed by the
    // broken stone lip it was torn out of. Physically distinct from a wall or
    // Block on purpose -- see ObjectDef.blocksProject -- an open pit has nothing
    // solid in it to stop a Project beam on, unlike either of those would.
    //
    // **Sixteen pieces, and the rim is the whole reason.** A chasm eleven tiles
    // long is one hole in the ground. Drawn as eleven copies of one sprite it
    // reads as tiling instead: every seam is a straight line the world does not
    // contain, and the player ends up counting squares. So the lip is drawn only
    // on the sides that *are* an edge -- the ones the shape suffix does not name
    // -- and a run gets one rim round the outside and pure black through the
    // middle, where two neighbouring pits meet with nothing between them.
    //
    // Drawn in a loop rather than by hand for the same reason the cable pieces
    // are: these are not sixteen different things, they are one pit with an edge
    // on some subset of its sides, and hand-drawing the combinations would be
    // sixteen chances for two of them to disagree about how deep the lip is.
    const CHASM_PIT = 0x000000;
    const CHASM_STONE = 0x2a2620; // the broken ground the pit was torn out of
    const CHASM_LIP = 0x4a4438; // the lit edge of that break
    // How far the stone lip reaches in from an open side, sampled every 4px
    // across the tile. **The first and last entries must match**: two tiles side
    // by side both showing a north edge meet at that seam, and a profile that
    // did not close would put a visible step exactly where the change was
    // supposed to remove one.
    //
    // Shallow at the ends and deeper in the middle, which matters most on the
    // lone piece: a tile with all four sides open gets its corners cut twice,
    // once by each band, so a profile that bit deep at the ends turned an
    // isolated pit into a four-pointed star instead of a hole.
    const CHASM_JAG = [2, 4, 2, 5, 3, 4, 2, 4, 2];
    // (u along the edge, v inward) -> tile coordinates, per side. One rotation
    // rather than four hand-written edges, so all four lips are the same lip.
    const chasmEdge: Record<string, (u: number, v: number) => [number, number]> = {
      n: (u, v) => [u, v],
      e: (u, v) => [TILE - v, u],
      s: (u, v) => [TILE - u, TILE - v],
      w: (u, v) => [v, TILE - u],
    };
    const chasmPiece = (shape: string): void => {
      g.clear();
      // The pit itself, edge to edge. Sides that continue into another chasm
      // simply keep it, which is what makes two tiles read as one hole.
      g.fillStyle(CHASM_PIT, 1);
      g.fillRect(0, 0, TILE, TILE);
      const step = TILE / (CHASM_JAG.length - 1);
      for (const side of ['n', 'e', 's', 'w']) {
        if (shape.includes(side)) continue; // more pit that way -- no edge here
        const at = chasmEdge[side];
        const rim: [number, number][] = CHASM_JAG.map((depth, i) => at(i * step, depth));
        g.fillStyle(CHASM_STONE, 1);
        g.beginPath();
        const [sx, sy] = at(0, 0);
        g.moveTo(sx, sy);
        const [ex, ey] = at(TILE, 0);
        g.lineTo(ex, ey);
        for (let i = rim.length - 1; i >= 0; i--) g.lineTo(rim[i][0], rim[i][1]);
        g.closePath();
        g.fillPath();
        // The break itself, picked out along the inner boundary only -- the
        // outer boundary is where this tile stops, not where the ground does.
        g.lineStyle(1, CHASM_LIP, 0.7);
        g.beginPath();
        g.moveTo(rim[0][0], rim[0][1]);
        for (let i = 1; i < rim.length; i++) g.lineTo(rim[i][0], rim[i][1]);
        g.strokePath();
      }
    };
    for (const shape of AUTOTILE_SHAPES) {
      chasmPiece(shape === 'none' ? '' : shape);
      g.generateTexture(`object-chasm-${shape}`, TILE, TILE);
    }
    // The pre-autotile name, kept as the isolated piece. Nothing in the shipped
    // world asks for it any more, but a draft room saved before 2026-08-18 does,
    // and a missing texture is a magenta box rather than a stale-looking pit.
    chasmPiece('');
    g.generateTexture('object-chasm', TILE, TILE);

    // Rock -- Separate·Mass's split pieces (room7). Tier 2 uses this at full
    // size; tier 3 reuses the exact same texture scaled down further
    // (spriteScale) rather than needing a third hand-drawn sprite. An
    // irregular jagged blob, deliberately distinct from the pushable
    // block's cut rectangle, so a split piece reads as "broken off of"
    // rather than "a smaller version of the same cut stone."
    g.clear();
    g.fillStyle(0x767066, 1);
    g.beginPath();
    g.moveTo(TILE / 2, 3);
    g.lineTo(TILE - 5, 10);
    g.lineTo(TILE - 3, TILE / 2 + 4);
    g.lineTo(TILE - 9, TILE - 4);
    g.lineTo(9, TILE - 3);
    g.lineTo(3, TILE / 2);
    g.lineTo(6, 9);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, 0x433f38, 1);
    g.strokePath();
    g.fillStyle(0x8f887c, 1);
    g.fillTriangle(TILE / 2, 8, TILE - 10, TILE / 2, TILE / 2 + 2, TILE / 2 + 6);
    g.generateTexture('object-rock', TILE, TILE);

    // Signpost -- a dev-only hint object placed near every room's entrance (see
    // buildRoomN's `dev-signpost-*` entries). Deliberately distinct from every
    // other prop: a plain wooden post with a blank board, no color-coding, so it
    // reads as "developer note" rather than another piece of world lore.
    g.clear();
    g.fillStyle(0x6b5335, 1);
    g.fillRect(TILE / 2 - 2, 14, 4, TILE - 16);
    g.fillStyle(0xd8c8a0, 1);
    g.fillRect(5, 4, TILE - 10, 12);
    g.lineStyle(2, 0x4a3a22, 1);
    g.strokeRect(5, 4, TILE - 10, 12);
    g.fillStyle(0x4a3a22, 1);
    g.fillRect(TILE / 2 - 1, 8, 2, 5);
    g.fillRect(TILE / 2 - 1, 14, 2, 1);
    g.generateTexture('object-signpost', TILE, TILE);

    // Note -- a small sheet of parchment lying on the floor with three lines of
    // writing on it, deliberately *not* the signpost's post-and-board shape: a
    // Signpost is the dev-only walkthrough (hidden unless Developer Tools is
    // on), a Note is in-world text somebody left, and the player only ever sees
    // one of the two. Low and flat, because it is walkable.
    g.clear();
    g.fillStyle(0xd8cba8, 1);
    g.fillRect(6, 8, TILE - 12, TILE - 14);
    g.lineStyle(1, 0x8a7b58, 1);
    g.strokeRect(6, 8, TILE - 12, TILE - 14);
    // A turned-up corner, so it reads as paper rather than as a tile.
    g.fillStyle(0xbfae86, 1);
    g.fillTriangle(TILE - 6, 8, TILE - 6, 13, TILE - 11, 8);
    g.lineStyle(1, 0x6b5f44, 1);
    for (let i = 0; i < 3; i++) g.lineBetween(9, 13 + i * 4, TILE - 10, 13 + i * 4);
    g.generateTexture('object-note', TILE, TILE);

    // Table -- a wooden top seen from above with four legs at the corners, the
    // grain running the long way. Reads as furniture rather than as the Wood
    // Block's solid cube: a table is pushable but is not on the Separate·Mass
    // ladder, and the two must not be mistakable for each other.
    g.clear();
    g.fillStyle(0x7a5a32, 1);
    g.fillRect(3, 6, TILE - 6, TILE - 12);
    g.lineStyle(2, 0x4a3620, 1);
    g.strokeRect(3, 6, TILE - 6, TILE - 12);
    g.lineStyle(1, 0x5f462a, 1);
    g.lineBetween(6, 12, TILE - 6, 12);
    g.lineBetween(6, 18, TILE - 6, 18);
    g.fillStyle(0x4a3620, 1);
    for (const [lx, ly] of [[3, 3], [TILE - 8, 3], [3, TILE - 8], [TILE - 8, TILE - 8]] as const) {
      g.fillRect(lx, ly, 5, 5);
    }
    g.generateTexture('object-table', TILE, TILE);

    // Wall with Candle -- the room's wall colour with a lit candle in a sconce
    // set into it. Shares the candle's flame so it reads as the same *kind* of
    // Energy source as one standing on the floor, and shares the wall's block
    // so it reads as solid: both halves of what it is are visible, which is the
    // rule the 2026-08-09 sprite audit settled on.
    g.clear();
    g.fillStyle(0x2b2f38, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x1b1f26, 1);
    g.lineBetween(0, TILE / 3, TILE, TILE / 3);
    g.lineBetween(0, (TILE * 2) / 3, TILE, (TILE * 2) / 3);
    g.lineBetween(TILE / 2, 0, TILE / 2, TILE / 3);
    g.lineBetween(TILE / 3, (TILE * 2) / 3, TILE / 3, TILE);
    // The sconce bracket, then the candle and its flame.
    g.fillStyle(0x6b6b76, 1);
    g.fillRect(TILE / 2 - 6, TILE - 9, 12, 3);
    g.fillStyle(0xe8dcc0, 1);
    g.fillRect(TILE / 2 - 3, TILE - 19, 6, 10);
    g.fillStyle(0xf0a83f, 1);
    g.fillTriangle(TILE / 2, TILE - 27, TILE / 2 - 4, TILE - 19, TILE / 2 + 4, TILE - 19);
    g.generateTexture('object-wall-candle', TILE, TILE);

    // Trophy Wall -- a slab cut with six shallow niches, each shaped for one
    // rune crystal. Empty: the niches are darker than the wall, which is what a
    // hole looks like.
    const trophyNiches = [
      [6, 6], [TILE / 2 - 2, 6], [TILE - 10, 6],
      [6, TILE - 12], [TILE / 2 - 2, TILE - 12], [TILE - 10, TILE - 12],
    ] as const;
    g.clear();
    g.fillStyle(0x3c4049, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(2, 0x22262d, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0x1b1f26, 1);
    for (const [nx, ny] of trophyNiches) g.fillRect(nx, ny, 4, 6);
    g.generateTexture('object-trophy-wall', TILE, TILE);

    // Trophy Wall, filled -- the same slab with the niches holding something
    // that catches the light. The only difference is the niches, so the two
    // read as one object in two states rather than as two objects.
    g.clear();
    g.fillStyle(0x3c4049, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(2, 0x22262d, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    for (const [nx, ny] of trophyNiches) {
      g.fillStyle(0x1b1f26, 1);
      g.fillRect(nx, ny, 4, 6);
      g.fillStyle(0xc9a24b, 1);
      g.fillRect(nx + 1, ny + 1, 2, 4);
    }
    g.generateTexture('object-trophy-wall-lit', TILE, TILE);

    // Withered Standard -- a cracked, leaning urn, deliberately asymmetric and
    // drained of color (the "ruined" object in room6's Entropy puzzle). Its
    // opposite number is object-relic-pristine below.
    g.clear();
    g.fillStyle(0x4a463e, 1);
    g.fillTriangle(TILE / 2 - 3, TILE - 4, TILE / 2 + 7, TILE - 4, TILE / 2 + 1, 6);
    g.lineStyle(1, 0x2e2b26, 0.9);
    g.lineBetween(TILE / 2 - 1, 10, TILE / 2 + 3, TILE - 6);
    g.lineBetween(TILE / 2 + 4, 16, TILE / 2, TILE - 8);
    g.fillStyle(0x2e2b26, 1);
    g.fillCircle(TILE / 2 - 5, TILE - 10, 2);
    g.fillCircle(TILE / 2 + 6, TILE - 16, 1.5);
    g.generateTexture('object-relic-ruined', TILE, TILE);

    // Pristine Reliquary -- a smooth, symmetrical, brightly polished urn --
    // the "fine" object in room6's Entropy puzzle, deliberately drawn as the
    // Withered Standard's clean, upright mirror image.
    g.clear();
    g.fillStyle(0xd9c88a, 1);
    g.fillTriangle(TILE / 2 - 7, TILE - 4, TILE / 2 + 7, TILE - 4, TILE / 2, 5);
    g.lineStyle(1, 0xffe9a8, 0.9);
    g.strokeTriangle(TILE / 2 - 7, TILE - 4, TILE / 2 + 7, TILE - 4, TILE / 2, 5);
    g.fillStyle(0xb89a5a, 1);
    g.fillCircle(TILE / 2, TILE / 2 + 2, 3);
    g.generateTexture('object-relic-pristine', TILE, TILE);

    // Quarry Gate (room8) -- a portcullis of iron bars set in a stone frame,
    // shut. A gate that can only ever open once is removed from the room the
    // moment it does (see checkGateThreshold), so this is the only look it
    // needs; a gate that can be shut again keeps its frame and raises the bars
    // instead -- object-gate-open, just below.
    g.clear();
    g.fillStyle(0x3a3a3e, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(2, 0x232326, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0x6b6f76, 1);
    for (let i = 0; i < 4; i++) {
      const bx = 5 + i * 6;
      g.fillRect(bx, 3, 3, TILE - 6);
    }
    g.lineStyle(2, 0x4a4e54, 1);
    g.lineBetween(3, TILE / 2, TILE - 3, TILE / 2);
    g.generateTexture('object-gate', TILE, TILE);

    // The same gate with its bars drawn up into the head of the frame: what a
    // gate that can be shut again looks like while open (`gate.openTexture`).
    // Deliberately the same frame in the same place -- the thing that changed
    // is the bars, and an open gate that shared nothing with its shut self
    // would read as a different object rather than as the same one, open.
    g.clear();
    g.fillStyle(0x3a3a3e, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(2, 0x232326, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0x6b6f76, 1);
    for (let i = 0; i < 4; i++) {
      const bx = 5 + i * 6;
      g.fillRect(bx, 3, 3, 5);
    }
    g.lineStyle(2, 0x4a4e54, 1);
    g.lineBetween(3, 9, TILE - 3, 9);
    g.generateTexture('object-gate-open', TILE, TILE);

    // Pressure pads (room8) -- a stone plate set flush in the floor, the inset
    // metal plate sized to hint at what tier of rock it wants (small/medium/
    // large), distinct enough from each other to read at a glance which is
    // which. Always `walkable: true` -- a floor fixture, not an obstacle.
    const padBase = () => {
      g.clear();
      g.fillStyle(0x55544c, 1);
      g.fillRect(2, 2, TILE - 4, TILE - 4);
      g.lineStyle(1, 0x403f38, 1);
      g.strokeRect(2, 2, TILE - 4, TILE - 4);
    };
    padBase();
    g.fillStyle(0x8a8578, 1);
    g.fillRect(TILE / 2 - 4, TILE / 2 - 4, 8, 8);
    g.lineStyle(1, 0x2e2d28, 1);
    g.strokeRect(TILE / 2 - 4, TILE / 2 - 4, 8, 8);
    g.generateTexture('object-pad-small', TILE, TILE);

    // The pad that wants something other than a specific rock tier
    // (`forAnyPushable`, `forPlayer`) -- a ring rather than a sized plate,
    // added 2026-08-09. The three sizes above are a real claim: room7 teaches
    // small/medium/large as tier 3/2/1, and a player who learns that then met
    // a large pad in room5/room14 that took anything and a small one in room16
    // that read their own feet. A sprite that means a tier in one room and
    // nothing in another is worse than one that never claimed to, so size now
    // means tier and only tier, and this is what everything else wears.
    padBase();
    g.lineStyle(2, 0x8a8578, 1);
    g.strokeCircle(TILE / 2, TILE / 2, 6);
    g.lineStyle(1, 0x2e2d28, 1);
    g.strokeCircle(TILE / 2, TILE / 2, 7.5);
    g.generateTexture('object-pad-plain', TILE, TILE);

    padBase();
    g.fillStyle(0x8a8578, 1);
    g.fillRect(TILE / 2 - 8, TILE / 2 - 8, 16, 16);
    g.lineStyle(1, 0x2e2d28, 1);
    g.strokeRect(TILE / 2 - 8, TILE / 2 - 8, 16, 16);
    g.generateTexture('object-pad-medium', TILE, TILE);

    padBase();
    g.fillStyle(0x8a8578, 1);
    g.fillRect(4, 4, TILE - 8, TILE - 8);
    g.lineStyle(1, 0x2e2d28, 1);
    g.strokeRect(4, 4, TILE - 8, TILE - 8);
    g.generateTexture('object-pad-large', TILE, TILE);

    // Charged Font -- a stone basin with charge crackling across its surface,
    // the world's charge source. Deliberately distinct from room3's Magnetic
    // Block (a solid charged mass) -- this one reads as a reservoir/well, not
    // an object with charge locked inside.
    //
    // Three of them, one per rung of the load ladder (sim/chargeSource.ts).
    // The basin is identical in all three and only the arc changes: colour,
    // brightness, and how high it reaches. That is the point -- the object has
    // not become a different object, it is the same basin failing to keep up,
    // so the silhouette has to stay put while the thing the player is actually
    // being told about (how much is getting through) is what moves.
    const chargeFont = (key: string, arc: number, alpha: number, reach: number): void => {
      g.clear();
      g.fillStyle(0x3a3a40, 1);
      g.fillRect(3, TILE / 2, TILE - 6, TILE / 2 - 4);
      g.lineStyle(1, 0x24242a, 1);
      g.strokeRect(3, TILE / 2, TILE - 6, TILE / 2 - 4);
      g.lineStyle(2, arc, alpha);
      g.lineBetween(6, TILE / 2 + 2, TILE / 2 - 2, reach);
      g.lineBetween(TILE / 2 - 2, reach, TILE / 2 + 4, TILE / 2 - 2);
      g.lineBetween(TILE / 2 + 4, TILE / 2 - 2, TILE - 6, reach - 2);
      g.generateTexture(key, TILE, TILE);
    };
    chargeFont('object-charge-font', 0x5aa8ee, 0.9, 10);
    chargeFont('object-charge-font-straining', 0xe0a33c, 0.85, TILE / 2 - 8);
    chargeFont('object-charge-font-overdrawn', 0xd0503c, 0.8, TILE / 2 - 4);

    // Standing pillar (room9) -- a plain stone column, the passive charge
    // accumulator a marked Charged Font gets Transferred into. No lit/unlit
    // states of its own (unlike the gate pillars below) -- its own Charge
    // value is directly castable and readable via the debug overlay, nothing
    // about it needs a live visual.
    g.clear();
    g.fillStyle(0x5c5a56, 1);
    g.fillRect(8, 2, TILE - 16, TILE - 4);
    g.lineStyle(1, 0x403e3a, 1);
    g.strokeRect(8, 2, TILE - 16, TILE - 4);
    g.fillStyle(0x6b6862, 1);
    g.fillRect(6, 2, TILE - 12, 3);
    g.fillRect(6, TILE - 6, TILE - 12, 3);
    g.generateTexture('object-pillar', TILE, TILE);

    // Gate pillar (room9), unlit -- a squat stone pillar capped with an
    // empty charge socket, dark until conducted charge reaches it.
    g.clear();
    g.fillStyle(0x4a4844, 1);
    g.fillRect(7, 6, TILE - 14, TILE - 8);
    g.lineStyle(1, 0x322f2b, 1);
    g.strokeRect(7, 6, TILE - 14, TILE - 8);
    g.fillStyle(0x2e2c28, 1);
    g.fillCircle(TILE / 2, 9, 4);
    g.generateTexture('object-pillar-gate', TILE, TILE);

    // Gate pillar, lit -- the same shape, its socket glowing once conducted
    // charge actually reaches it (see refreshConduitVisual).
    g.clear();
    g.fillStyle(0x4a4844, 1);
    g.fillRect(7, 6, TILE - 14, TILE - 8);
    g.lineStyle(1, 0x322f2b, 1);
    g.strokeRect(7, 6, TILE - 14, TILE - 8);
    g.fillStyle(0x5aa8ee, 1);
    g.fillCircle(TILE / 2, 9, 4);
    g.lineStyle(1, 0xbfe0ff, 0.9);
    g.strokeCircle(TILE / 2, 9, 6);
    g.generateTexture('object-pillar-gate-lit', TILE, TILE);

    // Metal conduction block (room9) -- cool silvery-blue with rivets, reads
    // as conductive at a glance against the wood block below.
    g.clear();
    g.fillStyle(0x8a95a0, 1);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x525d68, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.fillStyle(0x525d68, 1);
    [
      [6, 6],
      [TILE - 8, 6],
      [6, TILE - 8],
      [TILE - 8, TILE - 8],
    ].forEach(([rx, ry]) => g.fillCircle(rx, ry, 2));
    g.generateTexture('object-block-metal', TILE, TILE);

    // Rusted Metal Block (sim/rust.ts) -- same silhouette and rivets as the
    // clean block above, but the silvery-blue base is gone, replaced with a
    // dull rust-brown and blotchy patches of corrosion. Reads as "no longer
    // conductive" at a glance, the same way the clean version reads as
    // conductive.
    g.clear();
    g.fillStyle(0x7a4a30, 1);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x4a2c1a, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.fillStyle(0x9c5a34, 1);
    [
      [10, 9, 5],
      [TILE - 12, 12, 4],
      [12, TILE - 11, 4],
      [TILE - 10, TILE - 10, 3],
    ].forEach(([rx, ry, r]) => g.fillCircle(rx, ry, r));
    g.fillStyle(0x4a2c1a, 1);
    [
      [6, 6],
      [TILE - 8, 6],
      [6, TILE - 8],
      [TILE - 8, TILE - 8],
    ].forEach(([rx, ry]) => g.fillCircle(rx, ry, 2));
    g.generateTexture('object-block-metal-rusted', TILE, TILE);

    // Wood conduction block (room9) -- warm brown with a grain pattern,
    // reads as non-conductive against the metal block above.
    g.clear();
    g.fillStyle(0x7a5a36, 1);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x4a3620, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(1, 0x5e4526, 0.8);
    g.lineBetween(4, 10, TILE - 4, 8);
    g.lineBetween(4, 18, TILE - 4, 20);
    g.lineBetween(4, 26, TILE - 4, 24);
    g.generateTexture('object-block-wood', TILE, TILE);

    // Wood Block, rotten (sim/rust.ts) -- same silhouette, gone soft and dark
    // with rot rather than corroded like metal: duller, greyed-brown fill,
    // no clean grain lines, a scatter of dark rot spots instead.
    g.clear();
    g.fillStyle(0x5c4a34, 1);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x362a1c, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.fillStyle(0x2e2418, 0.9);
    [
      [8, 9, 2.5],
      [TILE - 10, 8, 2],
      [10, TILE - 10, 2],
      [TILE - 9, TILE - 9, 2.5],
    ].forEach(([rx, ry, r]) => g.fillCircle(rx, ry, r));
    g.generateTexture('object-block-wood-rotten', TILE, TILE);

    // Sand Block (sim/sand.ts) -- a packed, sun-baked mound, pale tan with a
    // scatter of darker grain-flecks rather than wood's clean lines or
    // metal's rivets, reading as granular rather than solid.
    g.clear();
    g.fillStyle(0xd8c496, 1);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0xa8925e, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.fillStyle(0xb8a06c, 0.8);
    [
      [8, 8],
      [TILE - 10, 9],
      [7, TILE - 9],
      [TILE - 9, TILE - 8],
      [TILE / 2, TILE / 2 + 2],
    ].forEach(([rx, ry]) => g.fillCircle(rx, ry, 1.5));
    g.generateTexture('object-block-sand', TILE, TILE);

    // Sand Block, molten (sim/sand.ts) -- Energy above meltEnergy: a slumped,
    // rounded blob rather than a rigid rectangle (it's liquid now), glowing
    // orange-red with a brighter, hotter core.
    g.clear();
    g.fillStyle(0x8a2e0f, 1);
    g.fillEllipse(TILE / 2, TILE / 2 + 3, TILE / 2 - 3, TILE / 2 - 6);
    g.fillStyle(0xd94b1f, 1);
    g.fillEllipse(TILE / 2, TILE / 2 + 3, TILE / 2 - 7, TILE / 2 - 10);
    g.fillStyle(0xf0a83f, 0.9);
    g.fillEllipse(TILE / 2, TILE / 2 + 4, 6, 4);
    g.generateTexture('object-block-glass-molten', TILE, TILE);

    // Sand Block, glass -- cooled back to a rigid block silhouette, smooth
    // pale blue-white with a diagonal highlight streak reading as
    // translucent rather than solid.
    g.clear();
    g.fillStyle(0xbfe0e6, 0.85);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0x7fa8ae, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0xf0fbfc, 0.8);
    g.lineBetween(7, TILE - 7, TILE - 7, 7);
    g.generateTexture('object-block-glass', TILE, TILE);

    // Sand Block, quartz ("crystal" deliberately avoided as a name -- this
    // world already has a proper-noun Crystal, the four reward pillars, and
    // this is not that) -- clear white-violet with angular facet lines
    // meeting at a point, reading as an ordered lattice rather than glass's
    // smooth translucency.
    g.clear();
    g.fillStyle(0xe8e0f5, 0.9);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(2, 0xa898c8, 1);
    g.strokeRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(1, 0xffffff, 0.9);
    g.lineBetween(TILE / 2, 4, TILE / 2, TILE - 4);
    g.lineBetween(4, TILE / 2, TILE - 4, TILE / 2);
    g.lineBetween(6, 6, TILE - 6, TILE - 6);
    g.lineBetween(TILE - 6, 6, 6, TILE - 6);
    g.generateTexture('object-block-quartz', TILE, TILE);

    // Split Beam -- a length of timber split down from a Wood Block, tier-2
    // mass piece (material:'wood'). An elongated diagonal splinter rather than
    // object-rock's irregular blob or object-scrap-metal's jagged offcut --
    // wood splits along the grain, so the piece reads as a clean length rather
    // than a broken chunk. Kindling (tier 3) reuses this scaled down, the same
    // shortcut Pebble takes off Rock.
    g.clear();
    g.fillStyle(0x8a6a42, 1);
    g.beginPath();
    g.moveTo(6, TILE - 6);
    g.lineTo(12, TILE - 16);
    g.lineTo(TILE - 8, 8);
    g.lineTo(TILE - 4, 12);
    g.lineTo(TILE - 12, TILE - 8);
    g.lineTo(TILE - 18, TILE - 4);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, 0x4a3620, 1);
    g.strokePath();
    g.lineStyle(1, 0x6b4f2c, 0.8);
    g.lineBetween(10, TILE - 10, TILE - 10, 10);
    g.generateTexture('object-wood-beam', TILE, TILE);

    // Split Beam / Kindling, rotten (sim/rust.ts) -- the same splinter shape,
    // gone dull and dark rather than jagged-and-shiny like rusted scrap metal.
    g.clear();
    g.fillStyle(0x5c4a34, 1);
    g.beginPath();
    g.moveTo(6, TILE - 6);
    g.lineTo(12, TILE - 16);
    g.lineTo(TILE - 8, 8);
    g.lineTo(TILE - 4, 12);
    g.lineTo(TILE - 12, TILE - 8);
    g.lineTo(TILE - 18, TILE - 4);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, 0x362a1c, 1);
    g.strokePath();
    g.fillStyle(0x2e2418, 0.9);
    g.fillCircle(TILE / 2 + 2, TILE / 2, 3);
    g.fillCircle(TILE / 2 - 6, TILE / 2 + 6, 2);
    g.generateTexture('object-wood-beam-rotten', TILE, TILE);

    // Warded Boulder (room9) -- an ordinary-looking boulder (object-block's
    // cut-rectangle shape) with a faint etched rune, hinting at a ward before
    // the player ever tries to Separate it and finds out the hard way.
    g.clear();
    g.fillStyle(0x6b6b6b, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0x818181, 1);
    g.fillRect(4, 4, TILE - 8, TILE - 8);
    g.lineStyle(2, 0x3a3a3a, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(1, 0x3a2f1a, 0.85);
    g.strokeCircle(TILE / 2, TILE / 2, 6);
    g.lineBetween(TILE / 2 - 4, TILE / 2 - 4, TILE / 2 + 4, TILE / 2 + 4);
    g.lineBetween(TILE / 2 + 4, TILE / 2 - 4, TILE / 2 - 4, TILE / 2 + 4);
    g.generateTexture('object-boulder-warded', TILE, TILE);

    // Fractured Reliquary (room9) -- the entropy source for the bridge
    // collapse, deliberately jagged and split through the middle (distinct
    // from room6's Withered Standard, a separate relic with the same
    // "holds real entropy to give away" role).
    g.clear();
    g.fillStyle(0x4a4642, 1);
    g.fillTriangle(TILE / 2 - 7, TILE - 4, TILE / 2 + 7, TILE - 4, TILE / 2, 5);
    g.lineStyle(1, 0x2a2824, 1);
    g.lineBetween(TILE / 2 - 2, 8, TILE / 2 + 3, TILE / 2);
    g.lineBetween(TILE / 2 + 3, TILE / 2, TILE / 2 - 3, TILE - 6);
    g.fillStyle(0x2a2824, 1);
    g.fillCircle(TILE / 2 - 5, TILE - 8, 1.5);
    g.generateTexture('object-relic-fractured', TILE, TILE);

    // Bridge pillar (room9) -- a stone archway wedge, seen from above jutting
    // up out of its recess below the walkway. The one Entropy·Transfer is
    // actually cast at (pillar-9-bridge-a) to bring the whole span down; its
    // inert twin (pillar-9-bridge-b) reuses the same texture.
    g.clear();
    g.fillStyle(0x5a5650, 1);
    g.fillRect(4, TILE / 2 - 2, TILE - 8, TILE / 2 - 2);
    g.fillTriangle(4, TILE / 2 - 2, TILE - 4, TILE / 2 - 2, TILE / 2, 4);
    g.lineStyle(1, 0x3a3730, 1);
    g.strokeRect(4, TILE / 2 - 2, TILE - 8, TILE / 2 - 2);
    g.strokeTriangle(4, TILE / 2 - 2, TILE - 4, TILE / 2 - 2, TILE / 2, 4);
    g.generateTexture('object-bridge-pillar', TILE, TILE);

    // Rubble -- the shared `collapsesInto` texture for both the bridge
    // pillar and every warded boulder once the bridge's entropy threshold
    // is crossed: a flat, low pile of debris, walkable rather than an
    // obstacle, reading as "collapsed" rather than "gone".
    g.clear();
    g.fillStyle(0x4a463e, 1);
    g.fillEllipse(TILE / 2, TILE / 2 + 4, TILE / 2 - 3, 8);
    g.fillStyle(0x5c574c, 1);
    g.fillEllipse(TILE / 2 - 5, TILE / 2 + 2, 6, 4);
    g.fillEllipse(TILE / 2 + 6, TILE / 2 + 5, 5, 3);
    g.generateTexture('object-rubble', TILE, TILE);

    // Ruined Aqueduct (room9, the aqueduct/water-wheel room) -- a cracked
    // stone trough, empty and dark inside. Entropy·Transfer draining it
    // below threshold swaps it for object-aqueduct-restored (transformsInto).
    g.clear();
    g.fillStyle(0x6b665c, 1);
    g.fillRect(2, 10, TILE - 4, TILE - 16);
    g.lineStyle(2, 0x433f38, 1);
    g.strokeRect(2, 10, TILE - 4, TILE - 16);
    g.fillStyle(0x2a2620, 1);
    g.fillRect(5, 13, TILE - 10, TILE - 22);
    g.lineStyle(1, 0x433f38, 0.9);
    g.lineBetween(8, 12, TILE - 8, 20);
    g.lineBetween(TILE - 10, 12, 10, 20);
    g.generateTexture('object-aqueduct-ruined', TILE, TILE);

    // Restored Aqueduct -- the same trough, mended, and *empty*. Mending is
    // what the Entropy cast does; water arriving is what the channel feeding it
    // does, and drawing the second into the sprite for the first meant an
    // aqueduct mended while its feed was frozen or dammed sat there brim-full
    // of water that was not arriving (reported 2026-08-11). The dark channel
    // interior is the ruined trough's, unchanged -- what mending changes is the
    // stone around it.
    g.clear();
    g.fillStyle(0x8a8578, 1);
    g.fillRect(2, 10, TILE - 4, TILE - 16);
    g.lineStyle(2, 0x55534a, 1);
    g.strokeRect(2, 10, TILE - 4, TILE - 16);
    g.fillStyle(0x2a2620, 1);
    g.fillRect(5, 13, TILE - 10, TILE - 22);
    g.generateTexture('object-aqueduct-restored', TILE, TILE);

    // Flowing Aqueduct -- the mended trough with real water in it. Painted by
    // recomputeWaterFlow (water.flowingTexture) exactly while the trace reaches
    // this tile, like any other channel tile, rather than by the barrier that
    // mended the stone.
    g.clear();
    g.fillStyle(0x8a8578, 1);
    g.fillRect(2, 10, TILE - 4, TILE - 16);
    g.lineStyle(2, 0x55534a, 1);
    g.strokeRect(2, 10, TILE - 4, TILE - 16);
    g.fillStyle(0x3d7ab8, 1);
    g.fillRect(5, 13, TILE - 10, TILE - 22);
    g.lineStyle(1, 0xa9d4f2, 0.8);
    g.lineBetween(7, 16, TILE - 7, 16);
    g.generateTexture('object-aqueduct-flowing', TILE, TILE);

    // River source (the spring/font the water-flow recompute always treats
    // as "on") -- a small rounded basin with water welling up, distinct from
    // the Charged Font's crackle-lines above (this one reads as flow, not
    // charge).
    g.clear();
    g.fillStyle(0x565248, 1);
    g.fillCircle(TILE / 2, TILE / 2 + 2, TILE / 2 - 4);
    g.lineStyle(2, 0x363329, 1);
    g.strokeCircle(TILE / 2, TILE / 2 + 2, TILE / 2 - 4);
    g.fillStyle(0x3d7ab8, 1);
    g.fillCircle(TILE / 2, TILE / 2 + 2, TILE / 2 - 8);
    g.fillStyle(0xa9d4f2, 0.9);
    g.fillCircle(TILE / 2, TILE / 2 + 2, 3);
    g.generateTexture('object-river-source', TILE, TILE);

    // Riverbed, dry -- cracked pale earth, walkable, becomes
    // object-riverbed-flowing once the water-flow recompute reaches it.
    g.clear();
    g.fillStyle(0x9c9070, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x7a7058, 0.8);
    g.lineBetween(4, 6, 12, 14);
    g.lineBetween(20, 4, 14, 16);
    g.lineBetween(6, 22, 16, 26);
    g.lineBetween(22, 20, 26, 28);
    g.generateTexture('object-riverbed-dry', TILE, TILE);

    // The lava channel's three states (2026-08-09), deliberately built as the
    // *same silhouette* as the water channel above with a different material:
    // shape says "this is a channel", colour says which fluid runs in it. A
    // Fissure is the lava twin of the Spring, a Basalt Trench of the dry
    // Riverbed, and Cooled Basalt of the frozen one -- flowing reuses
    // `object-floor-lava`, since a channel running with lava should look like
    // lava and does.

    // Fissure -- the lava source. Same stone rim as object-river-source,
    // filled with the glow of something coming up from a long way down.
    g.clear();
    g.fillStyle(0x565248, 1);
    g.fillCircle(TILE / 2, TILE / 2 + 2, TILE / 2 - 4);
    g.lineStyle(2, 0x363329, 1);
    g.strokeCircle(TILE / 2, TILE / 2 + 2, TILE / 2 - 4);
    g.fillStyle(0xb83d10, 1);
    g.fillCircle(TILE / 2, TILE / 2 + 2, TILE / 2 - 8);
    g.fillStyle(0xffc46a, 0.95);
    g.fillCircle(TILE / 2, TILE / 2 + 2, 3);
    g.generateTexture('object-lava-fissure', TILE, TILE);

    // Basalt Trench, empty -- the same cracked-channel drawing as the dry
    // riverbed, in dark volcanic stone rather than pale earth.
    g.clear();
    g.fillStyle(0x4a4340, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x312c2a, 0.9);
    g.lineBetween(4, 6, 12, 14);
    g.lineBetween(20, 4, 14, 16);
    g.lineBetween(6, 22, 16, 26);
    g.lineBetween(22, 20, 26, 28);
    g.generateTexture('object-trench-basalt', TILE, TILE);

    // Cooled Basalt -- a trench whose lava has frozen solid. Ice is to water
    // what this is to lava, and it reads the same way: the channel is still
    // visibly a channel, but what fills it has set hard, with only the last
    // heat still showing in the cracks.
    g.clear();
    g.fillStyle(0x2e2a29, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.fillStyle(0x413b39, 1);
    g.fillRect(2, 2, TILE - 4, TILE - 4);
    g.lineStyle(1, 0x6b3a1e, 0.85);
    g.lineBetween(5, 8, 14, 15);
    g.lineBetween(14, 15, 11, 25);
    g.lineBetween(14, 15, 24, 19);
    g.generateTexture('object-basalt-cooled', TILE, TILE);

    // Riverbed, flowing -- the same tile, filled with moving water.
    g.clear();
    g.fillStyle(0x3d7ab8, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0xa9d4f2, 0.7);
    g.lineBetween(2, 8, 12, 6);
    g.lineBetween(16, 18, 28, 16);
    g.lineBetween(4, 26, 14, 24);
    g.generateTexture('object-riverbed-flowing', TILE, TILE);

    // Riverbed, frozen -- water-flow recompute treats this as impassable to
    // the flood-fill (draining Energy from a flowing tile below threshold,
    // via a Transfer·Energy cast targeting a marked bonfire, swaps a
    // flowing tile to this).
    g.clear();
    g.fillStyle(0xbfe0ee, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0xffffff, 0.8);
    g.lineBetween(6, 4, 10, TILE - 4);
    g.lineBetween(TILE - 8, 2, TILE - 14, TILE - 6);
    g.lineBetween(4, 18, 18, 12);
    g.generateTexture('object-riverbed-frozen', TILE, TILE);

    // Water wheel, still -- a circular wheel with spokes, dry. The endpoint of
    // the redirected river, and what it looks like with nothing running on it.
    // Entropy at or above 4 wrecks it (collapsesInto object-wheel-broken);
    // water reaching it swaps it for object-wheel-turning below and powers the
    // substation (see the flow-recompute pass).
    g.clear();
    g.lineStyle(3, 0x5a4a30, 1);
    g.strokeCircle(TILE / 2, TILE / 2, TILE / 2 - 4);
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      g.lineBetween(TILE / 2, TILE / 2, TILE / 2 + Math.cos(angle) * (TILE / 2 - 5), TILE / 2 + Math.sin(angle) * (TILE / 2 - 5));
    }
    g.fillStyle(0x5a4a30, 1);
    g.fillCircle(TILE / 2, TILE / 2, 3);
    g.generateTexture('object-wheel', TILE, TILE);

    // Water wheel, turning -- the same wheel with the river actually on it, and
    // the one sprite in the pair that says the machine is *working*. Painted by
    // the flow pass through `water.flowingTexture`, exactly as a wet Riverbed
    // is, and deliberately not shown while the wheel is wrecked (see
    // `queries.isWrecked` and `recomputeWaterFlow`'s own note): a seized wheel
    // with a river hitting it is still a seized wheel.
    //
    // Three things separate it from the still wheel, in order of how far away
    // they read: the paddles are **wet** (a lighter, cooler timber, and water
    // caught between them at the bottom of the arc), the rim carries **motion
    // arcs** trailing off the leading edge of each paddle, and the whole thing
    // is **rotated half a spoke** so a glance between the two sees the wheel in
    // a different position. There is no sprite animation anywhere in this
    // renderer, so "turning" has to be legible in one frame.
    g.clear();
    // Water gathered in the lower paddles, drawn first so the timber sits over
    // it -- this is the half of the wheel the river is actually pushing on.
    g.fillStyle(0x2f6b8a, 0.75);
    g.slice(TILE / 2, TILE / 2, TILE / 2 - 4, Math.PI * 0.15, Math.PI * 0.85);
    g.fillPath();
    g.lineStyle(3, 0x7a6742, 1); // wet timber: lighter than the dry 0x5a4a30
    g.strokeCircle(TILE / 2, TILE / 2, TILE / 2 - 4);
    for (let i = 0; i < 6; i++) {
      // Half a spoke round from the still wheel, so the two never look alike.
      const angle = (Math.PI / 3) * i + Math.PI / 6;
      g.lineBetween(TILE / 2, TILE / 2, TILE / 2 + Math.cos(angle) * (TILE / 2 - 5), TILE / 2 + Math.sin(angle) * (TILE / 2 - 5));
    }
    // Motion: a short arc trailing each spoke, fading with distance from it.
    g.lineStyle(1, 0xbfe0ee, 0.65);
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i + Math.PI / 6;
      g.beginPath();
      g.arc(TILE / 2, TILE / 2, TILE / 2 - 7, angle - 0.42, angle - 0.1);
      g.strokePath();
    }
    g.fillStyle(0x7a6742, 1);
    g.fillCircle(TILE / 2, TILE / 2, 3);
    g.generateTexture('object-wheel-turning', TILE, TILE);

    // Water wheel, broken -- cracked through, one spoke missing, tilted.
    g.clear();
    g.lineStyle(3, 0x433524, 1);
    g.strokeCircle(TILE / 2, TILE / 2, TILE / 2 - 4);
    for (let i = 0; i < 5; i++) {
      const angle = (Math.PI / 3) * i + 0.3;
      g.lineBetween(TILE / 2, TILE / 2, TILE / 2 + Math.cos(angle) * (TILE / 2 - 5), TILE / 2 + Math.sin(angle) * (TILE / 2 - 5));
    }
    g.fillStyle(0x433524, 1);
    g.fillCircle(TILE / 2, TILE / 2, 3);
    g.lineStyle(2, 0x1a1712, 0.9);
    g.lineBetween(8, 8, TILE - 8, TILE - 8);
    g.generateTexture('object-wheel-broken', TILE, TILE);

    // Power substation -- a squat stone plinth fed by the wheel, holding the
    // Charge the tower needs (reuses the Standing Pillar's plain-reservoir
    // role, drawn distinctly so it doesn't read as a room9-Conduit-Crossing
    // duplicate).
    g.clear();
    g.fillStyle(0x4e4c46, 1);
    g.fillRect(5, 8, TILE - 10, TILE - 12);
    g.lineStyle(1, 0x2e2c28, 1);
    g.strokeRect(5, 8, TILE - 10, TILE - 12);
    g.fillStyle(0x2e2c28, 1);
    g.fillRect(9, 4, TILE - 18, 6);
    g.fillStyle(0x5aa8ee, 0.85);
    g.fillCircle(TILE / 2, TILE / 2 + 2, 3);
    g.generateTexture('object-substation', TILE, TILE);

    // Tower (unlit) -- taller/narrower than a Gate Pillar, so it reads
    // visually distinct even though it plays the exact same conduit.feed role.
    g.clear();
    g.fillStyle(0x4a4844, 1);
    g.fillRect(10, 2, TILE - 20, TILE - 4);
    g.lineStyle(1, 0x2e2c28, 1);
    g.strokeRect(10, 2, TILE - 20, TILE - 4);
    g.fillStyle(0x2e2c28, 1);
    g.fillCircle(TILE / 2, 6, 3);
    g.generateTexture('object-tower', TILE, TILE);

    // Tower, lit -- glows once conductive blocks bridge it to the
    // substation (see refreshConduitVisual, reused unchanged).
    g.clear();
    g.fillStyle(0x4a4844, 1);
    g.fillRect(10, 2, TILE - 20, TILE - 4);
    g.lineStyle(1, 0x2e2c28, 1);
    g.strokeRect(10, 2, TILE - 20, TILE - 4);
    g.fillStyle(0x5aa8ee, 1);
    g.fillCircle(TILE / 2, 6, 3);
    g.lineStyle(1, 0xbfe0ff, 0.9);
    g.strokeCircle(TILE / 2, 6, 5);
    g.generateTexture('object-tower-lit', TILE, TILE);

    // Unlit bonfire -- a stack of cold, grey logs. Draining Energy into it
    // from a marked, flowing river tile lights it (transformsInto below).
    g.clear();
    g.fillStyle(0x5a5248, 1);
    g.fillRect(6, TILE - 10, TILE - 12, 6);
    g.fillStyle(0x6b6258, 1);
    g.fillRect(8, TILE - 16, TILE - 16, 6);
    g.fillRect(10, TILE - 22, TILE - 20, 6);
    g.generateTexture('object-bonfire-unlit', TILE, TILE);

    // Lit bonfire -- the same log stack, flame on top (fx-flame's triangle
    // language, scaled down to sit above the logs rather than fill the tile).
    g.clear();
    g.fillStyle(0x5a5248, 1);
    g.fillRect(6, TILE - 10, TILE - 12, 6);
    g.fillStyle(0x6b6258, 1);
    g.fillRect(8, TILE - 16, TILE - 16, 6);
    g.fillRect(10, TILE - 22, TILE - 20, 6);
    g.fillStyle(0xd94b1f, 1);
    g.fillTriangle(TILE / 2, 4, 9, TILE - 22, TILE - 9, TILE - 22);
    g.fillStyle(0xf0a83f, 1);
    g.fillTriangle(TILE / 2, 9, TILE / 2 - 5, TILE - 22, TILE / 2 + 5, TILE - 22);
    g.generateTexture('object-bonfire-lit', TILE, TILE);

    // Immutable block -- flanks the final reward, deliberately reads as
    // "not pushable, not splittable" against every other block texture: no
    // rivets (metal), no grain (wood), no cut-highlight (ordinary rock),
    // just a flat, heavily bordered slab.
    g.clear();
    g.fillStyle(0x38373a, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(3, 0x1e1d20, 1);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.generateTexture('object-immutable-block', TILE, TILE);

    // Metal Tool -- a wrench-like silhouette, tier-2 mass piece
    // (material:'metal'), same silvery palette as object-block-metal so it
    // reads as the same substance despite the different shape.
    g.clear();
    g.fillStyle(0x8a95a0, 1);
    g.fillRect(TILE / 2 - 3, 6, 6, TILE - 12);
    g.fillCircle(TILE / 2, 8, 6);
    g.fillCircle(TILE / 2, TILE - 8, 5);
    g.lineStyle(2, 0x525d68, 1);
    g.strokeCircle(TILE / 2, 8, 6);
    g.strokeCircle(TILE / 2, TILE - 8, 5);
    g.generateTexture('object-metal-tool', TILE, TILE);

    // Corroded Metal Tool (sim/rust.ts, kind 'metal-tool') -- the same wrench
    // silhouette in the same rust palette the scrap and block tiers use, so a
    // corroded tool reads as the same corroded material family rather than a
    // different object. The tool needs a pair of its own because its shape is
    // not on the Separate·Mass tier ladder: dressing it from the tier tables
    // would repaint it as an offcut.
    g.clear();
    g.fillStyle(0x7a4a30, 1);
    g.fillRect(TILE / 2 - 3, 6, 6, TILE - 12);
    g.fillCircle(TILE / 2, 8, 6);
    g.fillCircle(TILE / 2, TILE - 8, 5);
    g.lineStyle(2, 0x4a2c1a, 1);
    g.strokeCircle(TILE / 2, 8, 6);
    g.strokeCircle(TILE / 2, TILE - 8, 5);
    g.fillStyle(0x9c5a34, 1);
    g.fillCircle(TILE / 2 + 2, TILE / 2 + 2, 3);
    g.fillCircle(TILE / 2 - 3, TILE / 2 - 4, 2);
    g.generateTexture('object-metal-tool-rusted', TILE, TILE);

    // Scrap Metal -- a jagged offcut, tier-2 mass piece (material 'metal'),
    // reusing object-rock's broken-blob silhouette but in the metal palette.
    g.clear();
    g.fillStyle(0x8a95a0, 1);
    g.beginPath();
    g.moveTo(TILE / 2, 4);
    g.lineTo(TILE - 6, 11);
    g.lineTo(TILE - 4, TILE / 2 + 5);
    g.lineTo(TILE - 10, TILE - 5);
    g.lineTo(10, TILE - 4);
    g.lineTo(4, TILE / 2);
    g.lineTo(7, 10);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, 0x525d68, 1);
    g.strokePath();
    g.generateTexture('object-scrap-metal', TILE, TILE);

    // Rusted Scrap Metal (sim/rust.ts) -- the same jagged silhouette, in the
    // same rust palette the block above uses, so both metal tiers read as one
    // corroded material family rather than two unrelated reskins.
    g.clear();
    g.fillStyle(0x7a4a30, 1);
    g.beginPath();
    g.moveTo(TILE / 2, 4);
    g.lineTo(TILE - 6, 11);
    g.lineTo(TILE - 4, TILE / 2 + 5);
    g.lineTo(TILE - 10, TILE - 5);
    g.lineTo(10, TILE - 4);
    g.lineTo(4, TILE / 2);
    g.lineTo(7, 10);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, 0x4a2c1a, 1);
    g.strokePath();
    g.fillStyle(0x9c5a34, 1);
    g.fillCircle(TILE / 2 + 2, TILE / 2, 4);
    g.fillCircle(TILE / 2 - 6, TILE / 2 + 6, 3);
    g.generateTexture('object-scrap-metal-rusted', TILE, TILE);

    // Metal Floor (sim/rust.ts) -- walkable, paneled metal flooring rather
    // than a solid block: a flat fill, seam lines marking it off as floor
    // panels, and corner rivets echoing object-block-metal's own silhouette
    // so the two read as the same substance despite one being terrain.
    g.clear();
    g.fillStyle(0x8a95a0, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x6b7580, 0.9);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.lineBetween(0, TILE / 2, TILE, TILE / 2);
    g.lineBetween(TILE / 2, 0, TILE / 2, TILE);
    g.fillStyle(0x525d68, 1);
    [
      [5, 5],
      [TILE - 6, 5],
      [5, TILE - 6],
      [TILE - 6, TILE - 6],
    ].forEach(([rx, ry]) => g.fillCircle(rx, ry, 1.5));
    g.generateTexture('object-floor-metal', TILE, TILE);

    // Metal Floor, rusted -- same paneled floor, rust-brown with corrosion
    // blotches, same palette every other rusted metal texture uses.
    g.clear();
    g.fillStyle(0x7a4a30, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x5e3a26, 0.9);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.lineBetween(0, TILE / 2, TILE, TILE / 2);
    g.lineBetween(TILE / 2, 0, TILE / 2, TILE);
    g.fillStyle(0x9c5a34, 1);
    g.fillCircle(9, 10, 4);
    g.fillCircle(TILE - 10, TILE - 11, 3);
    g.fillStyle(0x4a2c1a, 1);
    [
      [5, 5],
      [TILE - 6, 5],
      [5, TILE - 6],
      [TILE - 6, TILE - 6],
    ].forEach(([rx, ry]) => g.fillCircle(rx, ry, 1.5));
    g.generateTexture('object-floor-metal-rusted', TILE, TILE);

    // Cabling (sim/cable.ts) -- sixteen pieces per palette, drawn in a loop
    // rather than by hand, because they are not sixteen different things: a
    // cable piece is a hub plus one arm per direction it is joined to, and
    // hand-drawing the combinations would be sixteen chances for two of them to
    // disagree about how wide a cable is.
    //
    // Deliberately flatter and darker than object-floor-metal beside it. The two
    // are both conductive terrain and it matters that they do not read as the
    // same substance: a Metal Floor tile arcs into whoever steps on a live one,
    // and this is insulated cable that never does. The sheath is what the player
    // has to be able to see.
    const cablePiece = (shape: string, sheath: number, core: number, edge: number): void => {
      g.clear();
      // The floor showing around it. A cable is pinned on top of the ground
      // rather than being the ground, so the tile is mostly not cable.
      g.fillStyle(0x2f2f33, 0.55);
      g.fillRect(0, 0, TILE, TILE);
      const mid = TILE / 2;
      const half = 5; // sheath half-width
      const dirs = [
        [0, -1, 'n'],
        [1, 0, 'e'],
        [0, 1, 's'],
        [-1, 0, 'w'],
      ] as const;
      const joined = dirs.filter(([, , letter]) => shape.includes(letter));
      // Sheath first, every arm and the junction box, then conductor over all of
      // it. Two passes rather than one arm at a time, so the conductor runs
      // *through* each junction instead of being capped at it -- drawn arm by arm
      // the same run reads as a dashed line rather than as one wire.
      g.fillStyle(sheath, 1);
      for (const [dx, dy] of joined) {
        // From the hub out to the tile edge, so two neighbouring pieces meet
        // flush with no seam between them.
        g.fillRect(dx < 0 ? 0 : mid - half, dy < 0 ? 0 : mid - half, dx === 0 ? half * 2 : mid + half, dy === 0 ? half * 2 : mid + half);
      }
      // The junction box, drawn even on an isolated piece -- a cable joined to
      // nothing is still a cable lying there, and a blank tile would read as the
      // object having vanished.
      g.fillRect(mid - half, mid - half, half * 2, half * 2);
      g.lineStyle(1, edge, 0.9);
      g.strokeRect(mid - half + 0.5, mid - half + 0.5, half * 2 - 1, half * 2 - 1);
      g.fillStyle(core, 1);
      for (const [dx, dy] of joined) {
        if (dx === 0) g.fillRect(mid - 1.5, dy < 0 ? 0 : mid, 3, mid);
        else g.fillRect(dx < 0 ? 0 : mid, mid - 1.5, mid, 3);
      }
      g.fillRect(mid - 1.5, mid - 1.5, 3, 3);
    };
    for (const shape of CABLE_SHAPES) {
      cablePiece(shape === 'none' ? '' : shape, 0x33383f, 0x8a95a0, 0x1d2126);
      g.generateTexture(`object-cable-${shape}`, TILE, TILE);
      // Corroded: the same silhouette in the rust-brown every other spoiled
      // metal in the world wears, so "this run is dead" reads the same way it
      // does on a block or a floor panel.
      cablePiece(shape === 'none' ? '' : shape, 0x4a3527, 0x9c5a34, 0x2a1a10);
      g.generateTexture(`object-cable-rusted-${shape}`, TILE, TILE);
    }

    // Circuit Breaker -- a blade switch in a ceramic housing, in four states:
    // the two switch positions crossed with the two corrosion stages. Drawn from
    // one function for the same reason the cable pieces are: they are not four
    // objects, they are one object with its blade up or down, and hand-drawing
    // the combinations is four chances for two of them to disagree about where
    // the hinge is.
    //
    // The open position is the whole silhouette -- a blade standing up out of
    // the housing with a visible gap under it -- because that is the state a
    // player has to read across a room to understand why the far end is dark.
    const breaker = (open: boolean, body: number, metal: number, edge: number): void => {
      g.clear();
      // Ceramic housing.
      g.fillStyle(body, 1);
      g.fillRect(4, 6, TILE - 8, TILE - 10);
      g.lineStyle(1, edge, 1);
      g.strokeRect(4.5, 6.5, TILE - 9, TILE - 11);
      // The two contacts the blade bridges, and the terminals either side.
      g.fillStyle(edge, 1);
      g.fillRect(7, TILE - 10, 5, 3);
      g.fillRect(TILE - 12, TILE - 10, 5, 3);
      g.fillStyle(metal, 1);
      g.fillRect(0, TILE - 10, 4, 3);
      g.fillRect(TILE - 4, TILE - 10, 4, 3);
      // The blade: lying across both contacts when closed, hinged up off the
      // right-hand one when open.
      g.fillStyle(metal, 1);
      if (open) {
        // Pivoted about the left contact, standing up and to the right.
        g.fillTriangle(9, TILE - 9, 12, TILE - 9, TILE - 8, 8);
        g.fillTriangle(12, TILE - 9, TILE - 10, 10, TILE - 8, 8);
      } else {
        g.fillRect(9, TILE - 11, TILE - 18, 4);
      }
      // The hinge, drawn in both states so the eye has a fixed point to read the
      // blade's angle against.
      g.fillStyle(edge, 1);
      g.fillCircle(10, TILE - 9, 2);
    };
    breaker(false, 0xe0d6c4, 0x8a95a0, 0x4a4e54);
    g.generateTexture('object-breaker', TILE, TILE);
    breaker(true, 0xe0d6c4, 0x8a95a0, 0x4a4e54);
    g.generateTexture('object-breaker-open', TILE, TILE);
    // Corroded: the same housing, the same blade, in the rust-brown every other
    // spoiled metal in the world wears.
    breaker(false, 0xbfae95, 0x9c5a34, 0x4a2c1a);
    g.generateTexture('object-breaker-rusted', TILE, TILE);
    breaker(true, 0xbfae95, 0x9c5a34, 0x4a2c1a);
    g.generateTexture('object-breaker-rusted-open', TILE, TILE);

    // Gate Terminal -- a box set into the stonework with a dark indicator panel
    // and cable entering from below, unlit.
    const terminalBase = () => {
      g.clear();
      g.fillStyle(0x4a4844, 1);
      g.fillRect(0, 0, TILE, TILE);
      g.lineStyle(1, 0x322f2b, 1);
      g.strokeRect(0.5, 0.5, TILE - 1, TILE - 1);
      g.fillStyle(0x6e737a, 1);
      g.fillRect(6, 5, TILE - 12, TILE - 13);
      g.lineStyle(1, 0x3a3e44, 1);
      g.strokeRect(6.5, 5.5, TILE - 13, TILE - 14);
      // The cable stub running into its underside, in Cabling's own palette --
      // the one visual promise the object makes about what feeds it.
      g.fillStyle(0x33383f, 1);
      g.fillRect(TILE / 2 - 4, TILE - 9, 8, 9);
      g.fillStyle(0x8a95a0, 1);
      g.fillRect(TILE / 2 - 1.5, TILE - 9, 3, 9);
    };
    terminalBase();
    g.fillStyle(0x2e2c28, 1);
    g.fillRect(9, 8, TILE - 18, 6);
    g.generateTexture('object-terminal', TILE, TILE);

    // ...and lit, its indicator panel carrying the same charge-blue every other
    // live conduit in the world uses.
    terminalBase();
    g.fillStyle(0x5aa8ee, 1);
    g.fillRect(9, 8, TILE - 18, 6);
    g.lineStyle(1, 0xbfe0ff, 0.9);
    g.strokeRect(8, 7, TILE - 16, 8);
    g.generateTexture('object-terminal-lit', TILE, TILE);

    // Electric Lamp -- a squat lamp on a metal stem with a cable running into
    // its base. Unlit: the glass is smoked and dark.
    const lampBase = () => {
      g.clear();
      g.fillStyle(0x33383f, 1);
      g.fillRect(TILE / 2 - 7, TILE - 6, 14, 5);
      g.fillStyle(0x6e737a, 1);
      g.fillRect(TILE / 2 - 2, 13, 4, TILE - 18);
      g.fillStyle(0x8a95a0, 1);
      g.fillRect(TILE / 2 - 8, 9, 16, 4);
    };
    lampBase();
    g.fillStyle(0x3d4046, 1);
    g.fillCircle(TILE / 2, 8, 6);
    g.lineStyle(1, 0x5a5f66, 1);
    g.strokeCircle(TILE / 2, 8, 6);
    g.generateTexture('object-lamp', TILE, TILE);

    lampBase();
    g.fillStyle(0x5aa8ee, 1);
    g.fillCircle(TILE / 2, 8, 6);
    g.fillStyle(0xdcefff, 1);
    g.fillCircle(TILE / 2, 8, 3);
    g.lineStyle(1, 0xbfe0ff, 0.6);
    g.strokeCircle(TILE / 2, 8, 8.5);
    g.generateTexture('object-lamp-lit', TILE, TILE);

    // Conveyor Belt -- a slatted belt set flush with the floor, drawn once per
    // heading in each of its two corrosion palettes. Eight textures from one
    // function for the same reason the cable pieces are: they are not eight
    // objects, they are one object pointing four ways in two conditions, and
    // hand-drawing the combinations is eight chances for two of them to disagree
    // about where the rollers are.
    //
    // The arrow is the whole point of the sprite. Which way a belt runs is the
    // one thing the player has to read off it from across the room, and unlike a
    // cable -- whose live and dead runs are deliberately drawn identically,
    // because the Lamp answers that -- a belt has nothing else to ask.
    const conveyor = (dir: Facing, bed: number, slat: number, arrow: number): void => {
      g.clear();
      // The bed, inset so the floor shows at the edges: a belt is pinned on top
      // of the ground, the same claim the cable's own tile makes.
      g.fillStyle(0x2f2f33, 0.55);
      g.fillRect(0, 0, TILE, TILE);
      g.fillStyle(bed, 1);
      g.fillRect(2, 2, TILE - 4, TILE - 4);
      g.lineStyle(1, 0x1d2126, 0.9);
      g.strokeRect(2.5, 2.5, TILE - 5, TILE - 5);
      // Slats across the direction of travel, so the belt reads as a belt even
      // before the arrow is found.
      const across = dir === 'left' || dir === 'right';
      g.fillStyle(slat, 1);
      for (let i = 6; i < TILE - 4; i += 6) {
        if (across) g.fillRect(i, 3, 2, TILE - 6);
        else g.fillRect(3, i, TILE - 6, 2);
      }
      // The arrow, pointing where the belt carries.
      const mid = TILE / 2;
      const tip = 6;
      g.fillStyle(arrow, 1);
      if (dir === 'right') g.fillTriangle(mid + tip, mid, mid - 3, mid - tip, mid - 3, mid + tip);
      else if (dir === 'left') g.fillTriangle(mid - tip, mid, mid + 3, mid - tip, mid + 3, mid + tip);
      else if (dir === 'up') g.fillTriangle(mid, mid - tip, mid - tip, mid + 3, mid + tip, mid + 3);
      else g.fillTriangle(mid, mid + tip, mid - tip, mid - 3, mid + tip, mid - 3);
    };
    for (const dir of DIRECTIONS) {
      conveyor(dir, 0x3b3f45, 0x585e66, 0xc9d2dc);
      g.generateTexture(`object-conveyor-${dir}`, TILE, TILE);
      // Seized: the same silhouette in the rust-brown every other spoiled metal
      // in the world wears, with the arrow gone dull -- it still says which way
      // the thing was built to run, and it no longer says it is going to.
      conveyor(dir, 0x4a3527, 0x6b4a33, 0x8a6a4a);
      g.generateTexture(`object-conveyor-rusted-${dir}`, TILE, TILE);
    }

    // Conveyor Console -- a waist-high control post with a reversing lever and
    // an indicator, cabling running into its foot. Deliberately drawn in the
    // Gate Terminal's palette with the Cabling stub the Terminal has: it *is* a
    // Terminal mechanically (a self-reading `conduit.feed` with a capacity of
    // its own), and a console that looked like a different kind of thing would
    // be the world lying about which system it belongs to.
    const consoleBase = () => {
      g.clear();
      // The post.
      g.fillStyle(0x4a4844, 1);
      g.fillRect(7, 6, TILE - 14, TILE - 10);
      g.lineStyle(1, 0x322f2b, 1);
      g.strokeRect(7.5, 6.5, TILE - 15, TILE - 11);
      // The head, angled toward the player.
      g.fillStyle(0x6e737a, 1);
      g.fillRect(4, 4, TILE - 8, 9);
      g.lineStyle(1, 0x3a3e44, 1);
      g.strokeRect(4.5, 4.5, TILE - 9, 8);
      // The reversing lever, thrown to one side -- the one moving part, and the
      // only thing on the object a player can act on.
      g.fillStyle(0x8a95a0, 1);
      g.fillRect(TILE - 11, 1, 3, 7);
      g.fillCircle(TILE - 9.5, 1.5, 2);
      // The cable stub into its foot, in Cabling's own palette: the one visual
      // promise the object makes about what feeds it.
      g.fillStyle(0x33383f, 1);
      g.fillRect(TILE / 2 - 4, TILE - 6, 8, 6);
      g.fillStyle(0x8a95a0, 1);
      g.fillRect(TILE / 2 - 1.5, TILE - 6, 3, 6);
    };
    consoleBase();
    g.fillStyle(0x2e2c28, 1);
    g.fillRect(8, 6, TILE - 16, 5);
    g.generateTexture('object-conveyor-console', TILE, TILE);

    // Powered: the same indicator in the charge-blue every other live conduit in
    // the world uses, which is what makes "this line is live" one colour across
    // the Terminal, the Lamp and this.
    consoleBase();
    g.fillStyle(0x5aa8ee, 1);
    g.fillRect(8, 6, TILE - 16, 5);
    g.lineStyle(1, 0xbfe0ff, 0.9);
    g.strokeRect(7, 5, TILE - 14, 7);
    g.generateTexture('object-conveyor-console-lit', TILE, TILE);

    // Corroded: dark indicator, rust-brown housing. There is no lit-and-corroded
    // pair, and there cannot be one -- rust stops it conducting, so a corroded
    // console's own network is only itself and it can never read charged.
    consoleBase();
    g.fillStyle(0x9c5a34, 0.85);
    g.fillRect(7, 6, TILE - 14, TILE - 10);
    g.fillStyle(0x4a2c1a, 1);
    g.fillRect(8, 6, TILE - 16, 5);
    g.generateTexture('object-conveyor-console-rusted', TILE, TILE);

    // Electric Gate -- the same mechanism as the Quarry Gate above wearing a
    // different door: two leaves meeting down the middle in a metal frame,
    // shut. Drawn so that the open state is unmistakably *this* door with the
    // leaves drawn back, rather than a second object.
    const electricGateFrame = () => {
      g.clear();
      g.fillStyle(0x3a3e44, 1);
      g.fillRect(0, 0, TILE, TILE);
      g.lineStyle(2, 0x24272c, 1);
      g.strokeRect(1, 1, TILE - 2, TILE - 2);
      // Head and sill rails, so the leaves read as running in a track.
      g.fillStyle(0x6e737a, 1);
      g.fillRect(1, 1, TILE - 2, 3);
      g.fillRect(1, TILE - 4, TILE - 2, 3);
    };
    const leaf = (x: number, w: number) => {
      g.fillStyle(0x8a95a0, 1);
      g.fillRect(x, 5, w, TILE - 10);
      g.lineStyle(1, 0x525d68, 1);
      g.strokeRect(x + 0.5, 5.5, w - 1, TILE - 11);
    };
    electricGateFrame();
    leaf(2, TILE / 2 - 2);
    leaf(TILE / 2, TILE / 2 - 2);
    // The seam the two leaves meet on, which is the whole silhouette.
    g.lineStyle(2, 0x24272c, 1);
    g.lineBetween(TILE / 2, 5, TILE / 2, TILE - 5);
    g.generateTexture('object-gate-electric', TILE, TILE);

    // Open: the leaves drawn back into the jambs, the middle clear.
    electricGateFrame();
    leaf(2, 5);
    leaf(TILE - 7, 5);
    g.generateTexture('object-gate-electric-open', TILE, TILE);

    // Sluice Gate (sim/queries.ts isSluice) -- the Electric Gate's mechanism
    // standing in a water course rather than a doorway. Four looks from two
    // facts crossed: whether the leaf is up, and whether the fluid has arrived.
    //
    // Drawn on the Aqueduct's silhouette rather than the gate's on purpose: what
    // the player has to read at a glance is "this is part of the channel", since
    // that is what decides both of the surprising rules (water crosses it, they
    // never can). The gate is what is *in* the race, not what the race is.
    const sluiceRace = (wet: boolean) => {
      g.clear();
      // The stone race: the aqueduct trough's masonry, the same greys, so a
      // channel built of both reads as one structure.
      g.fillStyle(0x8a8578, 1);
      g.fillRect(2, 8, TILE - 4, TILE - 12);
      g.lineStyle(2, 0x55534a, 1);
      g.strokeRect(2, 8, TILE - 4, TILE - 12);
      g.fillStyle(wet ? 0x3d7ab8 : 0x2a2620, 1);
      g.fillRect(5, 11, TILE - 10, TILE - 18);
    };
    // The winch gear the leaf runs in, at the head of the race -- present in all
    // four looks, because it is what says the leaf can move at all.
    const sluiceHousing = () => {
      g.fillStyle(0x6e737a, 1);
      g.fillRect(1, 3, TILE - 2, 6);
      g.lineStyle(1, 0x3a3e44, 1);
      g.strokeRect(1.5, 3.5, TILE - 3, 5);
    };

    // Shut and dry: the leaf down across an empty race.
    sluiceRace(false);
    g.fillStyle(0x8a95a0, 1);
    g.fillRect(6, 8, TILE - 12, TILE - 12);
    g.lineStyle(1, 0x525d68, 1);
    g.strokeRect(6.5, 8.5, TILE - 13, TILE - 13);
    sluiceHousing();
    g.generateTexture('object-sluice-shut', TILE, TILE);

    // Shut, with the fluid standing against it (water.dammedTexture). Water on
    // the upstream half only, held at the leaf -- the picture of the flow
    // arriving and stopping, which is the state a player has to be able to tell
    // from "nothing is coming" before they go looking upstream for a fault.
    sluiceRace(false);
    g.fillStyle(0x3d7ab8, 1);
    g.fillRect(5, 11, TILE / 2 - 5, TILE - 18);
    // The head of standing water piled at the leaf, brighter than the flow.
    g.fillStyle(0x9ec9ea, 0.9);
    g.fillRect(TILE / 2 - 3, 11, 3, TILE - 18);
    g.fillStyle(0x8a95a0, 1);
    g.fillRect(6, 8, TILE - 12, TILE - 12);
    g.lineStyle(1, 0x525d68, 1);
    g.strokeRect(6.5, 8.5, TILE - 13, TILE - 13);
    sluiceHousing();
    g.generateTexture('object-sluice-shut-dammed', TILE, TILE);

    // Open and dry (gate.openTexture): the leaf hauled up into its housing, the
    // race clear and empty. Deliberately the same empty channel the shut-and-dry
    // sprite shows, differing only in where the leaf is -- the two states this
    // pair distinguishes are "shut" and "open", and drawing anything else into
    // one of them would be the sprite making a claim the mechanism does not.
    sluiceRace(false);
    g.fillStyle(0x8a95a0, 1);
    g.fillRect(6, 1, TILE - 12, 7);
    g.lineStyle(1, 0x525d68, 1);
    g.strokeRect(6.5, 1.5, TILE - 13, 6);
    sluiceHousing();
    g.generateTexture('object-sluice-open', TILE, TILE);

    // Open and running (water.flowingTexture): leaf up, water crossing the tile
    // end to end. The flow lines run the length of the race, matching the
    // Flowing Aqueduct's, so a channel built of both reads as one current.
    sluiceRace(true);
    g.lineStyle(1, 0xa9d4f2, 0.8);
    g.lineBetween(7, 14, TILE - 7, 14);
    g.lineBetween(7, TILE - 14, TILE - 7, TILE - 14);
    g.fillStyle(0x8a95a0, 1);
    g.fillRect(6, 1, TILE - 12, 7);
    g.lineStyle(1, 0x525d68, 1);
    g.strokeRect(6.5, 1.5, TILE - 13, 6);
    sluiceHousing();
    g.generateTexture('object-sluice-flowing', TILE, TILE);

    // Wooden Floor (sim/rust.ts) -- walkable planked flooring, non-conductive
    // (unlike Metal Floor it never carries `conduit` at all): a warm wood
    // fill with plank-seam lines rather than object-floor-metal's paneled
    // grid, echoing object-block-wood's own grain treatment.
    g.clear();
    g.fillStyle(0x8a6a42, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x5e4526, 0.9);
    g.lineBetween(0, TILE / 3, TILE, TILE / 3);
    g.lineBetween(0, (2 * TILE) / 3, TILE, (2 * TILE) / 3);
    g.lineStyle(1, 0x6b4f2c, 0.6);
    g.lineBetween(6, 4, 6, TILE / 3);
    g.lineBetween(20, TILE / 3, 20, (2 * TILE) / 3);
    g.lineBetween(10, (2 * TILE) / 3, 10, TILE - 4);
    g.generateTexture('object-floor-wood', TILE, TILE);

    // Wooden Floor, rotten -- the same planked floor, gone soft and dark with
    // rot, same palette every other rotten wood texture uses.
    g.clear();
    g.fillStyle(0x5c4a34, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x362a1c, 0.9);
    g.lineBetween(0, TILE / 3, TILE, TILE / 3);
    g.lineBetween(0, (2 * TILE) / 3, TILE, (2 * TILE) / 3);
    g.fillStyle(0x2e2418, 0.9);
    g.fillCircle(8, 6, 2);
    g.fillCircle(TILE - 9, TILE / 3 + 6, 2.5);
    g.fillCircle(11, (2 * TILE) / 3 + 7, 2);
    g.generateTexture('object-floor-wood-rotten', TILE, TILE);

    // Lava (objectCatalog.ts) -- walkable hazard floor with no rust or charge
    // state of its own, so there is only ever the one look: a dark crust with
    // glowing cracks, distinct from Sand Floor's smooth molten pool since this
    // one never cools. As of 2026-08-09 what makes it dangerous is simply that
    // it is very hot (`thermalContact` at Energy 30) rather than a flat
    // `hazard.damage`, but nothing about how it is drawn changed.
    g.clear();
    g.fillStyle(0x3a0e05, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(2, 0xff6a1a, 0.9);
    g.lineBetween(3, 4, 12, 14);
    g.lineBetween(12, 14, 9, 24);
    g.lineBetween(12, 14, 22, 18);
    g.lineBetween(22, 18, TILE - 4, 10);
    g.lineBetween(22, 18, 20, TILE - 5);
    g.fillStyle(0xffb347, 0.9);
    g.fillCircle(12, 14, 2);
    g.fillCircle(22, 18, 2);
    g.generateTexture('object-floor-lava', TILE, TILE);

    // Scorched Floor (ThermalContactDef) -- hot *stone*, deliberately not the
    // lava crust above, which it used to share a texture with (2026-08-09).
    // The two shared one look until then, and were opposite rules at the time:
    // Lava billed once on the step onto it, where this bills for every tick you
    // linger. Both are `thermalContact` now (2026-08-09) and differ only in how
    // hot they are -- 30 against 10 -- but they still have to read differently
    // at a glance, because one is survivable indefinitely and the other kills
    // in three ticks. Flagstones with heat in the seams, not molten
    // rock: the surface is intact and grey-brown, and what glows is the gaps
    // between the slabs plus a low ember bloom, so it reads as "this floor is
    // hot" rather than "this floor is liquid".
    g.clear();
    g.fillStyle(0x6b5b52, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.fillStyle(0x7d6a5e, 1);
    g.fillRect(1, 1, TILE / 2 - 2, TILE / 2 - 2);
    g.fillRect(TILE / 2 + 1, TILE / 2 + 1, TILE / 2 - 2, TILE / 2 - 2);
    // The seams glow, not the stone.
    g.lineStyle(2, 0xd4622a, 0.85);
    g.lineBetween(0, TILE / 2, TILE, TILE / 2);
    g.lineBetween(TILE / 2, 0, TILE / 2, TILE);
    g.lineStyle(1, 0xff9a4a, 0.7);
    g.lineBetween(0, TILE / 2, TILE, TILE / 2);
    g.lineBetween(TILE / 2, 0, TILE / 2, TILE);
    // A little radiated heat over the slabs themselves, well short of lava's
    // bright branching cracks.
    g.fillStyle(0xc2551f, 0.28);
    g.fillCircle(TILE / 2, TILE / 2, 7);
    g.generateTexture('object-floor-scorched', TILE, TILE);

    // Heated Plate (ThermalContactDef) -- the same panelled silhouette as
    // Metal Floor above, since it *is* the same substance, but banked with
    // heat instead of carrying a charge. Split off `object-floor-metal`
    // (2026-08-09) for the same reason Scorched Floor left the lava crust:
    // one look was standing for two unrelated mechanisms, and here they are
    // not even the same concept -- a Metal Floor is a Charge hazard, this is
    // an Energy reservoir you can Transfer out of. Rivets and seams are kept
    // identical so the two still read as one material; only the temperature
    // of the palette differs, cool blue-grey against heat-soaked copper.
    g.clear();
    g.fillStyle(0x9c6a4a, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0x7b4c31, 0.9);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.lineBetween(0, TILE / 2, TILE, TILE / 2);
    g.lineBetween(TILE / 2, 0, TILE / 2, TILE);
    // The heat itself, pooled in the middle of the plate rather than at the
    // seams -- the plate is the thing that is hot, not the gaps.
    g.fillStyle(0xd8813f, 0.55);
    g.fillCircle(TILE / 2, TILE / 2, 9);
    g.fillStyle(0xf0a860, 0.4);
    g.fillCircle(TILE / 2, TILE / 2, 5);
    g.fillStyle(0x5f3c26, 1);
    [
      [5, 5],
      [TILE - 6, 5],
      [5, TILE - 6],
      [TILE - 6, TILE - 6],
    ].forEach(([rx, ry]) => g.fillCircle(rx, ry, 1.5));
    g.generateTexture('object-plate-heated', TILE, TILE);

    // Sand Floor (sim/sand.ts) -- walkable packed sand, pale tan with a
    // scatter of grain-flecks, the flat-floor treatment of Sand Block's own
    // palette (no seam/panel lines -- loose sand has no planks or panels).
    g.clear();
    g.fillStyle(0xd8c496, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.fillStyle(0xb8a06c, 0.7);
    [
      [5, 6],
      [TILE - 7, 5],
      [9, TILE - 8],
      [TILE - 6, TILE - 9],
      [TILE / 2 - 2, TILE / 2],
      [TILE / 2 + 6, TILE / 2 - 6],
    ].forEach(([rx, ry]) => g.fillCircle(rx, ry, 1.5));
    g.generateTexture('object-floor-sand', TILE, TILE);

    // Sand Floor, molten -- a flat pool of molten glass rather than Sand
    // Block's slumped blob (it's terrain, already flat), orange-red with a
    // brighter core pool.
    g.clear();
    g.fillStyle(0x8a2e0f, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.fillStyle(0xd94b1f, 1);
    g.fillEllipse(TILE / 2, TILE / 2, TILE / 2 - 5, TILE / 2 - 5);
    g.fillStyle(0xf0a83f, 0.9);
    g.fillEllipse(TILE / 2, TILE / 2, 6, 5);
    g.generateTexture('object-floor-glass-molten', TILE, TILE);

    // Sand Floor, glass -- flat, smooth, pale translucent blue-white, a
    // couple of highlight streaks reading as a reflective cooled surface.
    g.clear();
    g.fillStyle(0xbfe0e6, 0.85);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0xf0fbfc, 0.7);
    g.lineBetween(4, TILE - 8, TILE - 8, 4);
    g.lineBetween(TILE - 6, TILE - 4, TILE - 4, TILE - 6);
    g.generateTexture('object-floor-glass', TILE, TILE);

    // Sand Floor, quartz -- flat, clear white-violet, angular facet lines
    // radiating from the tile's own center, the floor-tile treatment of
    // Sand Block's quartz lattice.
    g.clear();
    g.fillStyle(0xe8e0f5, 0.9);
    g.fillRect(0, 0, TILE, TILE);
    g.lineStyle(1, 0xffffff, 0.9);
    g.lineBetween(TILE / 2, 2, TILE / 2, TILE - 2);
    g.lineBetween(2, TILE / 2, TILE - 2, TILE / 2);
    g.lineBetween(5, 5, TILE - 5, TILE - 5);
    g.lineBetween(TILE - 5, 5, 5, TILE - 5);
    g.generateTexture('object-floor-quartz', TILE, TILE);

    // Dirt Path -- purely aesthetic floor decoration (no mechanism at all,
    // unlike every other floor tile above): packed earth, warm brown, with
    // a scatter of small darker pebbles rather than the sharp grain-flecks
    // Sand Floor uses -- reads as trodden ground, not desert.
    g.clear();
    g.fillStyle(0x8a6a4a, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.fillStyle(0x6e5238, 0.7);
    [
      [7, 9, 2],
      [TILE - 8, 7, 1.5],
      [10, TILE - 9, 1.5],
      [TILE - 7, TILE - 8, 2],
      [TILE / 2, TILE / 2 + 3, 1.5],
    ].forEach(([rx, ry, r]) => g.fillCircle(rx, ry, r));
    g.generateTexture('object-floor-dirt', TILE, TILE);

    // Cobblestone -- individual rounded stones (small offset circles, two
    // alternating grays) with dark mortar showing through the gaps, distinct
    // from Metal Floor's own seamed panels or Moss Stone's single flagstone.
    g.clear();
    g.fillStyle(0x4a4742, 1);
    g.fillRect(0, 0, TILE, TILE);
    const cobbles: [number, number, number, number][] = [
      [6, 6, 6, 0x8a867e],
      [18, 5, 5, 0x7d7970],
      [27, 8, 5, 0x8a867e],
      [5, 17, 5, 0x7d7970],
      [16, 17, 6, 0x8a867e],
      [27, 19, 5, 0x7d7970],
      [7, 27, 5, 0x8a867e],
      [19, 27, 6, 0x7d7970],
    ];
    cobbles.forEach(([cx, cy, r, color]) => {
      g.fillStyle(color, 1);
      g.fillCircle(cx, cy, r);
    });
    g.generateTexture('object-floor-cobblestone', TILE, TILE);

    // Red Carpet -- a deep red runner with a darker woven border, a lighter
    // inner field, and a couple of dust/wear marks -- the one floor texture
    // in this cluster that reads as fabric rather than a mineral surface.
    g.clear();
    g.fillStyle(0x5c1e1e, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.fillStyle(0x7a2a2a, 1);
    g.fillRect(3, 3, TILE - 6, TILE - 6);
    g.lineStyle(1, 0x4a1717, 0.8);
    g.strokeRect(3, 3, TILE - 6, TILE - 6);
    g.fillStyle(0x4a1717, 0.5);
    g.fillCircle(9, 22, 2.5);
    g.fillCircle(23, 10, 2);
    g.generateTexture('object-floor-carpet-red', TILE, TILE);

    // Moss Stone -- a single gray flagstone (unlike Cobblestone's many small
    // ones) with irregular green moss patches creeping across it, damp
    // rather than dry.
    g.clear();
    g.fillStyle(0x6b6862, 1);
    g.fillRect(1, 1, TILE - 2, TILE - 2);
    g.lineStyle(1, 0x4a4742, 0.9);
    g.strokeRect(1, 1, TILE - 2, TILE - 2);
    g.fillStyle(0x5a7a44, 0.85);
    g.fillEllipse(9, 8, 7, 5);
    g.fillEllipse(TILE - 9, TILE - 10, 8, 6);
    g.fillStyle(0x4a6838, 0.85);
    g.fillEllipse(TILE - 10, 9, 4, 3);
    g.fillEllipse(8, TILE - 9, 5, 4);
    g.generateTexture('object-floor-moss', TILE, TILE);

    // Tile -- glazed ceramic, a clean two-tone diamond grid with thin grout
    // lines, the one floor texture here that reads as built/maintained
    // rather than found or worn.
    g.clear();
    g.fillStyle(0xd8cdb8, 1);
    g.fillRect(0, 0, TILE, TILE);
    g.fillStyle(0xc0b39a, 1);
    g.fillTriangle(TILE / 2, 0, TILE, TILE / 2, TILE / 2, TILE);
    g.fillTriangle(TILE / 2, 0, 0, TILE / 2, TILE / 2, TILE);
    g.lineStyle(1, 0x9c8f76, 0.9);
    g.strokeRect(0, 0, TILE, TILE);
    g.lineBetween(TILE / 2, 0, TILE, TILE / 2);
    g.lineBetween(TILE, TILE / 2, TILE / 2, TILE);
    g.lineBetween(TILE / 2, TILE, 0, TILE / 2);
    g.lineBetween(0, TILE / 2, TILE / 2, 0);
    g.generateTexture('object-floor-tile', TILE, TILE);

    // Grunt -- the first mob (see rune-lab/src/sim/mob.ts), and so the first
    // texture in this file that has to read as *alive* rather than as terrain,
    // masonry or a fixture. Three things do that work, and none of them is
    // detail: a body that overhangs its own footprint (nothing built here does),
    // legs that are visibly too many and too thin for it, and two eyes that face
    // out of the screen. Deliberately a silhouette rather than a portrait --
    // every other sprite here is a flat-lit shape at 32px and a carefully
    // rendered monster among them would read as a different game's asset.
    //
    // Bruise-purple over the world's greys and browns, a hue nothing else in the
    // vocabulary uses, because the one thing a player must never have to look
    // twice at is whether the thing in the corridor moves.
    g.clear();
    // Legs first, so the body sits over their tops -- six thin ones, splayed
    // unevenly, none of them a mirror of another.
    g.lineStyle(2, 0x2b1c33, 1);
    g.lineBetween(10, 20, 5, TILE - 3);
    g.lineBetween(13, 21, 11, TILE - 2);
    g.lineBetween(19, 21, 21, TILE - 2);
    g.lineBetween(22, 20, TILE - 5, TILE - 3);
    g.lineBetween(9, 18, 3, 25);
    g.lineBetween(23, 18, TILE - 3, 25);
    // The body: a low, wide hunch, wider than it is tall.
    g.fillStyle(0x6b3f7a, 1);
    g.fillEllipse(TILE / 2, 16, 25, 18);
    // A darker back ridge, offset from centre so it does not read as symmetric.
    g.fillStyle(0x4a2a58, 1);
    g.fillEllipse(TILE / 2 + 1, 11, 18, 8);
    // Two eyes, pale and pupilled, set wide and low on the front of the hunch --
    // the whole reason this reads as facing the player rather than as a rock.
    g.fillStyle(0xf2e7b8, 1);
    g.fillCircle(12, 17, 3.5);
    g.fillCircle(21, 17, 3.5);
    g.fillStyle(0x1a1020, 1);
    g.fillCircle(12, 17, 1.6);
    g.fillCircle(21, 17, 1.6);
    // A short mouth line under them with two teeth showing.
    g.fillStyle(0x2b1c33, 1);
    g.fillRect(12, 22, 9, 2);
    g.fillStyle(0xf2e7b8, 1);
    g.fillTriangle(14, 24, 16, 24, 15, 26);
    g.fillTriangle(18, 24, 20, 24, 19, 26);
    g.generateTexture('object-mob-grunt', TILE, TILE);

    // Swarmer -- a Grunt at half the interval and, deliberately, the same
    // creature seen small: same hunch, same two-eyed face, same too-many legs.
    // What differs is what *speed* looks like -- a narrower body carried higher
    // on longer legs, and a hot rust-red where the Grunt is bruise-purple, so a
    // room holding both reads as one thing and a faster one rather than as two
    // unrelated monsters. The family resemblance is the point: `physics.mob`
    // says a Swarmer is a Grunt with one number changed, and the sprite should
    // not claim more difference than the simulation has.
    g.clear();
    g.lineStyle(2, 0x3a1712, 1);
    g.lineBetween(11, 17, 4, TILE - 2);
    g.lineBetween(14, 18, 10, TILE - 1);
    g.lineBetween(18, 18, 22, TILE - 1);
    g.lineBetween(21, 17, TILE - 4, TILE - 2);
    g.lineBetween(10, 15, 2, 22);
    g.lineBetween(22, 15, TILE - 2, 22);
    g.fillStyle(0xa8452a, 1);
    g.fillEllipse(TILE / 2, 14, 19, 13);
    g.fillStyle(0x7a2c18, 1);
    g.fillEllipse(TILE / 2 + 1, 10, 13, 6);
    g.fillStyle(0xf2e7b8, 1);
    g.fillCircle(13, 15, 2.8);
    g.fillCircle(20, 15, 2.8);
    g.fillStyle(0x1a1020, 1);
    g.fillCircle(13, 15, 1.3);
    g.fillCircle(20, 15, 1.3);
    g.fillStyle(0x3a1712, 1);
    g.fillRect(13, 19, 7, 2);
    g.generateTexture('object-mob-swarmer', TILE, TILE);

    // Drone Mage, five flavours -- one drawing, one accent colour, exactly as
    // the five kinds are one `physics.mob` row and five `mob.projectile` values
    // (rune-lab/src/sim/mob.ts). Anything a Fire Mage and an Ice Mage do not
    // share mechanically is the colour of what they throw, so the colour is all
    // the sprite is allowed to change: two objects a player cannot tell apart
    // must not behave differently, and the converse holds just as hard.
    //
    // Read against the Grunt family: no legs at all (it hovers, hem tapering to
    // a point above the floor), an upright rather than a hunched silhouette, and
    // a hood with no eyes in it -- a shadowed face reads as a thing that does
    // something to you at range, where the Grunt's stare reads as a thing that
    // arrives. The orb is what carries the element, held out in front.
    const droneMageTexture = (key: string, robe: number, trim: number, orb: number) => {
      g.clear();
      // Hem: a tapered robe with a ragged bottom edge, floating clear of the
      // tile's baseline so it reads as hovering rather than standing.
      g.fillStyle(robe, 1);
      g.beginPath();
      g.moveTo(TILE / 2, 4);
      g.lineTo(TILE - 7, TILE - 6);
      g.lineTo(TILE / 2 + 3, TILE - 4);
      g.lineTo(TILE / 2, TILE - 7);
      g.lineTo(TILE / 2 - 3, TILE - 4);
      g.lineTo(7, TILE - 6);
      g.closePath();
      g.fillPath();
      // Trim down the front seam, the element's darker shade.
      g.lineStyle(2, trim, 1);
      g.lineBetween(TILE / 2, 7, TILE / 2, TILE - 8);
      // The hood: a cowl over a face that is only shadow.
      g.fillStyle(trim, 1);
      g.beginPath();
      g.moveTo(TILE / 2, 2);
      g.lineTo(TILE / 2 + 7, 13);
      g.lineTo(TILE / 2 - 7, 13);
      g.closePath();
      g.fillPath();
      g.fillStyle(0x120c18, 1);
      g.fillEllipse(TILE / 2, 10, 8, 6);
      // The orb, held out to one side, with a pale core so it reads as lit
      // rather than painted -- the same "bright core inside a colour" the altar
      // pillars and the Portal already use.
      g.fillStyle(orb, 0.55);
      g.fillCircle(TILE - 7, 19, 6);
      g.fillStyle(orb, 1);
      g.fillCircle(TILE - 7, 19, 4);
      g.fillStyle(0xfff6df, 0.9);
      g.fillCircle(TILE - 8, 18, 1.6);
      g.generateTexture(key, TILE, TILE);
    };
    // The four spell colours are the ones their own FX textures already use --
    // fx-flame's orange, fx-frost's ice-blue, fx-bolt's yellow, fx-shock's pale
    // violet -- so a mage is coloured by what comes out of it. Decay's green is
    // new here for the same reason fx-decay is: Project·Entropy has no look yet.
    droneMageTexture('object-mob-mage-fire', 0x8a3418, 0x5c1f0c, 0xf0a83f);
    droneMageTexture('object-mob-mage-ice', 0x2a5c74, 0x173d52, 0xafd8e6);
    droneMageTexture('object-mob-mage-charge', 0x6e5a12, 0x463809, 0xf5e06a);
    droneMageTexture('object-mob-mage-decay', 0x2f5c2a, 0x1b3a18, 0x7ad46a);
    droneMageTexture('object-mob-mage-force', 0x4a4470, 0x2c2848, 0xc9c2f5);

    // Mage Apprentice -- the same hovering silhouette, undyed: a plain grey
    // robe, because it is the one mage that has not committed to an element.
    // Its orb says so too, four coloured pips circling a pale centre rather
    // than one colour, which is exactly what `pickProjectile` does with it.
    // Smaller than a Drone Mage, which is the only other thing "apprentice"
    // could honestly mean in a sprite.
    g.clear();
    g.fillStyle(0x6a6558, 1);
    g.beginPath();
    g.moveTo(TILE / 2, 7);
    g.lineTo(TILE - 9, TILE - 6);
    g.lineTo(TILE / 2 + 3, TILE - 4);
    g.lineTo(TILE / 2, TILE - 7);
    g.lineTo(TILE / 2 - 3, TILE - 4);
    g.lineTo(9, TILE - 6);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, 0x494435, 1);
    g.lineBetween(TILE / 2, 10, TILE / 2, TILE - 8);
    g.fillStyle(0x494435, 1);
    g.beginPath();
    g.moveTo(TILE / 2, 5);
    g.lineTo(TILE / 2 + 6, 14);
    g.lineTo(TILE / 2 - 6, 14);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x120c18, 1);
    g.fillEllipse(TILE / 2, 12, 7, 5);
    // The undecided orb: a pale centre ringed by the four brightest of the five
    // spell colours (decay's green reads too close to the robe at this size to
    // earn its pip, and four is already the idea).
    g.fillStyle(0xe8e2d2, 0.9);
    g.fillCircle(TILE - 8, 20, 4.5);
    g.fillStyle(0xf0a83f, 1);
    g.fillCircle(TILE - 8, 17, 1.6);
    g.fillStyle(0xafd8e6, 1);
    g.fillCircle(TILE - 5, 20, 1.6);
    g.fillStyle(0xf5e06a, 1);
    g.fillCircle(TILE - 8, 23, 1.6);
    g.fillStyle(0xc9c2f5, 1);
    g.fillCircle(TILE - 11, 20, 1.6);
    g.generateTexture('object-mob-mage-apprentice', TILE, TILE);

    // Archer -- the one mob whose *movement* rule differs (it holds a distance),
    // so it is the one that should read differently at a glance. A standing
    // figure rather than a hovering robe or a low hunch, and the whole left half
    // of the tile given to a drawn bow: a shape no other sprite in this file has,
    // pointing the way a thing that keeps its distance ought to look.
    g.clear();
    // The bow: an arc with a taut string, drawn as two strokes so it reads at
    // 32px rather than dissolving into a smudge.
    g.lineStyle(2, 0x7a5a2e, 1);
    g.beginPath();
    g.arc(9, TILE / 2, 11, -Math.PI / 2.2, Math.PI / 2.2, false);
    g.strokePath();
    g.lineStyle(1, 0xd8cba8, 0.9);
    g.lineBetween(13, 8, 13, TILE - 8);
    // A nocked arrow, pointing the way the bow does.
    g.lineStyle(1, 0x9c8f76, 1);
    g.lineBetween(13, TILE / 2, 24, TILE / 2);
    g.fillStyle(0xd8cba8, 1);
    g.fillTriangle(24, TILE / 2 - 2, 24, TILE / 2 + 2, 28, TILE / 2);
    // The archer: a hooded body in forest colours, standing side-on behind it.
    g.fillStyle(0x3f5236, 1);
    g.fillEllipse(19, 20, 12, 16);
    g.fillStyle(0x2c3a26, 1);
    g.fillEllipse(19, 12, 10, 9);
    g.fillStyle(0x120c18, 1);
    g.fillEllipse(17, 12, 5, 4);
    g.generateTexture('object-mob-archer', TILE, TILE);

    // Project·Energy's flame FX -- a purely visual overlay (see
    // animateProjectFx), layered like the Candle's flame but scaled to
    // fill most of a tile: a deep-orange outer tongue, a brighter core.
    g.clear();
    g.fillStyle(0xd94b1f, 1);
    g.fillTriangle(TILE / 2, 2, 5, TILE - 4, TILE - 5, TILE - 4);
    g.fillStyle(0xf0a83f, 1);
    g.fillTriangle(TILE / 2, 9, 10, TILE - 4, TILE - 10, TILE - 4);
    g.fillStyle(0xffe38a, 1);
    g.fillTriangle(TILE / 2, 17, TILE / 2 - 6, TILE - 4, TILE / 2 + 6, TILE - 4);
    g.generateTexture('fx-flame', TILE, TILE);

    // Project·Energy·Reverse's cold-ray FX -- a cold-ray, not a second
    // heat-ray (see applyProjectEffect's own comment on why Reverse
    // subtracts here instead of adding): a compact six-armed snowflake
    // instead of the flame's triangular tongues, one arm per hexagonal
    // direction (the way frost crystals actually branch), a soft pale-cyan
    // glow underneath a bright icy-white core, each arm carrying a short
    // side-tick so it reads unmistakably as ice rather than another bolt.
    // Same ice palette room4's Ice Block already established (0xafd8e6/
    // 0xe8f8ff), not a new one invented for this.
    g.clear();
    {
      const cx = TILE / 2;
      const cy = TILE / 2;
      const armLen = TILE / 2 - 3;
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        const ex = cx + Math.cos(angle) * armLen;
        const ey = cy + Math.sin(angle) * armLen;
        g.lineStyle(4, 0x9fd0e0, 0.45);
        g.lineBetween(cx, cy, ex, ey);
      }
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        const ex = cx + Math.cos(angle) * armLen;
        const ey = cy + Math.sin(angle) * armLen;
        g.lineStyle(2, 0xe8f8ff, 0.95);
        g.lineBetween(cx, cy, ex, ey);
        const midX = cx + Math.cos(angle) * armLen * 0.55;
        const midY = cy + Math.sin(angle) * armLen * 0.55;
        const branchLen = 4;
        const branch1 = angle + Math.PI / 3;
        const branch2 = angle - Math.PI / 3;
        g.lineBetween(midX, midY, midX + Math.cos(branch1) * branchLen, midY + Math.sin(branch1) * branchLen);
        g.lineBetween(midX, midY, midX + Math.cos(branch2) * branchLen, midY + Math.sin(branch2) * branchLen);
      }
    }
    g.generateTexture('fx-frost', TILE, TILE);

    // Project·Charge's lightning-bolt FX -- same purely-visual role as the
    // flame above (see animateProjectFx), a jagged zigzag instead of a flicker:
    // a soft cyan glow underneath a bright white core, the same core-and-glow
    // layering the flame uses, just a lightning shape instead of fire.
    g.clear();
    g.lineStyle(6, 0x8ad9ff, 0.5);
    g.beginPath();
    g.moveTo(TILE / 2, 1);
    g.lineTo(TILE / 2 - 8, TILE * 0.4);
    g.lineTo(TILE / 2 + 6, TILE * 0.55);
    g.lineTo(TILE / 2 - 6, TILE - 1);
    g.strokePath();
    g.lineStyle(2, 0xffffff, 0.95);
    g.beginPath();
    g.moveTo(TILE / 2, 1);
    g.lineTo(TILE / 2 - 8, TILE * 0.4);
    g.lineTo(TILE / 2 + 6, TILE * 0.55);
    g.lineTo(TILE / 2 - 6, TILE - 1);
    g.strokePath();
    g.generateTexture('fx-bolt', TILE, TILE);

    // Project·Momentum's force-blast FX -- same purely-visual role as the
    // flame/bolt above, an 8-point starburst instead of fire or lightning:
    // symmetric on purpose, since (unlike a flame licking upward or a bolt
    // zigzagging down) this needs to read the same whether the beam that
    // spawned it is traveling up, down, left, or right, or radiating outward
    // in every direction at once.
    g.clear();
    g.lineStyle(5, 0x8a7a3a, 0.5);
    g.lineBetween(TILE / 2, 3, TILE / 2, TILE - 3);
    g.lineBetween(3, TILE / 2, TILE - 3, TILE / 2);
    g.lineBetween(7, 7, TILE - 7, TILE - 7);
    g.lineBetween(TILE - 7, 7, 7, TILE - 7);
    g.lineStyle(2, 0xe0c030, 0.95);
    g.lineBetween(TILE / 2, 5, TILE / 2, TILE - 5);
    g.lineBetween(5, TILE / 2, TILE - 5, TILE / 2);
    g.generateTexture('fx-shock', TILE, TILE);

    // Project·Entropy's look -- drawn now, cast by nobody. The expression has no
    // world effect yet (see 07_Open_Questions.md), so the only thing that throws
    // this today is a Decay Drone Mage's aesthetic projection; it is drawn here
    // beside the other four rather than off in mob territory precisely because
    // it is the *cast's* look, waiting for the cast.
    //
    // Deliberately not another tongue or bolt: entropy is not a ray, it is
    // something spreading and eating. A ragged olive cloud, a darker core, and
    // a scatter of holes punched back out of it so the shape reads as breaking
    // down rather than burning.
    g.clear();
    g.fillStyle(0x4e7a34, 0.75);
    g.fillCircle(TILE / 2 - 4, TILE / 2 - 3, 9);
    g.fillCircle(TILE / 2 + 5, TILE / 2 + 2, 8);
    g.fillCircle(TILE / 2 - 1, TILE / 2 + 6, 6);
    g.fillStyle(0x7ad46a, 0.85);
    g.fillCircle(TILE / 2 - 2, TILE / 2 - 1, 5);
    g.fillCircle(TILE / 2 + 4, TILE / 2 + 3, 3.5);
    // The holes: the tile's own darkness showing through, which is what makes
    // it read as rot rather than as a green flame.
    g.fillStyle(0x14180f, 0.9);
    g.fillCircle(TILE / 2 + 7, TILE / 2 - 6, 2.2);
    g.fillCircle(TILE / 2 - 8, TILE / 2 + 5, 1.8);
    g.fillCircle(TILE / 2 + 1, TILE / 2 - 7, 1.4);
    g.generateTexture('fx-decay', TILE, TILE);

    // An Archer's arrow -- the one projectile in this file with a nose, which is
    // why the `project-fx` event it rides on carries `oriented` and the renderer
    // turns it: drawn pointing right, rotated to whichever way it was actually
    // loosed. A physical object rather than a projection of a rune, and drawn
    // like one -- a shaft, a pale head, two fletches, no glow anywhere.
    g.clear();
    g.lineStyle(2, 0x8a6a3a, 1);
    g.lineBetween(6, TILE / 2, TILE - 8, TILE / 2);
    g.fillStyle(0xd8d2c4, 1);
    g.fillTriangle(TILE - 10, TILE / 2 - 4, TILE - 10, TILE / 2 + 4, TILE - 3, TILE / 2);
    g.fillStyle(0xb8442e, 1);
    g.fillTriangle(6, TILE / 2 - 1, 11, TILE / 2 - 1, 7, TILE / 2 - 5);
    g.fillTriangle(6, TILE / 2 + 1, 11, TILE / 2 + 1, 7, TILE / 2 + 5);
    g.generateTexture('fx-arrow', TILE, TILE);

    // Marked-source targeting reticle -- four corner brackets in the same gold
    // as the "Source: ..." HUD text, drawn on a canvas a bit larger than a tile
    // so it visibly surrounds the marked object rather than sitting flush on it.
    g.clear();
    g.lineStyle(3, 0xc9a24b, 1);
    const reticleSize = TILE + 8;
    const bracket = 9;
    g.beginPath();
    g.moveTo(0, bracket);
    g.lineTo(0, 0);
    g.lineTo(bracket, 0);
    g.strokePath();
    g.beginPath();
    g.moveTo(reticleSize - bracket, 0);
    g.lineTo(reticleSize, 0);
    g.lineTo(reticleSize, bracket);
    g.strokePath();
    g.beginPath();
    g.moveTo(0, reticleSize - bracket);
    g.lineTo(0, reticleSize);
    g.lineTo(bracket, reticleSize);
    g.strokePath();
    g.beginPath();
    g.moveTo(reticleSize - bracket, reticleSize);
    g.lineTo(reticleSize, reticleSize);
    g.lineTo(reticleSize, reticleSize - bracket);
    g.strokePath();
    g.generateTexture('fx-target', reticleSize, reticleSize);

    // Player aura -- a soft, layered radial falloff drawn in plain white so
    // it can be freely tinted per-concept at runtime (see refreshPlayerAura),
    // one texture shared by both the Energy and Charge halos rather than a
    // separate hand-drawn glow per concept. Built from concentric
    // low-alpha circles, largest (near-transparent) first and smallest
    // (most opaque) last, since Phaser's Graphics has no real blur filter.
    g.clear();
    const auraSize = TILE * 2;
    const auraCenter = auraSize / 2;
    for (let r = auraCenter; r > 0; r -= 3) {
      g.fillStyle(0xffffff, 0.1 * (1 - r / auraCenter));
      g.fillCircle(auraCenter, auraCenter, r);
    }
    g.generateTexture('fx-player-aura', auraSize, auraSize);

    // Unknown Rune Circle (room10) -- the room's dormant altar target,
    // walkable like room0's Faded Ruin Circle, but deliberately NOT that
    // same ring-plus-6-even-tick-marks shape (or the room10 predecessor that
    // reused it): 8 unevenly spaced, unevenly sized tick marks in a cold
    // pale violet instead, reading as a real inscription that just isn't in
    // a language the player has cracked -- not the "same pattern, now lit"
    // motif at all. Swapped for object-portal once every altar pillar below
    // is filled (see checkRuneAltars/activatesInto).
    g.clear();
    g.lineStyle(2, 0xa8a0c8, 0.85);
    g.strokeCircle(TILE / 2, TILE / 2, TILE / 2 - 5);
    g.lineStyle(1, 0xc8c0e0, 0.75);
    [10, 55, 95, 140, 200, 250, 300, 335].forEach((deg, i) => {
      const angle = (deg * Math.PI) / 180;
      const r1 = TILE / 2 - 5;
      const r2 = TILE / 2 - (7 + (i % 3) * 2);
      const cx = TILE / 2;
      const cy = TILE / 2;
      g.lineBetween(cx + Math.cos(angle) * r1, cy + Math.sin(angle) * r1, cx + Math.cos(angle) * r2, cy + Math.sin(angle) * r2);
    });
    g.generateTexture('object-rune-circle-unknown', TILE, TILE);

    // Room10's 4 rune altar pillars -- the same squat stone-column shape as
    // room9's standing pillar, with an accent-colored hollow carved into the
    // face instead of a plain block: dark and empty-looking until filled
    // (see onAltarItemPlaced), then a bright glowing version of the same
    // color, the same "dark socket -> glowing socket" shape room9's gate
    // pillars already use for litTexture.
    const altarPillarTexture = (key: string, accent: number, filled: boolean) => {
      g.clear();
      g.fillStyle(0x5c5a56, 1);
      g.fillRect(8, 2, TILE - 16, TILE - 4);
      g.lineStyle(1, 0x403e3a, 1);
      g.strokeRect(8, 2, TILE - 16, TILE - 4);
      g.fillStyle(0x6b6862, 1);
      g.fillRect(6, 2, TILE - 12, 3);
      g.fillRect(6, TILE - 6, TILE - 12, 3);
      if (filled) {
        g.fillStyle(accent, 1);
        g.fillCircle(TILE / 2, TILE / 2, 5);
        g.lineStyle(1, 0xffffff, 0.8);
        g.strokeCircle(TILE / 2, TILE / 2, 7);
      } else {
        g.fillStyle(0x201f1c, 1);
        g.fillCircle(TILE / 2, TILE / 2, 5);
        g.lineStyle(1, accent, 0.6);
        g.strokeCircle(TILE / 2, TILE / 2, 5);
      }
      g.generateTexture(key, TILE, TILE);
    };
    altarPillarTexture('object-pillar-altar-red', 0xd23c3c, false);
    altarPillarTexture('object-pillar-altar-red-lit', 0xd23c3c, true);
    altarPillarTexture('object-pillar-altar-green', 0x3ca85c, false);
    altarPillarTexture('object-pillar-altar-green-lit', 0x3ca85c, true);
    altarPillarTexture('object-pillar-altar-yellow', 0xd2b93c, false);
    altarPillarTexture('object-pillar-altar-yellow-lit', 0xd2b93c, true);
    altarPillarTexture('object-pillar-altar-blue', 0x3c7cd2, false);
    altarPillarTexture('object-pillar-altar-blue-lit', 0x3c7cd2, true);

    // Portal (room10) -- the demo's actual endpoint. Concentric rings
    // (Graphics has no real gradient fill) reading as a glow: a dark violet
    // outer ring, a brighter violet middle, a near-white core, so it draws
    // the eye as clearly "different" from every other object in the game.
    g.clear();
    g.fillStyle(0x1a1030, 1);
    g.fillCircle(TILE / 2, TILE / 2, TILE / 2 - 2);
    g.fillStyle(0x6a3fb0, 0.85);
    g.fillCircle(TILE / 2, TILE / 2, TILE / 2 - 7);
    g.fillStyle(0xb08fe0, 0.85);
    g.fillCircle(TILE / 2, TILE / 2, TILE / 2 - 12);
    g.fillStyle(0xf0e8ff, 0.9);
    g.fillCircle(TILE / 2, TILE / 2, 4);
    g.lineStyle(1, 0xe0d0ff, 0.6);
    g.strokeCircle(TILE / 2, TILE / 2, TILE / 2 - 2);
    g.generateTexture('object-portal', TILE, TILE);

    // Vines (sim/vines.ts), five stages of the same entropy/energy ladder --
    // `drawVineTangle` draws the linework common to all of them (a few
    // criss-crossing stems with small leaf dots), parameterized by stem/leaf
    // color so the five stages read as one plant at different points in its
    // life rather than five unrelated props. Drawn with no background fill
    // for the plain family -- these sit on top of whatever tile they occupy
    // (a cliff face, ordinary floor), and a vine tangle reads as sparse
    // growth over that tile, not a solid block covering it.
    const drawVineTangle = (stemColor: number, leafColor: number, stemAlpha = 1): void => {
      g.lineStyle(2, stemColor, stemAlpha);
      g.lineBetween(6, TILE - 4, TILE / 2 - 2, TILE * 0.55);
      g.lineBetween(TILE / 2 - 2, TILE * 0.55, 10, TILE * 0.3);
      g.lineBetween(10, TILE * 0.3, TILE / 2, 5);
      g.lineBetween(TILE - 6, TILE - 4, TILE / 2 + 4, TILE * 0.5);
      g.lineBetween(TILE / 2 + 4, TILE * 0.5, TILE - 10, TILE * 0.25);
      g.lineBetween(TILE - 10, TILE * 0.25, TILE / 2 + 2, 6);
      g.fillStyle(leafColor, stemAlpha);
      for (const [lx, ly] of [
        [TILE / 2 - 2, TILE * 0.55],
        [10, TILE * 0.3],
        [TILE / 2 + 4, TILE * 0.5],
        [TILE - 10, TILE * 0.25],
        [TILE / 2, 6],
      ]) {
        g.fillCircle(lx, ly, 2.5);
      }
    };

    const vineStageTextures: { key: string; stem: number; leaf: number; stone?: boolean; alpha?: number; fire?: boolean }[] = [
      { key: 'object-vines-dead', stem: 0x3a342c, leaf: 0x4a4438 },
      { key: 'object-vines-withered', stem: 0x7a7040, leaf: 0x958a4a },
      { key: 'object-vines-healthy', stem: 0x2f6b2f, leaf: 0x4fa84f },
      { key: 'object-vines-growing', stem: 0x2f6b2f, leaf: 0x5fc85f },
      { key: 'object-vines-burning', stem: 0x2f6b2f, leaf: 0x4fa84f, fire: true },
      { key: 'object-vines-stone-dead', stem: 0x3a342c, leaf: 0x4a4438, stone: true },
      { key: 'object-vines-stone-withered', stem: 0x7a7040, leaf: 0x958a4a, stone: true },
      { key: 'object-vines-stone-healthy', stem: 0x2f6b2f, leaf: 0x4fa84f, stone: true },
      { key: 'object-vines-stone-growing', stem: 0x2f6b2f, leaf: 0x5fc85f, stone: true },
      { key: 'object-vines-stone-burning', stem: 0x2f6b2f, leaf: 0x4fa84f, stone: true, fire: true },
    ];
    for (const stage of vineStageTextures) {
      g.clear();
      if (stage.stone) {
        // The moveable Stone-with-Vines variant: a visible block base (same
        // cut-rectangle read as object-block) so it stays recognizable as
        // "a block" even under a full tangle of vines.
        g.fillStyle(0x6b6b6b, 1);
        g.fillRect(2, 2, TILE - 4, TILE - 4);
        g.lineStyle(2, 0x3a3a3a, 1);
        g.strokeRect(2, 2, TILE - 4, TILE - 4);
      }
      drawVineTangle(stage.stem, stage.leaf);
      if (stage.fire) {
        // A translucent orange-red wash over the same linework, rather than a
        // separate drawing -- burning vines are the same plant, on fire, not
        // a sixth silhouette to keep in sync with the other five.
        g.fillStyle(0xd9473f, 0.35);
        g.fillTriangle(TILE / 2, 2, 4, TILE - 2, TILE - 4, TILE - 2);
        g.fillStyle(0xf0a83f, 0.45);
        g.fillTriangle(TILE / 2, TILE * 0.35, TILE * 0.35, TILE - 4, TILE * 0.65, TILE - 4);
      }
      g.generateTexture(stage.key, TILE, TILE);
    }

    // Bush/Shrub/Tree (sim/vines.ts's `vines` cluster, with `climbs`/`grows`
    // both false): the same five-stage ladder as vines above, sold as
    // background plant life instead of a climbable rope, so each gets its
    // own silhouette -- a low canopy blob, a sparser twiggy clump, and a
    // trunk-and-canopy tree -- rather than reusing the vine tangle lying
    // flat on the ground.
    const drawBush = (canopyColor: number): void => {
      g.fillStyle(canopyColor, 1);
      g.fillEllipse(TILE / 2, TILE * 0.62, TILE * 0.62, TILE * 0.5);
      g.fillCircle(TILE * 0.32, TILE * 0.5, TILE * 0.18);
      g.fillCircle(TILE * 0.68, TILE * 0.5, TILE * 0.18);
    };
    const drawShrub = (stemColor: number, leafColor: number): void => {
      g.lineStyle(2, stemColor, 1);
      g.lineBetween(TILE / 2, TILE - 4, TILE * 0.35, TILE * 0.55);
      g.lineBetween(TILE / 2, TILE - 4, TILE / 2, TILE * 0.45);
      g.lineBetween(TILE / 2, TILE - 4, TILE * 0.65, TILE * 0.55);
      g.fillStyle(leafColor, 1);
      g.fillCircle(TILE * 0.35, TILE * 0.55, 3);
      g.fillCircle(TILE / 2, TILE * 0.45, 3);
      g.fillCircle(TILE * 0.65, TILE * 0.55, 3);
    };
    // Trunk stays woody brown at every stage but dead (charred) or withered
    // (dulled) -- unlike vines' own stem, which is plant matter through and
    // through, a tree's canopy is what actually tells the ladder's story.
    const drawTree = (trunkColor: number, canopyColor: number): void => {
      g.fillStyle(trunkColor, 1);
      g.fillRect(TILE / 2 - 3, TILE * 0.55, 6, TILE * 0.4);
      g.fillStyle(canopyColor, 1);
      g.fillCircle(TILE / 2, TILE * 0.4, TILE * 0.32);
    };

    const plantStageTextures: { key: string; draw: () => void; fire?: boolean }[] = [
      { key: 'object-bush-dead', draw: () => drawBush(0x4a4438) },
      { key: 'object-bush-withered', draw: () => drawBush(0x958a4a) },
      { key: 'object-bush-healthy', draw: () => drawBush(0x4fa84f) },
      { key: 'object-bush-growing', draw: () => drawBush(0x5fc85f) },
      { key: 'object-bush-burning', draw: () => drawBush(0x4fa84f), fire: true },
      { key: 'object-shrub-dead', draw: () => drawShrub(0x3a342c, 0x4a4438) },
      { key: 'object-shrub-withered', draw: () => drawShrub(0x7a7040, 0x958a4a) },
      { key: 'object-shrub-healthy', draw: () => drawShrub(0x2f6b2f, 0x4fa84f) },
      { key: 'object-shrub-growing', draw: () => drawShrub(0x2f6b2f, 0x5fc85f) },
      { key: 'object-shrub-burning', draw: () => drawShrub(0x2f6b2f, 0x4fa84f), fire: true },
      { key: 'object-tree-dead', draw: () => drawTree(0x1a1512, 0x4a4438) },
      { key: 'object-tree-withered', draw: () => drawTree(0x6b5f4a, 0x958a4a) },
      { key: 'object-tree-healthy', draw: () => drawTree(0x5a3d1f, 0x4fa84f) },
      { key: 'object-tree-growing', draw: () => drawTree(0x5a3d1f, 0x5fc85f) },
      { key: 'object-tree-burning', draw: () => drawTree(0x5a3d1f, 0x4fa84f), fire: true },
    ];
    for (const stage of plantStageTextures) {
      g.clear();
      stage.draw();
      if (stage.fire) {
        // Same translucent flame wash as vines' own burning stage -- the
        // same plant, on fire, not a sixth silhouette to keep in sync.
        g.fillStyle(0xd9473f, 0.35);
        g.fillTriangle(TILE / 2, 2, 4, TILE - 2, TILE - 4, TILE - 2);
        g.fillStyle(0xf0a83f, 0.45);
        g.fillTriangle(TILE / 2, TILE * 0.35, TILE * 0.35, TILE - 4, TILE * 0.65, TILE - 4);
      }
      g.generateTexture(stage.key, TILE, TILE);
    }

    for (const spriteId of SPRITE_IDS) {
      for (const dir of DIRECTIONS) {
        this.generatePlayerTexture(g, dir, spriteId);
      }
    }

    g.destroy();
  }

  private generatePlayerTexture(g: Phaser.GameObjects.Graphics, dir: Facing, spriteId: SpriteId): void {
    g.clear();
    const cx = TILE / 2;
    const cy = TILE / 2;
    const radius = TILE * 0.35;

    // Amber body for the default sprite -- matches rune-lab's concept-noun accent
    // color, kept consistent across both tools since they're the same project.
    // Witch/wizard get their own robe colors plus a pointy hat so the three read as
    // distinct silhouettes, not just recolors.
    const bodyColor = spriteId === 'witch' ? 0x4a2e6b : spriteId === 'wizard' ? 0x2e4a7a : 0xc9a24b;
    const bodyBorder = spriteId === 'witch' ? 0x2e1a45 : spriteId === 'wizard' ? 0x1a2e4a : 0x8a6a2e;

    g.fillStyle(bodyColor, 1);
    g.fillCircle(cx, cy, radius);
    g.lineStyle(2, bodyBorder, 1);
    g.strokeCircle(cx, cy, radius);

    if (spriteId === 'witch' || spriteId === 'wizard') {
      const hatColor = spriteId === 'witch' ? 0x1a1420 : 0x2a3a6a;
      const trimColor = spriteId === 'witch' ? 0x7a5ec9 : 0xc9a24b;
      const brimY = cy - radius + 3;
      const hatTip = brimY - 14;

      g.fillStyle(hatColor, 1);
      g.fillEllipse(cx, brimY, radius * 1.7, 5);
      g.fillTriangle(cx, hatTip, cx - 7, brimY, cx + 7, brimY);
      g.fillStyle(trimColor, 1);
      g.fillRect(cx - 6, brimY - 4, 12, 3);
    }

    const offset = DIRECTION_OFFSETS[dir];
    const noseLen = radius * 0.9;
    g.fillStyle(0x2b2016, 1);
    g.fillCircle(cx + offset.x * noseLen, cy + offset.y * noseLen, 3);

    g.generateTexture(`player-${spriteId}-${dir}`, TILE, TILE);
  }
}
